import { Icons } from '@ifood/fits-crafts';
import React, { useMemo, forwardRef, Children, useRef, useState, useEffect, useCallback, useContext, createContext, Fragment, useDebugValue } from 'react';
import styled, { css, keyframes, createGlobalStyle } from 'styled-components';
import { useDay, useMonth, useDatepicker, START_DATE } from '@datepicker-react/hooks';
import isValidDate from 'date-fns/isValid';
import parseDate from 'date-fns/parse';
import SelectBase, { components } from 'react-select';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var _templateObject, _templateObject2, _templateObject3;
var inheritVariantStyles = /*#__PURE__*/css(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: inherit;\n  font-size: inherit;\n  line-height: inherit;\n"])));
var otherVariantStyles = /*#__PURE__*/css(function (_ref) {
  var gutterBottom = _ref.gutterBottom,
      _ref$theme = _ref.theme,
      breakpoint = _ref$theme.breakpoint,
      typography = _ref$theme.component.typography,
      variant = _ref.variant;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    font-family: ", ";\n    font-size: ", ";\n    line-height: ", ";\n    margin-block-end: ", ";\n    margin-bottom: ", ";\n\n    @media screen and (min-width: ", ") {\n      font-size: ", ";\n      line-height: ", ";\n      margin-block-end: ", ";\n      margin-bottom: ", ";\n    }\n  "])), typography["" + variant].fontFamily, typography[variant + "Mobile"].fontSize, typography[variant + "Mobile"].lineHeight, gutterBottom ? typography[variant + "MobileGutterBottom"].marginBottom : 0, gutterBottom ? typography[variant + "MobileGutterBottom"].marginBottom : 0, breakpoint.breakpointMdMinWidth, typography[variant + "Desktop"].fontSize, typography[variant + "Desktop"].lineHeight, gutterBottom ? typography[variant + "DesktopGutterBottom"].marginBottom : 0, gutterBottom ? typography[variant + "DesktopGutterBottom"].marginBottom : 0);
});
var TypographyRoot = /*#__PURE__*/styled.span(function (_ref2) {
  var align = _ref2.align,
      _ref2$theme = _ref2.theme,
      color = _ref2$theme.color,
      typography = _ref2$theme.component.typography,
      textColor = _ref2.textColor,
      variant = _ref2.variant,
      weight = _ref2.weight;
  return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n    font-weight: ", ";\n    margin-block-start: ", ";\n    margin-left: ", ";\n    margin-inline-end: ", ";\n    margin-inline-start: ", ";\n    margin-right: ", ";\n    margin-top: ", ";\n    text-align: ", ";\n\n    ", "\n\n    &,\n    &:any-link {\n      color: ", ";\n    }\n  "])), typeof weight === 'undefined' || weight === 'inherit' ? 'inherit' : typography[weight].fontWeight, typography.root.marginBlockStart, typography.root.marginLeft, typography.root.marginInlineEnd, typography.root.marginInlineStart, typography.root.marginRight, typography.root.marginTop, align === 'undefined' ? 'inherit' : align, typeof variant === 'undefined' || variant === 'inherit' ? inheritVariantStyles : otherVariantStyles, function () {
    if (typeof textColor === 'undefined') {
      if (variant !== 'inherit') {
        return typography["" + variant].color;
      } else {
        return 'inherit';
      }
    }

    if (textColor === 'inherit') {
      return 'inherit';
    }

    if (typeof color[textColor] === 'undefined') {
      throw new Error(textColor + " is not a valid color");
    }

    return color[textColor];
  });
});

var defaultVariantMapping = {
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  heading4: 'h4',
  display: 'h1',
  body1: 'p',
  body2: 'p',
  body3: 'p',
  inherit: 'span'
};
var useTypographyProps = function useTypographyProps(_ref) {
  var componentProp = _ref.component,
      variantProp = _ref.variant,
      weightProp = _ref.weight;
  var defaultVariant = 'body1';
  var variant = useMemo(function () {
    if (variantProp === 'undefined') {
      return defaultVariant;
    }

    var isValidVariant = Object.keys(defaultVariantMapping).includes(variantProp);

    if (!isValidVariant) {
      throw new Error("Invalid typography variant: " + variant + ". Valid variants are: " + Object.keys(defaultVariantMapping).join(', '));
    }

    return variantProp;
  }, [variantProp]);
  var component = useMemo(function () {
    if (componentProp) {
      return componentProp;
    }

    if (typeof variant === 'undefined') {
      return defaultVariantMapping[defaultVariant];
    }

    return defaultVariantMapping[variant];
  }, [componentProp, variant]);
  var weight = useMemo(function () {
    if (weightProp === 'undefined') {
      return 'regular';
    }

    if (weightProp === 'medium') {
      return 'bold';
    }

    var validWeights = ['regular', 'medium', 'bold', 'inherit'];

    if (!validWeights.includes(weightProp)) {
      throw new Error("Invalid typography weight: " + weight + ". Valid weights are: " + validWeights.join(', '));
    }

    return weightProp;
  }, [weightProp]);
  return {
    component: component,
    variant: variant,
    weight: weight
  };
};

var _excluded = ["align", "component", "gutterBottom", "textColor", "variant", "weight"];
var defaultVariant = 'body1';
var defaultWeight = 'regular';
var Typography = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$align = props.align,
      align = _props$align === void 0 ? 'inherit' : _props$align,
      componentProp = props.component,
      _props$gutterBottom = props.gutterBottom,
      gutterBottom = _props$gutterBottom === void 0 ? false : _props$gutterBottom,
      textColor = props.textColor,
      _props$variant = props.variant,
      variantProp = _props$variant === void 0 ? defaultVariant : _props$variant,
      _props$weight = props.weight,
      weightProp = _props$weight === void 0 ? defaultWeight : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded);

  var _useTypographyProps = useTypographyProps({
    component: componentProp,
    variant: variantProp,
    weight: weightProp
  }),
      component = _useTypographyProps.component,
      variant = _useTypographyProps.variant,
      weight = _useTypographyProps.weight;

  return React.createElement(TypographyRoot, Object.assign({
    align: align,
    as: component,
    gutterBottom: gutterBottom,
    ref: ref,
    textColor: textColor,
    variant: variant,
    weight: weight
  }, otherProps));
});
Typography.displayName = 'Typography';

var _templateObject$1;
var FormLabelRoot = /*#__PURE__*/styled(Typography)(_templateObject$1 || (_templateObject$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  -webkit-tap-highlight-color: transparent;\n  align-items: center;\n  cursor: pointer;\n  display: inline-flex;\n  min-height: ", ";\n  vertical-align: middle;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.formLabel.root.minHeight;
});

var _excluded$1 = ["children", "component", "required", "variant"];
var FormLabel = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'label' : _props$component,
      _props$required = props.required,
      required = _props$required === void 0 ? false : _props$required,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'body2' : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$1);

  return React.createElement(FormLabelRoot, Object.assign({
    component: component,
    variant: variant
  }, otherProps, {
    ref: ref
  }), children, required && React.createElement("span", {
    "aria-hidden": true
  }, "\u2009", '*'));
});
FormLabel.displayName = 'FormLabel';

var _templateObject$2, _templateObject2$1, _templateObject3$1;
var focusVisibleStyles = /*#__PURE__*/css(function (_ref) {
  var focusVisible = _ref.focusVisible,
      buttonBase = _ref.theme.component.buttonBase;
  return css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteralLoose(["\n    border-style: none;\n    outline: ", " auto\n      ", ";\n    outline-offset: ", ";\n    // Despite being redundant, it ensures that the outline is displayed correctly in Firefox.\n    outline-style: solid;\n  "])), buttonBase.root.outlineColor, focusVisible ? buttonBase.focusVisible.outlineWidth : buttonBase.root.outlineWidth, focusVisible ? buttonBase.focusVisible.outlineOffset : buttonBase.root.outlineOffset);
});
var buttonBaseRootStyles = /*#__PURE__*/css(function (_ref2) {
  var buttonBase = _ref2.theme.component.buttonBase;
  return css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteralLoose(["\n    -webkit-tap-highlight-color: transparent;\n    align-items: center;\n    appearance: none;\n    border-radius: ", ";\n    box-sizing: border-box;\n    display: inline-flex;\n    font-size: inherit;\n    justify-content: center;\n    margin-bottom: ", ";\n    margin-left: ", ";\n    margin-right: ", ";\n    margin-top: ", ";\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n    position: relative;\n    user-select: none;\n    vertical-align: middle;\n\n    &,\n    &:any-link {\n      background-color: transparent;\n      color: inherit;\n      text-decoration: none;\n    }\n  "])), buttonBase.root.borderRadius, buttonBase.root.marginBottom, buttonBase.root.marginLeft, buttonBase.root.marginRight, buttonBase.root.marginTop, buttonBase.root.paddingBottom, buttonBase.root.paddingLeft, buttonBase.root.paddingRight, buttonBase.root.paddingTop);
});
var ButtonBaseRoot = /*#__PURE__*/styled.button(function (_ref3) {
  var ariaDisabled = _ref3['aria-disabled'],
      disabled = _ref3.disabled,
      focused = _ref3.focused,
      focusVisible = _ref3.focusVisible,
      buttonBase = _ref3.theme.component.buttonBase;
  return css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteralLoose(["\n    border-width: ", ";\n    font-family: ", ";\n    pointer-events: ", ";\n\n    ", "\n    ", "\n\n    &,\n    &:any-link {\n      cursor: ", ";\n    }\n\n    &:after,\n    &:before {\n      bottom: 0;\n      left: 0;\n      position: absolute;\n      right: 0;\n      top: 0;\n    }\n\n    @media print {\n      color-adjust: exact;\n    }\n\n    &,\n    &[type='button'],\n    &[type='reset'],\n    &[type='submit'] {\n      &:focus-visible {\n        ", "\n      }\n    }\n  "])), buttonBase.root.borderWidth, buttonBase.root.fontFamily, ariaDisabled || disabled ? 'none' : 'auto', buttonBaseRootStyles, focused && focusVisible && focusVisibleStyles, disabled ? 'not-allowed' : 'pointer', focusVisibleStyles);
});

var _excluded$2 = ["aria-disabled", "component", "disabled", "focusVisible", "focused", "LinkComponent", "tabIndex"];
var ButtonBase = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaDisabledProp = props['aria-disabled'],
      _props$component = props.component,
      componentProp = _props$component === void 0 ? 'button' : _props$component,
      disabled = props.disabled,
      _props$focusVisible = props.focusVisible,
      focusVisible = _props$focusVisible === void 0 ? false : _props$focusVisible,
      _props$focused = props.focused,
      focused = _props$focused === void 0 ? false : _props$focused,
      _props$LinkComponent = props.LinkComponent,
      LinkComponent = _props$LinkComponent === void 0 ? 'a' : _props$LinkComponent,
      tabIndexProp = props.tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$2);

  var component = useMemo(function () {
    var component = componentProp;

    if (componentProp === 'button' && (otherProps.href || otherProps.to)) {
      component = LinkComponent;
    }

    return component;
  }, [LinkComponent, componentProp, otherProps.href, otherProps.to]);
  var ariaDisabled = useMemo(function () {
    if (ariaDisabledProp) {
      return ariaDisabledProp;
    }

    return disabled;
  }, [ariaDisabledProp, disabled]);
  var tabIndex = useMemo(function () {
    if (tabIndexProp) {
      return tabIndexProp;
    }

    if (ariaDisabled || disabled) {
      return -1;
    }

    return 0;
  }, [ariaDisabled, disabled, tabIndexProp]);
  return React.createElement(ButtonBaseRoot, Object.assign({
    "aria-disabled": ariaDisabled,
    as: component,
    disabled: disabled,
    focusVisible: focusVisible,
    focused: focused,
    tabIndex: tabIndex
  }, otherProps, {
    ref: ref
  }));
});
ButtonBase.displayName = 'ButtonBase';

var _templateObject$3, _templateObject2$2;
var waveKeyframe = /*#__PURE__*/keyframes(_templateObject$3 || (_templateObject$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  0% {\n    transform: translateX(-100%);\n  }\n  50% {\n    transform: translateX(100%);\n  }\n  100% {\n    transform: translateX(100%);\n  }\n"])));
var SkeletonRoot = /*#__PURE__*/styled.span(function (_ref) {
  var skeleton = _ref.theme.component.skeleton;
  return css(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteralLoose(["\n    display: block;\n    overflow: clip;\n    position: relative;\n\n    &::after {\n      animation-delay: ", ";\n      animation-duration: calc(2s - ", ");\n      animation-iteration-count: infinite;\n      animation-name: ", ";\n      animation-timing-function: linear;\n      background-image: linear-gradient(90deg, transparent, currentColor, transparent);\n      bottom: 0;\n      content: '';\n      left: 0;\n      position: absolute;\n      right: 0;\n      top: 0;\n      transform: translateX(-100%);\n    }\n  "])), skeleton.skeleton.animationDelay, skeleton.skeleton.animationDelay, waveKeyframe);
});

var _excluded$3 = ["className", "component", "style"];
var Skeleton = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'span' : _props$component,
      style = props.style,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$3);

  return React.createElement(SkeletonRoot, Object.assign({
    as: component,
    className: className,
    style: style
  }, otherProps, {
    ref: ref
  }));
});
Skeleton.displayName = 'Skeleton';

var _excluded$4 = ["component", "textColor", "variant", "weight"];
var Text = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var component = props.component,
      textColor = props.textColor,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'body1' : _props$variant,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'regular' : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$4);

  return React.createElement(Typography, Object.assign({
    component: component,
    textColor: textColor,
    variant: variant,
    weight: weight
  }, otherProps, {
    ref: forwardedRef
  }));
});
Text.displayName = 'Text';

var _templateObject$4, _templateObject2$3, _templateObject3$2, _templateObject4, _templateObject5, _templateObject6;
var activeStyles = /*#__PURE__*/css(function (_ref) {
  var disableShadow = _ref.disableShadow,
      button = _ref.theme.component.button,
      variant = _ref.variant;
  return css(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteralLoose(["\n    box-shadow: ", ";\n\n    &,\n    &:any-link {\n      color: ", ";\n    }\n\n    &:before {\n      background-color: ", ";\n    }\n    &:after {\n      background-color: ", ";\n      opacity: ", ";\n    }\n  "])), disableShadow ? 'none' : button[variant + "Pressed"].boxShadow, button[variant + "Pressed"].color, button[variant + "PressedBefore"].backgroundColor, button[variant + "PressedAfter"].backgroundColor, button[variant + "PressedAfter"].opacity);
});
var disabledStyles = /*#__PURE__*/css(function (_ref2) {
  var disableShadow = _ref2.disableShadow,
      button = _ref2.theme.component.button,
      variant = _ref2.variant;
  return css(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteralLoose(["\n    &:disabled,\n    &[aria-disabled='true'] {\n      box-shadow: ", ";\n      will-change: box-shadow, color, text-decoration;\n\n      &:before {\n        background-color: ", ";\n        will-change: background-color;\n      }\n      &:after {\n        will-change: background-color, opacity;\n      }\n      &,\n      &:any-link {\n        color: ", ";\n      }\n    }\n  "])), disableShadow ? 'none' : button[variant + "Disabled"].boxShadow, button[variant + "DisabledBefore"].backgroundColor, button[variant + "Disabled"].color);
});
var hoverStyles = /*#__PURE__*/css(function (_ref3) {
  var disableShadow = _ref3.disableShadow,
      button = _ref3.theme.component.button,
      variant = _ref3.variant;
  return css(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteralLoose(["\n    box-shadow: ", ";\n    text-decoration: none;\n\n    &,\n    &:any-link {\n      color: ", ";\n    }\n\n    &:before {\n      background-color: ", ";\n    }\n    &:after {\n      background-color: ", ";\n      opacity: ", ";\n    }\n  "])), disableShadow ? 'none' : button[variant + "Hover"].boxShadow, button[variant + "Hover"].color, button[variant + "HoverBefore"].backgroundColor, button[variant + "HoverAfter"].backgroundColor, button[variant + "HoverAfter"].opacity);
});
var ButtonRoot = /*#__PURE__*/styled(ButtonBase)(function (_ref4) {
  var allowDisabledStyling = _ref4.allowDisabledStyling,
      disableShadow = _ref4.disableShadow,
      renderStartIcon = _ref4.renderStartIcon,
      selected = _ref4.selected,
      size = _ref4.size,
      shapeStyle = _ref4.shapeStyle,
      button = _ref4.theme.component.button,
      variant = _ref4.variant;
  return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n    border-width: ", ";\n    box-shadow: ", ";\n    flex-direction: column;\n    font-size: ", ";\n    font-weight: ", ";\n    height: ", ";\n\n    line-height: ", ";\n    min-height: ", ";\n    min-width: ", ";\n\n    overflow: visible;\n\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n\n    text-align: center;\n    z-index: ", ";\n\n    ", "\n\n    &,\n    &[type='button'],\n    &[type='reset'],\n    &[type='submit'] {\n      -webkit-appearance: button;\n    }\n\n    &:active,\n    &:focus-visible,\n    &:hover {\n      will-change: box-shadow, color, text-decoration;\n\n      &:before {\n        will-change: background-color;\n      }\n      &:after {\n        will-change: background-color, opacity;\n      }\n    }\n\n    &,\n    &:any-link {\n      color: ", ";\n    }\n\n    &,\n    &:after,\n    &:before {\n      border-radius: ", ";\n      content: '';\n    }\n\n    &:before {\n      background-color: ", ";\n      z-index: ", ";\n    }\n\n    &:after {\n      background-color: transparent;\n      z-index: ", ";\n    }\n\n    @media (prefers-reduced-motion: no-preference) {\n      transition-property: border-color, box-shadow, color, outline, width;\n\n      &,\n      &:after,\n      &:before {\n        transition-delay: ", ";\n        transition-duration: ", ";\n        transition-timing-function: ", ";\n      }\n\n      &:after,\n      &:before {\n        transition-property: background-color, opacity;\n      }\n    }\n\n    @media (hover: hover) {\n      &:hover {\n        ", "\n      }\n    }\n\n    &:active {\n      ", "\n    }\n\n    ", "\n\n    &:focus-visible {\n      box-shadow: ", ";\n\n      &,\n      &:any-link {\n        color: ", ";\n      }\n\n      &:before {\n        background-color: ", ";\n      }\n      &:after {\n        background-color: ", ";\n        opacity: ", ";\n      }\n    }\n  "])), button.root.borderWidth, disableShadow ? 'none' : button.root.boxShadow, button.root.fontSize, button.root.fontWeight, button[size].height, button.root.lineHeight, button.root.minHeight, button.root.minWidth, button[size].paddingBottom, function () {
    if (renderStartIcon) {
      return button.withStartIcon.paddingLeft;
    }

    return button.gutters.paddingLeft;
  }, button.gutters.paddingRight, button[size].paddingTop, button.root.zIndex, allowDisabledStyling && disabledStyles, button[variant].color, button[shapeStyle].borderRadius, variant === 'tertiary' ? 'transparent' : button[variant + "Before"].backgroundColor, button.rootBefore.zIndex, button.rootAfter.zIndex, button.root.transitionDelay, button.root.transitionDuration, button.root.transitionTimingFunction, !selected && hoverStyles, activeStyles, selected && activeStyles, disableShadow ? 'none' : button[variant + "Focused"].boxShadow, button[variant + "Focused"].color, button[variant + "FocusedBefore"].backgroundColor, button[variant + "FocusedAfter"].backgroundColor, button[variant + "FocusedAfter"].opacity);
});
var ButtonContent = /*#__PURE__*/styled(Text)(function (_ref5) {
  var button = _ref5.theme.component.button;
  return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    column-gap: ", ";\n    display: flex;\n    justify-content: center;\n    min-height: ", ";\n    line-height: ", ";\n    min-width: ", ";\n    overflow: clip;\n    position: relative;\n    text-align: center;\n    text-transform: none;\n    z-index: ", ";\n  "])), button.content.columnGap, button.content.minHeight, button.content.lineHeight, button.content.minWidth, button.content.zIndex);
});
var LoadingSkeleton = /*#__PURE__*/styled(Skeleton)(function (_ref6) {
  var shapeStyle = _ref6.shapeStyle,
      button = _ref6.theme.component.button,
      variant = _ref6.variant;
  return css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteralLoose(["\n    border-radius: ", ";\n    bottom: 0;\n    color: ", ";\n    left: 0;\n    height: 100%;\n    position: absolute;\n    right: 0;\n    top: 0;\n    width: 100%;\n  "])), button[shapeStyle].borderRadius, button[variant + "DisabledBefore"].backgroundColor);
});

var _excluded$5 = ["allowDisabledStyling", "children", "disableShadow", "disabled", "focusVisible", "loading", "loadingChildren", "renderStartIcon", "renderEndIcon", "shapeStyle", "selected", "size", "variant"];
var defaultProps = {
  variant: 'primary'
};
var Button = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$allowDisabledS = props.allowDisabledStyling,
      allowDisabledStyling = _props$allowDisabledS === void 0 ? true : _props$allowDisabledS,
      children = props.children,
      _props$disableShadow = props.disableShadow,
      disableShadow = _props$disableShadow === void 0 ? false : _props$disableShadow,
      disabled = props.disabled,
      _props$focusVisible = props.focusVisible,
      focusVisible = _props$focusVisible === void 0 ? true : _props$focusVisible,
      _props$loading = props.loading,
      loading = _props$loading === void 0 ? false : _props$loading,
      loadingChildren = props.loadingChildren,
      renderStartIcon = props.renderStartIcon,
      renderEndIcon = props.renderEndIcon,
      _props$shapeStyle = props.shapeStyle,
      shapeStyle = _props$shapeStyle === void 0 ? 'rounded' : _props$shapeStyle,
      _props$selected = props.selected,
      selected = _props$selected === void 0 ? false : _props$selected,
      _props$size = props.size,
      size = _props$size === void 0 ? 'md' : _props$size,
      _props$variant = props.variant,
      variantProp = _props$variant === void 0 ? defaultProps.variant : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$5);

  var variant = useMemo(function () {
    if (variantProp === 'undefined') {
      return defaultProps.variant;
    }

    return variantProp;
  }, [variantProp]);
  return React.createElement(ButtonRoot, Object.assign({
    allowDisabledStyling: !loading && allowDisabledStyling,
    disabled: disabled,
    disableShadow: disableShadow,
    focusVisible: focusVisible,
    ref: ref,
    renderStartIcon: renderStartIcon,
    selected: selected,
    shapeStyle: shapeStyle,
    size: size,
    variant: variant
  }, otherProps), loading && React.createElement(LoadingSkeleton, {
    "aria-hidden": true,
    disabled: disabled,
    shapeStyle: shapeStyle,
    variant: variant
  }), React.createElement(ButtonContent, {
    align: 'center',
    component: 'span',
    textColor: 'inherit',
    variant: 'body1',
    weight: 'inherit'
  }, renderStartIcon == null ? void 0 : renderStartIcon(), loadingChildren && loading ? React.createElement("span", {
    "aria-live": 'assertive'
  }, loadingChildren) : children, renderEndIcon == null ? void 0 : renderEndIcon()));
});
Button.displayName = 'Button';

var _templateObject$5, _templateObject2$4;
var iconButtonRootStyles = /*#__PURE__*/css(function (_ref) {
  var iconButton = _ref.theme.component.iconButton;
  return css(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteralLoose(["\n    flex-basis: auto;\n    flex-direction: column;\n    flex-grow: 0;\n    flex-shrink: 0;\n    flex-wrap: wrap;\n    max-height: 100vh;\n    overflow: clip;\n    text-align: center;\n\n    &,\n    &:before,\n    &:after {\n      border-radius: ", ";\n    }\n  "])), iconButton.root.borderRadius);
});
var IconButtonRoot = /*#__PURE__*/styled(Button)(function (_ref2) {
  var edge = _ref2.edge,
      size = _ref2.size,
      iconButton = _ref2.theme.component.iconButton;
  return css(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteralLoose(["\n    margin-left: ", ";\n    margin-right: ", ";\n\n    min-height: ", ";\n    min-width: ", ";\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n\n    ", "\n  "])), function () {
    if (edge === 'start') {
      return iconButton.edgeStart.marginLeft;
    }

    if (edge === 'end') {
      return iconButton.edgeEnd.marginLeft;
    }

    return iconButton.root.marginLeft;
  }, function () {
    if (edge === 'start') {
      return iconButton.edgeStart.marginRight;
    }

    if (edge === 'end') {
      return iconButton.edgeEnd.marginRight;
    }

    return iconButton.root.marginRight;
  }, iconButton[size].minHeight, iconButton[size].minWidth, iconButton[size].paddingBottom, iconButton[size].paddingLeft, iconButton[size].paddingRight, iconButton[size].paddingTop, iconButtonRootStyles);
});

var _excluded$6 = ["aria-label", "aria-labelledby", "children", "disabled", "edge", "size", "title", "type", "variant"];
var defaultProps$1 = {
  size: 'md',
  variant: 'tertiary'
};
var IconButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaLabel = props['aria-label'],
      ariaLabelledBy = props['aria-labelledby'],
      children = props.children,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$edge = props.edge,
      edge = _props$edge === void 0 ? false : _props$edge,
      _props$size = props.size,
      size = _props$size === void 0 ? defaultProps$1.size : _props$size,
      title = props.title,
      _props$type = props.type,
      type = _props$type === void 0 ? 'button' : _props$type,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? defaultProps$1.variant : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$6);

  return React.createElement(IconButtonRoot, Object.assign({
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    disableShadow: true,
    disabled: disabled,
    edge: edge,
    size: size === 'undefined' ? defaultProps$1.size : size,
    title: title,
    type: type,
    variant: variant === 'undefined' ? defaultProps$1.variant : variant
  }, otherProps, {
    ref: ref
  }), children);
});
IconButton.displayName = 'IconButton';

var _excluded$7 = ["aria-label", "children", "className", "onClick", "size", "type", "variant"];
var ArrowLeftOutline = Icons.ArrowLeftOutline;
var BackButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$ariaLabel = props['aria-label'],
      ariaLabel = _props$ariaLabel === void 0 ? 'Back' : _props$ariaLabel,
      children = props.children,
      className = props.className,
      onClick = props.onClick,
      _props$size = props.size,
      size = _props$size === void 0 ? 'md' : _props$size,
      _props$type = props.type,
      type = _props$type === void 0 ? 'reset' : _props$type,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'tertiary' : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$7);

  return React.createElement(FormLabel, {
    className: className,
    weight: 'bold'
  }, React.createElement(IconButton, Object.assign({
    "aria-label": ariaLabel,
    onClick: onClick,
    size: size,
    type: type,
    variant: variant
  }, otherProps, {
    ref: ref
  }), React.createElement(ArrowLeftOutline, {
    iconColor: 'colorRoleOnBackground'
  })), React.createElement("span", {
    "aria-hidden": true
  }, children || ariaLabel));
});
BackButton.displayName = 'BackButton';

var _templateObject$6;
var BadgeRoot = /*#__PURE__*/styled.span(function (_ref) {
  var disabled = _ref.disabled,
      shapeStyle = _ref.shapeStyle,
      size = _ref.size,
      badge = _ref.theme.component.badge,
      variant = _ref.variant;
  return css(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteralLoose(["\n    align-content: center;\n    align-items: center;\n    background-color: ", ";\n    border-radius: ", ";\n    box-sizing: border-box;\n    color: ", ";\n    display: inline-block;\n    flex-direction: row;\n    flex-shrink: 0;\n    flex-wrap: wrap;\n    font-family: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    height: ", ";\n    justify-content: center;\n    line-height: ", ";\n    max-width: ", ";\n    min-width: ", ";\n    overflow: hidden;\n    padding: ", ";\n    position: relative;\n    text-align: center;\n    text-overflow: ellipsis;\n    vertical-align: middle;\n    white-space: nowrap;\n\n    @media (prefers-reduced-motion: no-preference) {\n      transition-delay: ", ";\n      transition-duration: ", ";\n      transition-property: background-color, color;\n      transition-timing-function: ", ";\n    }\n  "])), disabled ? badge.disabled.backgroundColor : badge[variant].backgroundColor, function () {
    if (['semi-square', 'semiSquare'].includes(shapeStyle)) {
      return badge.semiSquare.borderRadius;
    }

    return badge[shapeStyle].borderRadius;
  }, disabled ? badge.disabled.color : badge[variant].color, badge.root.fontFamily, badge[size].fontSize, badge.root.fontWeight, badge[size].height, badge.root.lineHeight, badge.root.maxWidth, badge[size].minWidth, badge[size].padding, function (_ref2) {
    var theme = _ref2.theme;
    return theme.component.badge.root.transitionDelay;
  }, function (_ref3) {
    var theme = _ref3.theme;
    return theme.component.badge.root.transitionDuration;
  }, function (_ref4) {
    var theme = _ref4.theme;
    return theme.component.badge.root.transitionTimingFunction;
  });
});

var _excluded$8 = ["aria-label", "children", "component", "disabled", "shapeStyle", "role", "size", "variant"];
var defaultProps$2 = {
  shapeStyle: 'pill',
  size: 'md',
  variant: 'standard'
};
var Badge = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaLabel = props['aria-label'],
      children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'span' : _props$component,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      shapeStyleProp = props.shapeStyle,
      _props$role = props.role,
      role = _props$role === void 0 ? 'status' : _props$role,
      _props$size = props.size,
      size = _props$size === void 0 ? defaultProps$2.size : _props$size,
      _props$variant = props.variant,
      variantProp = _props$variant === void 0 ? defaultProps$2.variant : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$8);

  var variant = useMemo(function () {
    if (variantProp === 'undefined') {
      return defaultProps$2.variant;
    }

    return variantProp;
  }, [variantProp]);
  var shapeStyle = useMemo(function () {
    if (typeof shapeStyleProp === 'undefined' || shapeStyleProp === 'undefined') {
      return defaultProps$2.shapeStyle;
    }

    if (shapeStyleProp === 'semiSquare') {
      console.warn('Badge: shapeStyle "semiSquare" is deprecated. Please use "semi-square" instead.');
      return 'semi-square';
    }

    return shapeStyleProp;
  }, [shapeStyleProp]);
  return React.createElement(BadgeRoot, Object.assign({
    "aria-label": ariaLabel,
    as: component,
    disabled: disabled,
    role: role,
    shapeStyle: shapeStyle === 'undefined' ? defaultProps$2.shapeStyle : shapeStyle,
    size: size === 'undefined' ? defaultProps$2.size : size,
    variant: variant,
    ref: ref
  }, otherProps), children);
});
Badge.displayName = 'Badge';

var _templateObject$7;
var HyperlinkRoot = /*#__PURE__*/styled(Text)(function (_ref) {
  var textColor = _ref.textColor,
      hyperlink = _ref.theme.component.hyperlink;
  return css(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteralLoose(["\n    &,\n    &:any-link {\n      background-color: transparent;\n      border-width: ", ";\n      color: ", ";\n      cursor: pointer;\n      text-decoration: none;\n      &:hover {\n        color: ", ";\n        text-decoration-color: ", ";\n        text-decoration-line: underline;\n        text-decoration-thickness: 1px;\n      }\n    }\n  "])), hyperlink.root.borderWidth, !textColor && hyperlink.root.color, hyperlink.hover.color, hyperlink.hover.color);
});

var useHyperlinkToTextProps = function useHyperlinkToTextProps(_ref) {
  var _ref$variant = _ref.variant,
      variantProp = _ref$variant === void 0 ? 'inherit' : _ref$variant;
  var variant = useMemo(function () {
    var variantMap = {
      hyperlink1: 'body1',
      hyperlink2: 'body2',
      hyperlink3: 'body3',
      inherit: 'inherit'
    };

    if (!(variantProp in variantMap)) {
      throw new Error("Invalid variant prop: " + variantProp + ". Valid variants: " + Object.keys(variantMap).join(', '));
    }

    return variantMap[variantProp];
  }, [variantProp]);
  return {
    variant: variant
  };
};

var _excluded$9 = ["children", "component", "textColor", "variant", "weight"];
var Hyperlink = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'a' : _props$component,
      textColor = props.textColor,
      _props$variant = props.variant,
      variantProp = _props$variant === void 0 ? 'inherit' : _props$variant,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'bold' : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$9);

  var _useHyperlinkToTextPr = useHyperlinkToTextProps({
    variant: variantProp
  }),
      variant = _useHyperlinkToTextPr.variant;

  return React.createElement(HyperlinkRoot, Object.assign({
    as: HyperlinkRoot,
    component: component,
    textColor: textColor,
    variant: variant,
    weight: weight
  }, otherProps, {
    ref: forwardedRef
  }), children);
});
Hyperlink.displayName = 'Hyperlink';

var _excluded$a = ["className", "children", "component", "current", "itemProp", "textColor", "weight", "variant"];
var BreadcrumbItem = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var className = props.className,
      children = props.children,
      component = props.component,
      _props$current = props.current,
      current = _props$current === void 0 ? false : _props$current,
      _props$itemProp = props.itemProp,
      itemProp = _props$itemProp === void 0 ? 'item' : _props$itemProp,
      _props$textColor = props.textColor,
      textColor = _props$textColor === void 0 ? 'colorRoleBodySecondary' : _props$textColor,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'regular' : _props$weight,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'hyperlink2' : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$a);

  if (current) {
    return React.createElement(Text, Object.assign({
      "aria-current": 'location',
      className: className,
      component: 'strong',
      weight: 'bold',
      variant: 'body2'
    }, otherProps, {
      ref: forwardedRef
    }), children);
  }

  return React.createElement(Hyperlink, Object.assign({
    className: className,
    component: component,
    itemProp: itemProp,
    textColor: textColor,
    variant: variant,
    weight: weight
  }, otherProps, {
    ref: forwardedRef
  }), React.createElement("span", {
    itemProp: 'name'
  }, children));
});
BreadcrumbItem.displayName = 'BreadcrumbItem';

var _templateObject$8, _templateObject2$5, _templateObject3$3, _templateObject4$1;
/**
 * @internal
 */

var BreadcrumbsRoot = /*#__PURE__*/styled.nav(function (_ref) {
  var breadcrumbs = _ref.theme.component.breadcrumbs;
  return css(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteralLoose(["\n    display: block;\n    min-height: ", ";\n    min-width: ", ";\n  "])), breadcrumbs.root.minHeight, breadcrumbs.root.minWidth);
});
/**
 * @internal
 */

var BreadcrumbsList = /*#__PURE__*/styled.ol(function (_ref2) {
  var breadcrumbs = _ref2.theme.component.breadcrumbs;
  return css(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    column-gap: ", ";\n    display: flex;\n    flex-direction: row;\n    flex-grow: 0;\n    flex-wrap: wrap;\n    list-style-type: none;\n    margin: ", ";\n    max-width: 100%;\n    padding: ", ";\n    text-align: start;\n  "])), breadcrumbs.list.columnGap, breadcrumbs.list.margin, breadcrumbs.list.padding);
});
/**
 * @internal
 */

var BreadcrumbListItem = /*#__PURE__*/styled.li(_templateObject3$3 || (_templateObject3$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  display: flex;\n  user-select: none;\n"])));
/**
 * @internal
 */

var BreadcrumbsSeparator = /*#__PURE__*/styled(BreadcrumbListItem)(_templateObject4$1 || (_templateObject4$1 = /*#__PURE__*/_taggedTemplateLiteralLoose([""])));

var _excluded$b = ["className", "children", "component"];
var Breadcrumbs = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var className = props.className,
      children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'nav' : _props$component,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$b);

  var separator = '/';
  return React.createElement(BreadcrumbsRoot, Object.assign({
    className: className,
    component: component
  }, otherProps, {
    ref: forwardedRef
  }), React.createElement(BreadcrumbsList, {
    itemScope: true,
    itemType: 'https://schema.org/BreadcrumbList'
  }, Children.map(children, function (child, index) {
    var position = index + 1;
    var isLastItem = position === Children.count(children);
    return React.createElement(React.Fragment, null, React.createElement(BreadcrumbListItem, {
      key: index,
      itemProp: 'itemListElement',
      itemType: 'https://schema.org/ListItem',
      itemScope: true
    }, child, React.createElement("meta", {
      itemProp: 'position',
      content: position.toString()
    })), !isLastItem ? React.createElement(BreadcrumbsSeparator, {
      "aria-hidden": true
    }, React.createElement(Text, {
      component: 'span',
      variant: 'body2'
    }, separator)) : '');
  })));
});
Breadcrumbs.displayName = 'Breadcrumbs';

var _templateObject$9;
var ButtonGroupRoot = /*#__PURE__*/styled.div(function (_ref) {
  var spacing = _ref.spacing,
      buttonGroup = _ref.theme.component.buttonGroup;
  return css(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    column-gap: ", ";\n    display: inline-flex;\n    flex: 0 0 auto;\n    flex-direction: row;\n    flex-wrap: wrap;\n    list-style-type: none;\n  "])), spacing === 'sm' ? buttonGroup.spacingSm.columnGap : buttonGroup.spacingNone.columnGap);
});

var _excluded$c = ["className", "component", "role", "spacing"];
var ButtonGroup = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$role = props.role,
      role = _props$role === void 0 ? 'group' : _props$role,
      _props$spacing = props.spacing,
      spacing = _props$spacing === void 0 ? 'none' : _props$spacing,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$c);

  return React.createElement(ButtonGroupRoot, Object.assign({
    as: component,
    className: className,
    ref: ref,
    role: role,
    spacing: spacing
  }, otherProps));
});
ButtonGroup.displayName = 'ButtonGroup';

var _templateObject$a;
var CardActionAreaRoot = /*#__PURE__*/styled(ButtonBase)(_templateObject$a || (_templateObject$a = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: block;\n  height: 100%;\n  outline-style: none;\n  text-align: inherit;\n  width: 100%;\n"])));

var _excluded$d = ["children", "component", "onBlur", "onClick", "onFocus"];
var CardActionArea = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      component = props.component,
      onBlur = props.onBlur,
      onClick = props.onClick,
      onFocus = props.onFocus,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$d);

  return React.createElement(CardActionAreaRoot, Object.assign({
    component: component,
    onBlur: onBlur,
    onClick: onClick,
    onFocus: onFocus
  }, otherProps, {
    ref: ref
  }), children);
});
CardActionArea.displayName = 'CardActionArea';

var _templateObject$b, _templateObject2$6;
var surfaceRootStyles = /*#__PURE__*/css(function (_ref) {
  var shapeStyle = _ref.shapeStyle,
      surfaceColor = _ref.surfaceColor,
      surface = _ref.theme.component.surface;
  return css(_templateObject$b || (_templateObject$b = _taggedTemplateLiteralLoose(["\n      --surface-color: ", ";\n      border-color: ", ";\n      border-image: none;\n      border-radius: ", ";\n      box-sizing: border-box;\n      display: block;\n      list-style-type: none;\n\n      &,\n      &:any-link {\n        background: var(--surface-color);\n        background-color: var(--surface-color);\n      }\n    "])), function () {
    switch (surfaceColor) {
      case 'transparent':
        return 'transparent';

      case 'lowest':
        return surface.colorLowest.backgroundColor;

      default:
        return surface.root.backgroundColor;
    }
  }, surface.root.borderColor, function () {
    if (shapeStyle === 'semi-square') {
      return surface.shapeStyleSemiSquare.borderRadius;
    }

    if (shapeStyle === 'rounded') {
      return surface.shapeStyleRounded.borderRadius;
    }

    if (shapeStyle === 'pill') {
      return surface.shapeStylePill.borderRadius;
    }

    return surface.root.borderRadius;
  });
});
var SurfaceRoot = /*#__PURE__*/styled.div(_templateObject2$6 || (_templateObject2$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-style: ", ";\n  border-width: ", ";\n  box-shadow: ", ";\n\n  &,\n  &:any-link {\n    color: ", ";\n  }\n\n  position: relative;\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-delay: ", ";\n    transition-duration: ", ";\n    transition-property: background-color, border-color, box-shadow, color, outline;\n    transition-timing-function: ", ";\n  }\n\n  ", "\n"])), function (_ref2) {
  var outlined = _ref2.outlined;
  return outlined ? 'solid' : 'none';
}, function (_ref3) {
  var outlined = _ref3.outlined,
      theme = _ref3.theme;
  return outlined ? theme.component.surface.outlined.borderWidth : theme.component.surface.root.borderWidth;
}, function (_ref4) {
  var shadow = _ref4.shadow,
      theme = _ref4.theme;
  return shadow === 'lowest' ? theme.component.surface.shadowLowest.boxShadow : theme.component.surface.root.boxShadow;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.surface.root.color;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.surface.root.transitionDelay;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.surface.root.transitionDuration;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.surface.root.transitionTimingFunction;
}, surfaceRootStyles);

var _excluded$e = ["className", "component", "outlined", "shadow", "shapeStyle", "surfaceColor"];
var Surface = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$outlined = props.outlined,
      outlined = _props$outlined === void 0 ? false : _props$outlined,
      _props$shadow = props.shadow,
      shadow = _props$shadow === void 0 ? false : _props$shadow,
      _props$shapeStyle = props.shapeStyle,
      shapeStyle = _props$shapeStyle === void 0 ? 'none' : _props$shapeStyle,
      _props$surfaceColor = props.surfaceColor,
      surfaceColor = _props$surfaceColor === void 0 ? 'surface' : _props$surfaceColor,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$e);

  return React.createElement(SurfaceRoot, Object.assign({
    as: component,
    className: className,
    outlined: outlined,
    shadow: shadow,
    shapeStyle: shapeStyle,
    surfaceColor: surfaceColor
  }, otherProps, {
    ref: ref
  }));
});
Surface.displayName = 'Surface';

var _templateObject$c, _templateObject2$7, _templateObject3$4, _templateObject4$2, _templateObject5$1;
var focusedStyles = /*#__PURE__*/css(_templateObject$c || (_templateObject$c = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-color: ", ";\n  outline: ", " solid\n    ", ";\n  outline-offset: ", ";\n  // Despite being redundant, it ensures that the outline is displayed correctly in Firefox.\n  outline-style: solid;\n  will-change: border-color, outline;\n"])), function (_ref) {
  var selected = _ref.selected,
      theme = _ref.theme;
  return selected ? theme.component.card.selectedFocusVisible : theme.component.card.focusVisible.borderColor;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.card.focusVisible.outlineWidth;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.card.focusVisible.outlineColor;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.card.focusVisible.outlineOffset;
});
var defaultStateStyles = /*#__PURE__*/css(function (_ref5) {
  var selected = _ref5.selected,
      card = _ref5.theme.component.card;
  return css(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteralLoose(["\n    background-color: ", ";\n    border-color: ", ";\n    box-shadow: ", ";\n  "])), selected ? card.selected.backgroundColor : card.root.backgroundColor, selected ? card.selected.borderColor : card.root.borderColor, card.root.boxShadow);
});
var hoverStyles$1 = /*#__PURE__*/css(_templateObject3$4 || (_templateObject3$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref6) {
  var hover = _ref6.hover,
      selected = _ref6.selected,
      card = _ref6.theme.component.card;

  if (hover) {
    return css(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        border-color: ", ";\n        box-shadow: ", ";\n        will-change: background-color, border-color;\n      "])), selected ? card.selectedHover.backgroundColor : card.hover.backgroundColor, selected ? card.selectedHover.borderColor : card.hover.borderColor, card.hover.boxShadow);
  }

  return defaultStateStyles;
});
var CardRoot = /*#__PURE__*/styled(Surface)(_templateObject5$1 || (_templateObject5$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  border-width: ", ";\n  box-sizing: border-box;\n  display: flex;\n  font-family: ", ";\n  justify-content: space-between;\n  max-height: 100%;\n  padding: ", ";\n  table-layout: auto;\n  width: 100%;\n\n  &,\n  &:any-link {\n    color: ", ";\n    text-decoration: none;\n  }\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-property: background-color, border-color, border-width, color, padding;\n  }\n\n  &:focus-visible {\n    ", ";\n  }\n\n  @media (hover: hover) {\n    &:hover {\n      ", ";\n    }\n  }\n\n  ", "\n\n  &:active {\n    background-color: ", ";\n\n    border-color: ", "\n      ", ";\n  }\n"])), function (_ref7) {
  var selected = _ref7.selected,
      theme = _ref7.theme;
  return selected ? theme.component.card.selected.borderWidth : theme.component.card.root.borderWidth;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.card.root.fontFamily;
}, function (_ref9) {
  var selected = _ref9.selected,
      theme = _ref9.theme;
  return selected ? theme.component.card.selected.padding : theme.component.card.root.padding;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.card.root.color;
}, focusedStyles, hoverStyles$1, function (_ref11) {
  var focused = _ref11.focused;
  return focused ? focusedStyles : defaultStateStyles;
}, function (_ref12) {
  var onClick = _ref12.onClick,
      selected = _ref12.selected,
      theme = _ref12.theme;

  if (typeof onClick !== 'function') {
    return theme.component.card.root.backgroundColor;
  }

  return selected ? theme.component.card.selectedPressed.backgroundColor : theme.component.card.pressed.backgroundColor;
}, function (_ref13) {
  var onClick = _ref13.onClick,
      selected = _ref13.selected,
      theme = _ref13.theme;

  if (typeof onClick !== 'function') {
    return selected ? theme.component.card.selected.borderColor : theme.component.card.root.borderColor;
  }

  return selected ? theme.component.card.selectedPressed.borderColor : theme.component.card.pressed.borderColor;
}, function (_ref14) {
  var focused = _ref14.focused;
  return focused && focusedStyles;
});

var _excluded$f = ["aria-checked", "component", "focused", "hover", "onClick", "outlined", "selected", "shapeStyle", "tabIndex"];
var Card = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaChecked = props['aria-checked'],
      componentProp = props.component,
      focused = props.focused,
      hoverProp = props.hover,
      onClick = props.onClick,
      _props$outlined = props.outlined,
      outlined = _props$outlined === void 0 ? true : _props$outlined,
      _props$selected = props.selected,
      selected = _props$selected === void 0 ? false : _props$selected,
      _props$shapeStyle = props.shapeStyle,
      shapeStyle = _props$shapeStyle === void 0 ? 'rounded' : _props$shapeStyle,
      tabIndexProp = props.tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$f);

  var isInteractive = useMemo(function () {
    var href = otherProps.href,
        to = otherProps.to;
    return !!(onClick || href || to);
  }, [onClick, otherProps]);
  var component = useMemo(function () {
    if (componentProp) {
      return componentProp;
    }

    if (typeof componentProp === 'undefined' && isInteractive) {
      return CardActionArea;
    }

    return 'div';
  }, [componentProp, isInteractive]);
  var hover = useMemo(function () {
    if (hoverProp) {
      return hoverProp;
    }

    return isInteractive;
  }, [hoverProp, isInteractive]);
  var tabIndex = useMemo(function () {
    if (tabIndexProp) {
      return tabIndexProp;
    }

    if (isInteractive) {
      return 0;
    }

    return -1;
  }, [isInteractive, tabIndexProp]);
  return React.createElement(CardRoot, Object.assign({
    "aria-checked": ariaChecked != null ? ariaChecked : selected,
    component: component,
    focused: focused,
    hover: hover,
    onClick: onClick,
    outlined: outlined,
    selected: selected,
    shapeStyle: shapeStyle,
    tabIndex: tabIndex
  }, otherProps, {
    ref: ref
  }));
});
Card.displayName = 'Card';

var _templateObject$d;
var CardContentRoot = /*#__PURE__*/styled.div(function (_ref) {
  var compact = _ref.compact,
      cardContent = _ref.theme.component.cardContent;
  return css(_templateObject$d || (_templateObject$d = _taggedTemplateLiteralLoose(["\n    box-sizing: border-box;\n    display: flex;\n    flex: 1;\n    flex-direction: column;\n    font-weight: ", ";\n    justify-content: flex-start;\n    margin: 0;\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n    row-gap: ", ";\n  "])), compact ? cardContent.compact.fontWeight : cardContent.root.fontWeight, compact ? cardContent.compact.paddingBottom : cardContent.root.paddingBottom, compact ? cardContent.compact.paddingLeft : cardContent.root.paddingLeft, compact ? cardContent.compact.paddingRight : cardContent.root.paddingRight, compact ? cardContent.compact.paddingTop : cardContent.root.paddingTop, cardContent.root.rowGap);
});

var _excluded$g = ["className", "compact"];
var CardContent = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$compact = props.compact,
      compact = _props$compact === void 0 ? false : _props$compact,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$g);

  return React.createElement(CardContentRoot, Object.assign({
    className: className,
    compact: compact
  }, otherProps, {
    ref: ref
  }));
});
CardContent.displayName = 'CardContent';

function useControlled(_ref) {
  var controlledValue = _ref.controlledValue,
      defaultProp = _ref["default"],
      name = _ref.name,
      _ref$state = _ref.state,
      state = _ref$state === void 0 ? 'value' : _ref$state;

  // isControlled is ignored in the hook dependency lists as it should never change.
  var _useRef = useRef(controlledValue !== undefined),
      isControlled = _useRef.current;

  var _useState = useState(defaultProp),
      valueState = _useState[0],
      setValue = _useState[1];

  var value = isControlled ? controlledValue : valueState;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(function () {
      if (isControlled !== (controlledValue !== undefined)) {
        console.error([name + " component is changing the " + (isControlled ? '' : 'un') + "controlled " + state + " state of " + name + " to be " + (isControlled ? 'un' : '') + "controlled.", 'Elements should not switch from uncontrolled to controlled (or vice versa).', "Decide between using a controlled or uncontrolled " + name + " " + 'element for the lifetime of the component.', "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.", 'More info: https://fb.me/react-controlled-components'].join('\n'));
      }
    }, [state, name, controlledValue, isControlled]); // eslint-disable-next-line react-hooks/rules-of-hooks

    var _useRef2 = useRef(defaultProp),
        defaultValue = _useRef2.current; // eslint-disable-next-line react-hooks/rules-of-hooks


    useEffect(function () {
      if (!isControlled && defaultValue !== defaultProp) {
        console.error(["MUI: A component is changing the default " + state + " state of an uncontrolled " + name + " after being initialized. " + ("To suppress this warning opt to use a controlled " + name + ".")].join('\n'));
      } // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [JSON.stringify(defaultProp)]);
  }

  var setValueIfUncontrolled = useCallback(function (newValue) {
    if (!isControlled) {
      setValue(newValue);
    }
  }, [isControlled]);
  return {
    setValue: setValueIfUncontrolled,
    value: value
  };
}

var _templateObject$e;
var SwitchBaseInput = /*#__PURE__*/styled.input(function () {
  return css(_templateObject$e || (_templateObject$e = _taggedTemplateLiteralLoose(["\n    cursor: inherit;\n    left: 0;\n    height: 100%;\n    margin: 0;\n    opacity: 0;\n    padding: 0;\n    position: absolute;\n    top: 0;\n    // Ensure that the checkbox will be displayed, even with opacity equal to zero, so click actions will affect it.\n    visibility: visible;\n    width: 100%;\n    z-index: 1;\n  "])));
});

var _excluded$h = ["id", "aria-label", "aria-labelledby", "checked", "checkedIcon", "className", "component", "defaultChecked", "data-indeterminate", "disabled", "icon", "inputRef", "onBlur", "onChange", "onCheck", "onClick", "onFocus", "onKeyDown", "onUncheck", "readOnly", "required", "tabIndex", "title", "type", "value"];
var SwitchBase = /*#__PURE__*/forwardRef(function (props, ref) {
  var idProp = props.id,
      ariaLabel = props['aria-label'],
      ariaLabelledBy = props['aria-labelledby'],
      checkedProp = props.checked,
      checkedIcon = props.checkedIcon,
      className = props.className,
      component = props.component,
      defaultChecked = props.defaultChecked,
      dataIndeterminate = props['data-indeterminate'],
      disabled = props.disabled,
      icon = props.icon,
      inputRef = props.inputRef,
      onBlur = props.onBlur,
      onChange = props.onChange,
      onCheck = props.onCheck,
      onClick = props.onClick,
      onFocus = props.onFocus,
      onKeyDown = props.onKeyDown,
      onUncheck = props.onUncheck,
      readOnly = props.readOnly,
      required = props.required,
      tabIndex = props.tabIndex,
      title = props.title,
      type = props.type,
      valueProp = props.value,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$h);

  var _useControlled = useControlled({
    controlledValue: checkedProp,
    "default": Boolean(defaultChecked),
    name: 'SwitchBase',
    state: 'checked'
  }),
      setCheckedState = _useControlled.setValue,
      checked = _useControlled.value;

  var _useState = useState(false),
      focused = _useState[0],
      setFocusedState = _useState[1];

  var handleFocus = useCallback(function (event) {
    setFocusedState(true);
    onFocus == null ? void 0 : onFocus(event);
  }, [onFocus]);
  var handleBlur = useCallback(function (event) {
    setFocusedState(false);
    onBlur == null ? void 0 : onBlur(event);
  }, [onBlur]);
  var handleChange = useCallback(function (event) {
    if (event.nativeEvent.defaultPrevented) {
      return;
    }

    var newChecked = event.target.checked;
    setCheckedState(newChecked);
    onChange && (onChange == null ? void 0 : onChange(event, newChecked));

    if (newChecked) {
      onCheck == null ? void 0 : onCheck(event);
    } else {
      onUncheck == null ? void 0 : onUncheck(event);
    }
  }, [onChange, onCheck, onUncheck, setCheckedState]);
  var onClickInterceptor = useCallback(function (event) {
    onClick == null ? void 0 : onClick(event);
  }, [onClick]);
  var value = useMemo(function () {
    var hasLabelFor = type === 'checkbox' && typeof value === 'undefined';

    if (hasLabelFor) {
      return;
    }

    return valueProp;
  }, [type, valueProp]);
  var id = useMemo(function () {
    if (type === 'checkbox' || type === 'radio') {
      return idProp;
    }

    return;
  }, [idProp, type]);
  var handleKeyDown = useCallback(function (event) {
    if (event.key === ' ' || event.key === 'Enter') {
      setFocusedState(true);
      handleChange(event);
    }

    onKeyDown == null ? void 0 : onKeyDown(event);
  }, [handleChange, onKeyDown]);
  return React.createElement(IconButton, Object.assign({
    className: className,
    component: 'span',
    disabled: disabled,
    focusVisible: true,
    focused: focused,
    onBlur: handleBlur,
    onClick: onClickInterceptor,
    onFocus: handleFocus,
    role: undefined,
    size: 'sm',
    tabIndex: null
  }, otherProps, {
    ref: ref
  }), React.createElement(SwitchBaseInput, {
    id: id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as: component,
    checked: checkedProp,
    "data-indeterminate": dataIndeterminate,
    defaultChecked: defaultChecked,
    disabled: disabled,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    readOnly: readOnly,
    ref: inputRef,
    required: required,
    role: 'checkbox',
    tabIndex: tabIndex,
    title: title,
    type: type,
    value: value
  }), checked ? checkedIcon : icon);
});
SwitchBase.displayName = 'SwitchBase';

var _templateObject$f;
var CheckboxRoot = /*#__PURE__*/styled(SwitchBase)(function (_ref) {
  var checkbox = _ref.theme.component.checkbox;
  return css(_templateObject$f || (_templateObject$f = _taggedTemplateLiteralLoose(["\n    & input {\n      accent-color: ", ";\n      & + * {\n        color: ", ";\n      }\n    }\n    & input:checked {\n      accent-color: ", ";\n      & + * {\n        color: ", ";\n      }\n    }\n\n    & input:disabled {\n      accent-color: ", ";\n      & + * {\n        color: ", ";\n      }\n    }\n\n    @media (prefers-reduced-motion: reduce) {\n      &,\n      & input {\n        transition-delay: ", ";\n        transition-duration: ", ";\n        transition-timing-function: ", ";\n      }\n      & {\n        transition-property: color;\n      }\n      & input {\n        transition-property: accent-color;\n      }\n    }\n  "])), checkbox.root.color, checkbox.root.color, checkbox.checked.color, checkbox.checked.color, checkbox.disabled.color, checkbox.disabled.color, checkbox.root.transitionDelay, checkbox.root.transitionDuration, checkbox.root.transitionTimingFunction);
});

var _excluded$i = ["checked", "checkedIcon", "icon", "indeterminate", "indeterminateIcon", "inputRef", "onChange"];
var defaultCheckedIcon = /*#__PURE__*/React.createElement(Icons.CheckboxChecked, null);
var defaultIcon = /*#__PURE__*/React.createElement(Icons.CheckboxUnchecked, null);
var defaultIndeterminateIcon = /*#__PURE__*/React.createElement(Icons.CheckboxIndeterminate, null);
var Checkbox = /*#__PURE__*/forwardRef(function (props, ref) {
  var checked = props.checked,
      _props$checkedIcon = props.checkedIcon,
      checkedIconProp = _props$checkedIcon === void 0 ? defaultCheckedIcon : _props$checkedIcon,
      _props$icon = props.icon,
      iconProp = _props$icon === void 0 ? defaultIcon : _props$icon,
      _props$indeterminate = props.indeterminate,
      indeterminate = _props$indeterminate === void 0 ? false : _props$indeterminate,
      _props$indeterminateI = props.indeterminateIcon,
      indeterminateIconProp = _props$indeterminateI === void 0 ? defaultIndeterminateIcon : _props$indeterminateI,
      inputRef = props.inputRef,
      onChange = props.onChange,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$i);

  var icon = useMemo(function () {
    if (indeterminate) {
      return indeterminateIconProp;
    }

    return iconProp;
  }, [iconProp, indeterminate, indeterminateIconProp]);
  var checkedIcon = useMemo(function () {
    if (indeterminate) {
      return indeterminateIconProp;
    }

    return checkedIconProp;
  }, [checkedIconProp, indeterminate, indeterminateIconProp]);
  return React.createElement(CheckboxRoot, Object.assign({
    checked: checked,
    checkedIcon: checkedIcon,
    "data-indeterminate": indeterminate,
    icon: icon,
    inputRef: inputRef,
    onChange: onChange,
    type: 'checkbox'
  }, otherProps, {
    ref: ref
  }));
});
Checkbox.displayName = 'Checkbox';

var _templateObject$g;
var ChipRoot = /*#__PURE__*/styled(Surface)(function (_ref) {
  var endAdornment = _ref.endAdornment,
      onClick = _ref.onClick,
      startAdornment = _ref.startAdornment,
      chip = _ref.theme.component.chip;
  return css(_templateObject$g || (_templateObject$g = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    box-sizing: border-box;\n    column-gap: ", ";\n    cursor: ", ";\n    display: inline-flex;\n    flex-direction: row;\n    height: ", ";\n    justify-content: center;\n    max-width: 100%;\n    // We disable the focus ring for mouse, touch and keyboard users\n    outline: 0;\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n    text-decoration: none;\n    vertical-align: middle;\n    white-space: nowrap;\n  "])), chip.root.columnGap, onClick ? 'pointer' : 'default', chip.root.height, chip.root.paddingBottom, startAdornment ? chip.withStartAdornment.paddingLeft : chip.root.paddingLeft, endAdornment ? chip.withEndAdornment.paddingRight : chip.root.paddingRight, chip.root.paddingTop);
});

var _excluded$j = ["active", "children", "className", "component", "endAdornment", "onClick", "surfaceColor", "shapeStyle", "startAdornment", "tabIndex"];
var Chip = /*#__PURE__*/forwardRef(function (props, ref) {
  var active = props.active,
      children = props.children,
      className = props.className,
      componentProp = props.component,
      endAdornment = props.endAdornment,
      onClick = props.onClick,
      _props$surfaceColor = props.surfaceColor,
      surfaceColor = _props$surfaceColor === void 0 ? 'lowest' : _props$surfaceColor,
      _props$shapeStyle = props.shapeStyle,
      shapeStyle = _props$shapeStyle === void 0 ? 'pill' : _props$shapeStyle,
      startAdornment = props.startAdornment,
      tabIndexProp = props.tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$j);

  var component = useMemo(function () {
    if (componentProp) {
      return componentProp;
    }

    if (onClick) {
      return ButtonBase;
    }

    return 'div';
  }, [componentProp, onClick]);
  var tabIndex = useMemo(function () {
    if (tabIndexProp) {
      return tabIndexProp;
    }

    if (onClick) {
      return 0;
    }

    return -1;
  }, [onClick, tabIndexProp]);
  return React.createElement(ChipRoot, Object.assign({
    className: className,
    component: component,
    endAdornment: endAdornment,
    onClick: onClick,
    shapeStyle: shapeStyle,
    startAdornment: startAdornment,
    surfaceColor: surfaceColor
  }, otherProps, {
    ref: ref,
    tabIndex: tabIndex
  }), startAdornment, React.createElement(Text, {
    textColor: 'colorRoleOnSurface',
    variant: 'body2',
    weight: active ? 'bold' : 'regular'
  }, children), endAdornment);
});
Chip.displayName = 'Chip';

var _templateObject$h;
var ChipGroupRoot = /*#__PURE__*/styled.ul(_templateObject$h || (_templateObject$h = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  box-sizing: border-box;\n  column-gap: ", ";\n  display: flex;\n  list-style-type: none;\n  margin-block-end: 0;\n  margin-block-start: 0;\n  margin-inline-end: 0;\n  margin-inline-start: 0;\n  padding-inline-start: 0;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.spacing.spacing4xs;
});

var _excluded$k = ["className", "children", "component"];
var ChipGroup = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'ul' : _props$component,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$k);

  return React.createElement(ChipGroupRoot, Object.assign({
    as: component,
    className: className
  }, otherProps, {
    ref: ref
  }), children);
});
ChipGroup.displayName = 'ChipGroup';

var _templateObject$i, _templateObject2$8, _templateObject3$5, _templateObject4$3;
var circularRotateKeyframe = /*#__PURE__*/keyframes(_templateObject$i || (_templateObject$i = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  0% {\n    transform: rotate(0deg);\n  }\n  100% {\n    transform: rotate(360deg);\n  }\n"])));
var CircularProgressRoot = /*#__PURE__*/styled.span(_templateObject2$8 || (_templateObject2$8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-block;\n  transform-origin: center center;\n"])));
var CircularProgressSVG = /*#__PURE__*/styled.svg(_templateObject3$5 || (_templateObject3$5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: block;\n"])));
var CircularProgressCircle = /*#__PURE__*/styled.path(_templateObject4$3 || (_templateObject4$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  animation: ", " 1.4s linear infinite;\n  fill: currentColor;\n  stroke: none;\n  transform-origin: center center;\n"])), circularRotateKeyframe);

var _excluded$l = ["component", "role"];
var size = 24;
var CircularProgress = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'span' : _props$component,
      _props$role = props.role,
      role = _props$role === void 0 ? 'progressbar' : _props$role,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$l);

  return React.createElement(CircularProgressRoot, Object.assign({
    as: component,
    ref: ref,
    role: role
  }, otherProps), React.createElement(CircularProgressSVG, {
    fill: "none",
    height: size,
    viewBox: "0 0 " + size + " " + size,
    width: size,
    xmlns: "http://www.w3.org/2000/svg"
  }, React.createElement(CircularProgressCircle, {
    d: "M19.7612 17.0404C20.1065 17.2646 20.2068 17.7283 19.9575 18.056C18.755 19.6361 17.1051 20.8274 15.2142 21.4693C13.0872 22.1913 10.779 22.1762 8.66169 21.4264C6.54434 20.6766 4.74108 19.2358 3.54248 17.3361C2.34388 15.4365 1.81975 13.1886 2.05454 10.9547C2.28933 8.72083 3.26937 6.63105 4.83674 5.0221C6.40411 3.41315 8.46752 2.37874 10.6945 2.08555C12.9215 1.79236 15.1823 2.25747 17.1127 3.40594C18.8289 4.42696 20.195 5.93522 21.0427 7.73089C21.2185 8.10321 21.024 8.53593 20.6396 8.68348C20.2552 8.83103 19.8269 8.63722 19.6463 8.26722C18.922 6.78337 17.7786 5.53704 16.3503 4.68731C14.7078 3.71009 12.784 3.31432 10.8891 3.5638C8.99418 3.81327 7.23842 4.69345 5.90474 6.0625C4.57107 7.43156 3.73715 9.20976 3.53737 11.1106C3.33759 13.0114 3.78357 14.9241 4.80346 16.5405C5.82335 18.1569 7.35774 19.3829 9.15939 20.0209C10.961 20.6589 12.925 20.6718 14.7349 20.0574C16.3086 19.5232 17.6861 18.5418 18.7031 17.241C18.9567 16.9167 19.4159 16.8161 19.7612 17.0404Z"
  })));
});
CircularProgress.displayName = 'CircularProgress';

var _excluded$m = ["aria-label", "children", "size"];
var CloseButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaLabel = props['aria-label'],
      _props$children = props.children,
      children = _props$children === void 0 ? React.createElement(Icons.Close, {
    size: 'sm'
  }) : _props$children,
      _props$size = props.size,
      size = _props$size === void 0 ? 'sm' : _props$size,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$m);

  return React.createElement(IconButton, Object.assign({
    "aria-label": ariaLabel,
    ref: ref,
    size: size
  }, otherProps), children);
});
CloseButton.displayName = 'CloseButton';

var _templateObject$j, _templateObject2$9, _templateObject3$6, _templateObject4$4, _templateObject5$2, _templateObject6$1, _templateObject7, _templateObject8, _templateObject9, _templateObject10;
var maxWidthProperty = /*#__PURE__*/css(_templateObject$j || (_templateObject$j = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  --max-width: ", ";\n"])), function (_ref) {
  var maxWidth = _ref.maxWidth,
      theme = _ref.theme;

  if (!maxWidth) {
    return '100%';
  }

  var maxWidthMap = {
    sm: theme.component.container.maxWidthSm.maxWidth,
    md: theme.component.container.maxWidthMd.maxWidth,
    lg: theme.component.container.maxWidthLg.maxWidth,
    xl: theme.component.container.maxWidthXl.maxWidth,
    'sidebar-layout': theme.component.container.maxWidthSidebarLayout.maxWidth
  };

  if (maxWidthMap[maxWidth]) {
    return maxWidthMap[maxWidth];
  }

  return '100%';
});
var smStyles = /*#__PURE__*/css(_templateObject2$9 || (_templateObject2$9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref2) {
  var theme = _ref2.theme,
      variant = _ref2.variant;
  return css(_templateObject3$6 || (_templateObject3$6 = _taggedTemplateLiteralLoose(["\n    min-width: min(", ", var(--max-width));\n    padding-inline-start: max(\n      ", ",\n      env(safe-area-inset-left)\n    );\n    padding-inline-end: max(\n      ", ",\n      env(safe-area-inset-right)\n    );\n  "])), theme.component.container.sm.minWidth, variant === 'loose' ? theme.component.container.looseSm.paddingLeft : theme.component.container.tightSm.paddingLeft, variant === 'loose' ? theme.component.container.looseSm.paddingRight : theme.component.container.tightSm.paddingRight);
});
var mdStyles = /*#__PURE__*/css(_templateObject4$4 || (_templateObject4$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref3) {
  var theme = _ref3.theme,
      variant = _ref3.variant;
  return css(_templateObject5$2 || (_templateObject5$2 = _taggedTemplateLiteralLoose(["\n    min-width: min(", ", var(--max-width));\n    padding-inline-start: max(\n      ", ",\n      env(safe-area-inset-left)\n    );\n    padding-inline-end: max(\n      ", ",\n      env(safe-area-inset-right)\n    );\n  "])), theme.component.container.md.minWidth, variant === 'loose' ? theme.component.container.looseMd.paddingLeft : theme.component.container.tightMd.paddingLeft, variant === 'loose' ? theme.component.container.looseMd.paddingRight : theme.component.container.tightMd.paddingRight);
});
var lgStyles = /*#__PURE__*/css(_templateObject6$1 || (_templateObject6$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref4) {
  var theme = _ref4.theme,
      variant = _ref4.variant;
  return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteralLoose(["\n    min-width: min(", ", var(--max-width));\n    padding-inline-start: max(\n      ", ",\n      env(safe-area-inset-left)\n    );\n    padding-inline-end: max(\n      ", ",\n      env(safe-area-inset-right)\n    );\n  "])), theme.component.container.lg.minWidth, variant === 'loose' ? theme.component.container.looseLg.paddingLeft : theme.component.container.tightLg.paddingLeft, variant === 'loose' ? theme.component.container.looseLg.paddingRight : theme.component.container.tightLg.paddingRight);
});
var xlStyles = /*#__PURE__*/css(_templateObject8 || (_templateObject8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref5) {
  var theme = _ref5.theme,
      variant = _ref5.variant;
  return css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteralLoose(["\n    min-width: min(", ", var(--max-width));\n    padding-inline-start: max(\n      ", ",\n      env(safe-area-inset-left)\n    );\n    padding-inline-end: max(\n      ", ",\n      env(safe-area-inset-right)\n    );\n  "])), theme.component.container.xl.minWidth, variant === 'loose' ? theme.component.container.looseXl.paddingLeft : theme.component.container.tightXl.paddingLeft, variant === 'loose' ? theme.component.container.looseXl.paddingRight : theme.component.container.tightXl.paddingRight);
});
var ContainerRoot = /*#__PURE__*/styled.div(function (_ref6) {
  var alignItems = _ref6.alignItems,
      flexDirection = _ref6.flexDirection,
      justifyContent = _ref6.justifyContent,
      maxWidth = _ref6.maxWidth,
      _ref6$theme = _ref6.theme,
      breakpoint = _ref6$theme.breakpoint,
      container = _ref6$theme.component.container;
  return css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteralLoose(["\n    align-content: start;\n    align-items: ", ";\n    box-sizing: border-box;\n    display: flex;\n    flex-direction: ", ";\n    justify-content: ", ";\n    list-style: none;\n    margin: 0 auto;\n    max-width: var(--max-width);\n    padding-inline-start: max(", ", env(safe-area-inset-left));\n    padding-inline-end: max(", ", env(safe-area-inset-right));\n    width: 100%;\n\n    @media (min-width: ", ") {\n      ", ";\n    }\n    @media (min-width: ", ") {\n      ", "\n    }\n    @media (min-width: ", ") {\n      ", "\n    }\n    @media (min-width: ", ") {\n      ", "\n    }\n\n    ", "\n  "])), alignItems, flexDirection, justifyContent, container.paddingInlineStart, container.paddingInlineEnd, breakpoint.breakpointSmMinWidth, smStyles, breakpoint.breakpointMdMinWidth, !maxWidth || ['md', 'lg', 'xl'].includes(maxWidth) ? mdStyles : '', breakpoint.breakpointLgMinWidth, !maxWidth || ['lg', 'xl'].includes(maxWidth) ? lgStyles : '', breakpoint.breakpointXlMinWidth, !maxWidth || ['xl'].includes(maxWidth) ? xlStyles : '', maxWidthProperty);
});

var _excluded$n = ["alignItems", "component", "flexDirection", "justifyContent", "maxWidth", "variant"];
var defaultProps$3 = {
  alignItems: 'stretch',
  component: 'div',
  flexDirection: 'column',
  maxWidth: false,
  variant: 'loose'
};
var Container = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$alignItems = props.alignItems,
      alignItems = _props$alignItems === void 0 ? defaultProps$3.alignItems : _props$alignItems,
      _props$component = props.component,
      component = _props$component === void 0 ? defaultProps$3.component : _props$component,
      _props$flexDirection = props.flexDirection,
      flexDirection = _props$flexDirection === void 0 ? defaultProps$3.flexDirection : _props$flexDirection,
      justifyContent = props.justifyContent,
      _props$maxWidth = props.maxWidth,
      maxWidth = _props$maxWidth === void 0 ? defaultProps$3.maxWidth : _props$maxWidth,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? defaultProps$3.variant : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$n);

  return React.createElement(ContainerRoot, Object.assign({
    alignItems: alignItems,
    as: component,
    flexDirection: flexDirection === 'undefined' ? defaultProps$3.flexDirection : flexDirection,
    justifyContent: justifyContent,
    maxWidth: maxWidth,
    variant: variant === 'undefined' ? defaultProps$3.variant : variant
  }, otherProps, {
    ref: ref
  }));
});
Container.displayName = 'Container';

var useEventListener = function useEventListener(eventName, handler, element) {
  var savedHandler = useRef();
  useEffect(function () {
    savedHandler.current = handler;
  }, [handler]);
  useEffect(function () {
    var targetElement = (element == null ? void 0 : element.current) || window;

    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    var eventListener = function eventListener(event) {
      if (savedHandler != null && savedHandler.current) {
        savedHandler.current(event);
      }
    };

    targetElement.addEventListener(eventName, eventListener);
    return function () {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
};

var useOnClickOutside = function useOnClickOutside(ref, handler, mouseEvent) {
  if (mouseEvent === void 0) {
    mouseEvent = 'mousedown';
  }

  useEventListener(mouseEvent, function (event) {
    var element = ref == null ? void 0 : ref.current;
    var isDescendentElement = element == null ? void 0 : element.contains(event.target);

    if (!element || isDescendentElement) {
      return;
    }

    handler(event);
  });
};

var _templateObject$k;
var InputAdornmentRoot = /*#__PURE__*/styled.div(_templateObject$k || (_templateObject$k = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  display: flex;\n  justify-content: center;\n  white-space: nowrap;\n\n  margin-left: ", ";\n  margin-right: ", ";\n"])), function (_ref) {
  var position = _ref.position,
      theme = _ref.theme;
  return position === 'start' ? theme.component.inputAdornment.positionStart.marginLeft : theme.component.inputAdornment.positionEnd.marginLeft;
}, function (_ref2) {
  var position = _ref2.position,
      theme = _ref2.theme;
  return position === 'start' ? theme.component.inputAdornment.positionStart.marginRight : theme.component.inputAdornment.positionEnd.marginRight;
});

var _excluded$o = ["component", "position"];
var InputAdornment = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$position = props.position,
      position = _props$position === void 0 ? 'start' : _props$position,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$o);

  return React.createElement(InputAdornmentRoot, Object.assign({
    as: component,
    position: position
  }, otherProps, {
    ref: ref
  }));
});
InputAdornment.displayName = 'InputAdornment';

var _templateObject$l;
var FormHelperTextRoot = /*#__PURE__*/styled(Text)(_templateObject$l || (_templateObject$l = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-block;\n  color: ", ";\n  line-height: ", ";\n  text-align: left;\n  will-change: color;\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-duration: ", ";\n    transition-property: color;\n    transition-timing-function: ", ";\n  }\n"])), function (_ref) {
  var error = _ref.error,
      theme = _ref.theme;
  return error ? theme.component.formHelperText.error.color : 'inherit';
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.formHelperText.root.lineHeight;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.formHelperText.root.transitionDuration;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.formHelperText.root.transitionTimingFunction;
});

var _excluded$p = ["children", "id", "role", "variant"];
var FormHelperText = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      id = props.id,
      _props$role = props.role,
      role = _props$role === void 0 ? 'note' : _props$role,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'body2' : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$p);

  return React.createElement(FormHelperTextRoot, Object.assign({
    id: id,
    role: role,
    variant: variant
  }, otherProps, {
    ref: ref
  }), children === ' ' ? React.createElement("span", {
    className: 'notranslate'
  }, "\u200B") : children);
});
FormHelperText.displayName = 'FormHelperText';

var useFormHelperTextId = function useFormHelperTextId(_ref) {
  var hint = _ref.hint,
      id = _ref.id;
  return useMemo(function () {
    return hint && id ? id + "-helper-text" : undefined;
  }, [hint, id]);
};

var _templateObject$m;
var InputLabelRoot = /*#__PURE__*/styled(FormLabel)(_templateObject$m || (_templateObject$m = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n  cursor: ", ";\n  max-width: 100%;\n  overflow: hidden;\n  pointer-events: ", ";\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  will-change: color;\n  z-index: ", ";\n  margin-bottom: ", ";\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-duration: ", ";\n    transition-property: color;\n    transition-timing-function: ", ";\n  }\n"])), function (_ref) {
  var error = _ref.error,
      theme = _ref.theme;

  if (error) {
    return theme.component.inputLabel.error.color;
  }

  return theme.component.inputLabel.root.color;
}, function (_ref2) {
  var disabled = _ref2.disabled;
  return disabled ? 'default' : 'pointer';
}, function (_ref3) {
  var disabled = _ref3.disabled;
  return disabled ? 'none' : 'auto';
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.inputLabel.root.zIndex;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.inputLabel.root.marginBottom;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.inputLabel.root.transitionDuration;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.inputLabel.root.transitionTimingFunction;
});

var _excluded$q = ["component", "weight"];
var InputLabel = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'label' : _props$component,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'bold' : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$q);

  return React.createElement(InputLabelRoot, Object.assign({
    component: component,
    weight: weight
  }, otherProps, {
    ref: ref
  }));
});
InputLabel.displayName = 'InputLabel';

var useInputLabelId = function useInputLabelId(_ref) {
  var label = _ref.label,
      id = _ref.id;
  return useMemo(function () {
    return label && id ? id + "-label" : undefined;
  }, [label, id]);
};

var _templateObject$n, _templateObject2$a;
var FieldWrapperRoot = /*#__PURE__*/styled.fieldset(function (_ref) {
  var fullWidth = _ref.fullWidth,
      fieldWrapper = _ref.theme.component.fieldWrapper;
  return css(_templateObject$n || (_templateObject$n = _taggedTemplateLiteralLoose(["\n    align-items: stretch;\n    border-color: transparent;\n    border-style: none;\n    border-width: ", ";\n    box-sizing: border-box;\n    display: flex;\n    flex-direction: column;\n    margin: ", ";\n    margin-inline-end: ", ";\n    margin-inline-start: ", ";\n    padding: ", ";\n    padding-block-end: ", ";\n    padding-block-start: ", ";\n    padding-inline-end: ", ";\n    padding-inline-start: ", ";\n    position: relative;\n    row-gap: ", ";\n    width: ", ";\n  "])), fieldWrapper.root.borderWidth, fieldWrapper.root.margin, fieldWrapper.root.marginInlineEnd, fieldWrapper.root.marginInlineStart, fieldWrapper.root.padding, fieldWrapper.root.paddingBlockEnd, fieldWrapper.root.paddingBlockStart, fieldWrapper.root.paddingInlineEnd, fieldWrapper.root.paddingInlineStart, fieldWrapper.root.rowGap, fullWidth ? 'auto' : 'max-content');
});
var FieldWrapperHintArea = /*#__PURE__*/styled.div(_templateObject2$a || (_templateObject2$a = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  justify-content: space-between;\n"])));

var _excluded$r = ["children", "component", "disabled", "error", "focused", "fullWidth", "hint", "id", "label", "required", "renderCounter"];
var FieldWrapper = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'fieldset' : _props$component,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      error = props.error,
      focused = props.focused,
      fullWidth = props.fullWidth,
      hint = props.hint,
      id = props.id,
      label = props.label,
      _props$required = props.required,
      required = _props$required === void 0 ? false : _props$required,
      renderCounter = props.renderCounter,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$r);

  var inputHintId = useFormHelperTextId({
    hint: hint,
    id: id
  });
  var inputLabelId = useInputLabelId({
    id: id,
    label: label
  });
  return React.createElement(FieldWrapperRoot, Object.assign({
    as: component,
    disabled: disabled,
    fullWidth: fullWidth,
    ref: ref
  }, otherProps), label && React.createElement(InputLabel, {
    id: inputLabelId,
    disabled: disabled,
    error: error,
    focused: focused,
    htmlFor: id,
    required: required
  }, label), children, React.createElement(FieldWrapperHintArea, null, hint && React.createElement(FormHelperText, {
    error: error,
    focused: focused,
    id: inputHintId
  }, hint), renderCounter == null ? void 0 : renderCounter({
    disabled: disabled,
    error: error,
    focused: focused
  })));
});
FieldWrapper.displayName = 'FieldWrapper';

var useElementRef = function useElementRef(ref) {
  var fallbackRef = useRef(null);
  return useMemo(function () {
    if (ref) {
      return ref;
    }

    return fallbackRef;
  }, [ref]);
};

var _templateObject$o, _templateObject2$b, _templateObject3$7, _templateObject4$5, _templateObject5$3, _templateObject6$2, _templateObject7$1, _templateObject8$1, _templateObject9$1, _templateObject10$1, _templateObject11, _templateObject12, _templateObject13;
var inputTypography = /*#__PURE__*/css(_templateObject$o || (_templateObject$o = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.input.root.fontFamily;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.input.root.fontSize;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.input.root.lineHeight;
});
var placeholderCss = /*#__PURE__*/css(_templateObject2$b || (_templateObject2$b = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n"])), function (_ref4) {
  var error = _ref4.error,
      theme = _ref4.theme;
  return error ? theme.component.input.error.color : theme.component.input.placeholder.color;
});
var InputBaseRoot = /*#__PURE__*/styled(Surface)(_templateObject3$7 || (_templateObject3$7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  cursor: ", ";\n  display: flex;\n  flex: 1;\n  width: 100%;\n  z-index: ", ";\n\n  ", "\n\n  ", "\n\n  ", "\n\n  &:hover {\n    will-change: background-color, border-color, outline;\n  }\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-property: background-color, border-color, outline;\n    transition-duration: ", ";\n    transition-timing-function: ", ";\n  }\n"])), function (_ref5) {
  var disabled = _ref5.disabled;
  return disabled ? 'default' : 'text';
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.input.root.zIndex;
}, inputTypography, function (_ref7) {
  var disabled = _ref7.disabled,
      error = _ref7.error,
      focused = _ref7.focused,
      theme = _ref7.theme;

  if (error) {
    return css(_templateObject4$5 || (_templateObject4$5 = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        border-color: ", ";\n      "])), theme.component.input.error.backgroundColor, theme.component.input.error.borderColor);
  }

  if (disabled) {
    return css(_templateObject5$3 || (_templateObject5$3 = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        border-color: ", ";\n      "])), theme.component.input.disabled.backgroundColor, theme.component.input.disabled.borderColor);
  }

  if (focused) {
    return css(_templateObject6$2 || (_templateObject6$2 = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        border-color: ", ";\n        will-change: background-color, border-color;\n      "])), theme.component.input.root.backgroundColor, theme.component.input.focused.borderColor);
  }

  return css(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteralLoose(["\n      background-color: ", ";\n      border-color: ", ";\n    "])), theme.component.input.root.backgroundColor, theme.component.input.root.borderColor);
}, function (_ref8) {
  var focused = _ref8.focused,
      theme = _ref8.theme;

  if (focused) {
    return css(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteralLoose(["\n        border-width: ", ";\n        outline: ", " solid ", ";\n        outline-offset: ", ";\n        // Despite being redundant, it ensures that the outline is displayed correctly in Firefox.\n        outline-style: solid;\n      "])), theme.component.input.focused.borderWidth, theme.component.input.focused.outlineWidth, theme.component.input.focused.outlineColor, theme.component.input.focused.outlineOffset);
  }

  return css(_templateObject9$1 || (_templateObject9$1 = _taggedTemplateLiteralLoose(["\n      outline-style: none;\n    "])));
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.input.root.transitionDuration;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.input.root.transitionTimingFunction;
});
var InputBaseComponent = /*#__PURE__*/styled.input(_templateObject10$1 || (_templateObject10$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  -webkit-tap-highlight-color: transparent;\n  background: transparent;\n  border-color: transparent;\n  border-style: none;\n  border-width: 0;\n  box-sizing: border-box;\n  color: inherit;\n  display: block;\n  letter-spacing: inherit;\n  margin: 0;\n  min-width: 0;\n  overflow: visible;\n  width: 100%;\n\n  ", "\n\n  ", "\n\n  &,\n  &[type='password' i],\n  &[type='search' i],\n  &[type='text' i] {\n    padding: ", ";\n\n    &::-ms-reveal {\n      display: none;\n      height: 0;\n      width: 0;\n    }\n  }\n\n  ::-webkit-input-placeholder {\n    ", "\n    ", "\n  }\n  ::-moz-placeholder {\n    ", "\n    ", "\n  }\n  :-ms-input-placeholder {\n    ", "\n    ", "\n  }\n  ::-ms-input-placeholder {\n    ", "\n    ", "\n  }\n  :-moz-placeholder {\n    ", "\n    ", "\n  }\n\n  &:focus,\n  &:focus-visible {\n    outline: 0 none transparent;\n    // Despite being redundant, it ensures that the outline is not displayed in Firefox.\n    outline-style: none;\n  }\n\n  &:read-only {\n    pointer-events: none;\n  }\n\n  &[type='number'] {\n    &::-webkit-inner-spin-button,\n    ::-webkit-outer-spin-button {\n      height: auto;\n    }\n  }\n\n  &[type='search'] {\n    -webkit-appearance: textfield;\n    outline-offset: -2px;\n    &::-ms-clear {\n      display: none;\n      height: 0;\n      width: 0;\n    }\n    &::-webkit-search-cancel-button,\n    &::-webkit-search-decoration,\n    &::-webkit-search-results-button,\n    &::-webkit-search-results-decoration {\n      -webkit-appearance: none;\n      display: none;\n    }\n  }\n"])), function (_ref11) {
  var disabled = _ref11.disabled,
      error = _ref11.error,
      theme = _ref11.theme;

  if (error) {
    return css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteralLoose(["\n        color: ", ";\n      "])), theme.component.input.root.color);
  }

  if (disabled) {
    return css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteralLoose(["\n        color: ", ";\n      "])), theme.component.input.disabled.color);
  }

  return css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteralLoose(["\n      color: ", ";\n    "])), theme.component.input.root.color);
}, inputTypography, function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.input.root.padding;
}, inputTypography, placeholderCss, inputTypography, placeholderCss, inputTypography, placeholderCss, inputTypography, placeholderCss, inputTypography, placeholderCss);

var _excluded$s = ["className", "component", "defaultValue", "disabled", "error", "focused", "inputComponent", "loading", "maxLength", "onBlur", "onChange", "onClick", "onFocus", "onKeyUp", "onMouseDown", "readOnly", "required", "renderEndAdornment", "renderStartAdornment", "value"];
var InputBase = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      defaultValue = props.defaultValue,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$error = props.error,
      error = _props$error === void 0 ? false : _props$error,
      _props$focused = props.focused,
      focusedProp = _props$focused === void 0 ? false : _props$focused,
      _props$inputComponent = props.inputComponent,
      inputComponent = _props$inputComponent === void 0 ? 'input' : _props$inputComponent,
      _props$loading = props.loading,
      loading = _props$loading === void 0 ? false : _props$loading,
      maxLength = props.maxLength,
      onBlur = props.onBlur,
      onChange = props.onChange,
      onClick = props.onClick,
      onFocus = props.onFocus,
      onKeyUp = props.onKeyUp,
      onMouseDown = props.onMouseDown,
      _props$readOnly = props.readOnly,
      readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
      required = props.required,
      renderEndAdornment = props.renderEndAdornment,
      renderStartAdornment = props.renderStartAdornment,
      value = props.value,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$s);

  var _useState = useState(focusedProp),
      focused = _useState[0],
      setFocused = _useState[1];

  var inputRef = useElementRef(ref);
  var handleBlur = useCallback(function (event) {
    setFocused(false);
    onBlur == null ? void 0 : onBlur(event);
  }, [onBlur]);
  var handleClick = useCallback(function (event) {
    if (event.currentTarget === event.target) {
      var _inputRef$current;

      (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
    }

    onClick == null ? void 0 : onClick(event);
  }, [inputRef, onClick]);
  var handleFocus = useCallback(function (event) {
    setFocused(true);
    onFocus == null ? void 0 : onFocus(event);
  }, [onFocus]);
  return React.createElement(InputBaseRoot, {
    className: className,
    component: component,
    disabled: disabled,
    error: error,
    focused: focused || focusedProp,
    onClick: handleClick,
    outlined: true,
    shapeStyle: 'semi-square'
  }, renderStartAdornment && renderStartAdornment(props), React.createElement(InputBaseComponent, Object.assign({
    as: inputComponent,
    defaultValue: defaultValue,
    disabled: disabled,
    error: error,
    maxLength: maxLength,
    onBlur: handleBlur,
    onChange: onChange,
    onFocus: handleFocus,
    onKeyUp: onKeyUp,
    onMouseDown: onMouseDown,
    readOnly: readOnly,
    required: required,
    value: value
  }, otherProps, {
    ref: inputRef
  })), loading && React.createElement(InputAdornment, {
    position: 'end'
  }, React.createElement(CircularProgress, null)), renderEndAdornment && renderEndAdornment(props));
});
InputBase.displayName = 'InputBase';

var _excluded$t = ["aria-describedby", "aria-errormessage", "aria-invalid", "disabled", "error", "focused", "fullWidth", "hint", "id", "label", "maxLength", "onBlur", "onFocus", "placeholder", "renderCounter", "renderEndAdornment", "renderStartAdornment", "required", "type", "value"];
var TextField = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaDescribedBy = props['aria-describedby'],
      ariaErrorMessage = props['aria-errormessage'],
      ariaInvalid = props['aria-invalid'],
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$error = props.error,
      error = _props$error === void 0 ? false : _props$error,
      _props$focused = props.focused,
      focused = _props$focused === void 0 ? false : _props$focused,
      _props$fullWidth = props.fullWidth,
      fullWidth = _props$fullWidth === void 0 ? true : _props$fullWidth,
      hint = props.hint,
      id = props.id,
      label = props.label,
      maxLength = props.maxLength,
      onBlur = props.onBlur,
      onFocus = props.onFocus,
      placeholder = props.placeholder,
      renderCounter = props.renderCounter,
      renderEndAdornment = props.renderEndAdornment,
      renderStartAdornment = props.renderStartAdornment,
      _props$required = props.required,
      required = _props$required === void 0 ? false : _props$required,
      type = props.type,
      value = props.value,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$t);

  var textFieldHintId = useFormHelperTextId({
    hint: hint,
    id: id
  });
  return React.createElement(FieldWrapper, {
    disabled: disabled,
    error: error,
    fullWidth: fullWidth,
    hint: hint,
    id: id,
    label: label,
    renderCounter: renderCounter,
    required: required
  }, React.createElement(InputBase, Object.assign({}, otherProps, {
    "aria-describedby": ariaDescribedBy || textFieldHintId,
    "aria-errormessage": ariaErrorMessage || textFieldHintId,
    "aria-invalid": ariaInvalid || error,
    disabled: disabled,
    error: error,
    focused: focused,
    id: id,
    maxLength: maxLength,
    onBlur: onBlur,
    onFocus: onFocus,
    placeholder: placeholder,
    renderEndAdornment: renderEndAdornment,
    renderStartAdornment: renderStartAdornment,
    required: required,
    type: type,
    value: value,
    ref: ref
  })));
});
TextField.displayName = 'TextField';

var _templateObject$p, _templateObject2$c, _templateObject3$8, _templateObject4$6, _templateObject5$4, _templateObject6$3, _templateObject7$2, _templateObject8$2, _templateObject9$2, _templateObject10$2, _templateObject11$1, _templateObject12$1, _templateObject13$1;
var InputDateWrapper = /*#__PURE__*/styled.div(_templateObject$p || (_templateObject$p = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n  display: inline-block;\n"])));
var DatePickerContainer = /*#__PURE__*/styled.div(_templateObject2$c || (_templateObject2$c = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  top: ", ";\n\n  z-index: ", ";\n\n  display: grid;\n  grid-template-columns: ", ";\n  border-style: solid;\n  border-radius: ", ";\n  border-width: ", ";\n  border-color: ", ";\n\n  background-color: ", ";\n\n  color: ", ";\n\n  width: 17.5rem;\n\n  overflow: hidden;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.calendar.root.top;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.calendar.root.zIndex;
}, function (_ref3) {
  var activeMonths = _ref3.activeMonths,
      theme = _ref3.theme;
  return "repeat(" + activeMonths + ", " + theme.component.calendar.root.width + ")";
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.calendar.root.borderRadius;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.calendar.root.borderWidth;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.calendar.root.borderColor;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.calendar.root.backgroundColor;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.calendar.root.color;
});
var DatePickerHeader = /*#__PURE__*/styled.header(_templateObject3$8 || (_templateObject3$8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  text-transform: uppercase;\n\n  font-weight: ", ";\n\n  color: ", ";\n\n  background-color: ", ";\n\n  padding-top: ", ";\n  padding-right: ", ";\n  padding-bottom: ", ";\n  padding-left: ", ";\n"])), function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.calendar.header.fontWeight;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.calendar.header.color;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.component.calendar.header.backgroundColor;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.calendar.header.paddingTop;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.calendar.header.paddingRight;
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.component.calendar.header.paddingBottom;
}, function (_ref15) {
  var theme = _ref15.theme;
  return theme.component.calendar.header.paddingLeft;
});
var DatePickerBody = /*#__PURE__*/styled.main(_templateObject4$6 || (_templateObject4$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  padding-right: ", ";\n  padding-bottom: ", ";\n  padding-left: ", ";\n\n  font-weight: ", ";\n"])), function (_ref16) {
  var theme = _ref16.theme;
  return theme.component.calendar.header.paddingRight;
}, function (_ref17) {
  var theme = _ref17.theme;
  return theme.component.calendar.header.paddingBottom;
}, function (_ref18) {
  var theme = _ref18.theme;
  return theme.component.calendar.header.paddingLeft;
}, function (_ref19) {
  var theme = _ref19.theme;
  return theme.component.calendar.root.fontWeight;
});
var CalendarTitle = /*#__PURE__*/styled.span(_templateObject5$4 || (_templateObject5$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: ", ";\n  font-size: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n"])), function (_ref20) {
  var theme = _ref20.theme;
  return theme.component.calendar.header.fontFamily;
}, function (_ref21) {
  var theme = _ref21.theme;
  return theme.component.calendar.header.fontSize;
}, function (_ref22) {
  var theme = _ref22.theme;
  return theme.component.calendar.header.fontWeight;
}, function (_ref23) {
  var theme = _ref23.theme;
  return theme.component.calendar.header.lineHeight;
});
var DatePickerCellsContainer = /*#__PURE__*/styled.div(_templateObject6$3 || (_templateObject6$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: grid;\n  grid-template-columns: repeat(7, 1fr);\n  gap: ", ";\n"])), function (_ref24) {
  var theme = _ref24.theme;
  return theme.component.calendar.cell.gap;
});
var WeekDayContainer = /*#__PURE__*/styled.div(_templateObject7$2 || (_templateObject7$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  margin-bottom: ", ";\n"])), function (_ref25) {
  var theme = _ref25.theme;
  return theme.component.calendar.weekdays.marginBottom;
});
var WeekDayLabel = /*#__PURE__*/styled.span(_templateObject8$2 || (_templateObject8$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"])));
var EmptyDayCell = /*#__PURE__*/styled.div(_templateObject9$2 || (_templateObject9$2 = /*#__PURE__*/_taggedTemplateLiteralLoose([""])));
var DayCell = /*#__PURE__*/styled(ButtonBase)(_templateObject10$2 || (_templateObject10$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-radius: 50%;\n  text-align: center;\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-property: background-color, color;\n    transition-duration: ", ";\n    transition-timing-function: ", ";\n    will-change: background-color, color;\n  }\n\n  height: ", ";\n  width: ", ";\n\n  ", "\n"])), function (_ref26) {
  var theme = _ref26.theme;
  return theme.component.calendar.cell.transitionDuration;
}, function (_ref27) {
  var theme = _ref27.theme;
  return theme.component.calendar.cell.transitionTimingFunction;
}, function (_ref28) {
  var theme = _ref28.theme;
  return theme.component.calendar.cell.height;
}, function (_ref29) {
  var theme = _ref29.theme;
  return theme.component.calendar.cell.width;
}, function (_ref30) {
  var disabled = _ref30.disabled,
      selected = _ref30.selected,
      theme = _ref30.theme;

  if (selected) {
    return css(_templateObject11$1 || (_templateObject11$1 = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        color: ", ";\n      "])), theme.component.calendar.cellSelected.backgroundColor, theme.component.calendar.cellSelected.color);
  } else if (disabled) {
    return css(_templateObject12$1 || (_templateObject12$1 = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        color: ", ";\n      "])), theme.component.calendar.cellDisabled.backgroundColor, theme.component.calendar.cellDisabled.color);
  }

  return css(_templateObject13$1 || (_templateObject13$1 = _taggedTemplateLiteralLoose(["\n      background-color: ", ";\n      color: ", ";\n    "])), theme.component.calendar.cell.backgroundColor, theme.component.calendar.cell.color);
});

var CalendarDay = function CalendarDay(_ref) {
  var date = _ref.date,
      datepickerState = _ref.datepickerState,
      dayLabel = _ref.dayLabel;
  var dayRef = useRef(null);

  var _useDay = useDay(_extends({
    date: date,
    dayRef: dayRef
  }, datepickerState)),
      onClick = _useDay.onClick,
      tabIndex = _useDay.tabIndex;

  var selected = useMemo(function () {
    var _datepickerState$curr;

    var calendarDateTime = date.getTime();
    var currentDateTime = (_datepickerState$curr = datepickerState.currentDate) == null ? void 0 : _datepickerState$curr.getTime();
    return calendarDateTime === currentDateTime;
  }, [date, datepickerState]);
  var disabled = useMemo(function () {
    return datepickerState.isDateBlocked(date);
  }, [date, datepickerState]);

  if (!dayLabel) {
    return React.createElement(EmptyDayCell, null);
  }

  return React.createElement(DayCell, {
    "aria-label": date.toLocaleDateString(),
    "data-testid": "calendar-day-button",
    focusVisible: true,
    disabled: disabled,
    onClick: onClick,
    ref: dayRef,
    selected: selected,
    tabIndex: tabIndex,
    type: "button"
  }, dayLabel);
};

var ChevronLeftOutline = Icons.ChevronLeftOutline,
    ChevronRightOutline = Icons.ChevronRightOutline;
var CalendarMonth = function CalendarMonth(_ref) {
  var dayLabelFormat = _ref.dayLabelFormat,
      datepickerState = _ref.datepickerState,
      firstDayOfWeek = _ref.firstDayOfWeek,
      goToPreviousMonth = _ref.goToPreviousMonth,
      goToNextMonth = _ref.goToNextMonth,
      month = _ref.month,
      monthLabelFormat = _ref.monthLabelFormat,
      nextMonthAriaLabel = _ref.nextMonthAriaLabel,
      previousMonthAriaLabel = _ref.previousMonthAriaLabel,
      weekdayLabelFormat = _ref.weekdayLabelFormat,
      year = _ref.year;

  var _useMonth = useMonth({
    dayLabelFormat: dayLabelFormat,
    firstDayOfWeek: firstDayOfWeek,
    month: month,
    monthLabelFormat: monthLabelFormat,
    weekdayLabelFormat: weekdayLabelFormat,
    year: year
  }),
      days = _useMonth.days,
      weekdayLabels = _useMonth.weekdayLabels,
      monthLabel = _useMonth.monthLabel;

  var monthAndYearLabel = useMemo(function () {
    return monthLabel + " " + year;
  }, [monthLabel, year]);
  return React.createElement("div", null, React.createElement(DatePickerHeader, null, React.createElement(IconButton, {
    "aria-label": previousMonthAriaLabel,
    "data-testid": "prev-month-button",
    onClick: goToPreviousMonth,
    size: 'sm',
    variant: 'tertiary'
  }, React.createElement(ChevronLeftOutline, {
    size: "sm"
  })), React.createElement(CalendarTitle, null, monthAndYearLabel), React.createElement(IconButton, {
    "aria-label": nextMonthAriaLabel,
    "data-testid": "next-month-button",
    onClick: goToNextMonth,
    size: 'sm',
    variant: 'tertiary'
  }, React.createElement(ChevronRightOutline, {
    size: "sm"
  }))), React.createElement(DatePickerBody, null, React.createElement(WeekDayContainer, null, React.createElement(DatePickerCellsContainer, null, weekdayLabels.map(function (label, index) {
    return React.createElement(WeekDayLabel, {
      key: label + "-" + index
    }, React.createElement(Text, {
      weight: "bold",
      variant: "body3"
    }, label));
  }))), React.createElement(DatePickerCellsContainer, null, days.map(function (day, index) {
    if (typeof day === 'object') {
      return React.createElement(CalendarDay, {
        date: day.date,
        key: day.date.toString(),
        dayLabel: day.dayLabel,
        datepickerState: datepickerState
      });
    }

    return React.createElement(EmptyDayCell, {
      key: index
    });
  }))));
};

var Calendar = function Calendar(_ref) {
  var activeMonths = _ref.activeMonths,
      datepickerState = _ref.datepickerState,
      dayLabelFormat = _ref.dayLabelFormat,
      firstDayOfTheWeek = _ref.firstDayOfTheWeek,
      goToNextMonth = _ref.goToNextMonth,
      goToPreviousMonth = _ref.goToPreviousMonth,
      monthLabelFormat = _ref.monthLabelFormat,
      nextMonthAriaLabel = _ref.nextMonthAriaLabel,
      previousMonthAriaLabel = _ref.previousMonthAriaLabel,
      weekdayLabelFormat = _ref.weekdayLabelFormat;
  return React.createElement(DatePickerContainer, {
    activeMonths: activeMonths.length
  }, activeMonths.map(function (month) {
    return React.createElement(CalendarMonth, {
      dayLabelFormat: dayLabelFormat,
      datepickerState: datepickerState,
      firstDayOfWeek: firstDayOfTheWeek,
      goToNextMonth: goToNextMonth,
      goToPreviousMonth: goToPreviousMonth,
      key: month.year + "-" + month.month,
      month: month.month,
      monthLabelFormat: monthLabelFormat,
      nextMonthAriaLabel: nextMonthAriaLabel,
      previousMonthAriaLabel: previousMonthAriaLabel,
      weekdayLabelFormat: weekdayLabelFormat,
      year: month.year
    });
  }));
};

function formatMonthName(date) {
  return date.toLocaleString('default', {
    month: 'long'
  });
}
function formatWeekDayName(date) {
  return date.toLocaleString('default', {
    weekday: 'narrow'
  });
}
function formatDayName(date) {
  return date.toLocaleString('default', {
    day: 'numeric'
  });
}

var _excluded$u = ["autoComplete", "calendarButtonAriaLabel", "dayLabelFormat", "disabled", "firstDayOfTheWeek", "max", "min", "monthLabelFormat", "nextMonthAriaLabel", "onChange", "onSelectDate", "previousMonthAriaLabel", "weekdayLabelFormat", "value"],
    _excluded2 = ["activeMonths", "firstDayOfWeek", "goToNextMonthsByOneMonth", "goToPreviousMonthsByOneMonth", "goToDate"];
var CalendarOutline = Icons.CalendarOutline;

var renderDateIcon = function renderDateIcon(_ref) {
  var disabled = _ref.disabled,
      onIconClick = _ref.onIconClick,
      calendarButtonAriaLabel = _ref.calendarButtonAriaLabel;

  var renderIconMarkup = function renderIconMarkup(_ref2) {
    var error = _ref2.error;
    var iconColor = error ? 'colorRoleOnCriticalSecondary' : 'colorRoleOnBackground';
    return React.createElement(InputAdornment, {
      position: 'end'
    }, React.createElement(IconButton, {
      "aria-label": calendarButtonAriaLabel,
      "data-testid": "toggle-calendar",
      disabled: disabled,
      onClick: onIconClick,
      size: "sm",
      type: "button",
      variant: "tertiary"
    }, React.createElement(CalendarOutline, {
      iconColor: iconColor
    })));
  };

  return renderIconMarkup;
};

var SUNDAY = 0;
var DateInput = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var _ref3$autoComplete = _ref3.autoComplete,
      autoComplete = _ref3$autoComplete === void 0 ? 'off' : _ref3$autoComplete,
      calendarButtonAriaLabel = _ref3.calendarButtonAriaLabel,
      _ref3$dayLabelFormat = _ref3.dayLabelFormat,
      dayLabelFormat = _ref3$dayLabelFormat === void 0 ? formatDayName : _ref3$dayLabelFormat,
      disabled = _ref3.disabled,
      _ref3$firstDayOfTheWe = _ref3.firstDayOfTheWeek,
      firstDayOfTheWeek = _ref3$firstDayOfTheWe === void 0 ? SUNDAY : _ref3$firstDayOfTheWe,
      max = _ref3.max,
      min = _ref3.min,
      _ref3$monthLabelForma = _ref3.monthLabelFormat,
      monthLabelFormat = _ref3$monthLabelForma === void 0 ? formatMonthName : _ref3$monthLabelForma,
      _ref3$nextMonthAriaLa = _ref3.nextMonthAriaLabel,
      nextMonthAriaLabel = _ref3$nextMonthAriaLa === void 0 ? 'Próximo mês' : _ref3$nextMonthAriaLa,
      onChange = _ref3.onChange,
      onSelectDate = _ref3.onSelectDate,
      _ref3$previousMonthAr = _ref3.previousMonthAriaLabel,
      previousMonthAriaLabel = _ref3$previousMonthAr === void 0 ? 'Mês anterior' : _ref3$previousMonthAr,
      _ref3$weekdayLabelFor = _ref3.weekdayLabelFormat,
      weekdayLabelFormat = _ref3$weekdayLabelFor === void 0 ? formatWeekDayName : _ref3$weekdayLabelFor,
      _ref3$value = _ref3.value,
      valueProp = _ref3$value === void 0 ? '' : _ref3$value,
      props = _objectWithoutPropertiesLoose(_ref3, _excluded$u);

  var wrapperRef = useRef(null);

  var _useState = useState(false),
      calendarShown = _useState[0],
      setCalendarShown = _useState[1];

  var _useState2 = useState(null),
      selectedDate = _useState2[0],
      setSelectedDate = _useState2[1];

  var _useState3 = useState(valueProp),
      value = _useState3[0],
      setValue = _useState3[1];

  function closeCalendar() {
    setCalendarShown(false);
  }

  function toggleCalendar() {
    setCalendarShown(!calendarShown);
  }

  var handleSelectDate = useCallback(function (_ref4) {
    var startDate = _ref4.startDate;

    if (startDate === null) {
      return;
    }

    var localeString = startDate.toLocaleDateString();
    setSelectedDate(startDate);
    setValue(localeString);
    var event = {
      target: {
        value: localeString
      }
    };
    onChange == null ? void 0 : onChange(event);
    onSelectDate == null ? void 0 : onSelectDate(event, localeString);
    closeCalendar();
  }, [onChange, onSelectDate]);

  function handleInputFocus(e) {
    if (!disabled) {
      props.onFocus == null ? void 0 : props.onFocus(e);
      toggleCalendar();
    }
  }

  var maxBookingDate = max ? new Date(max) : undefined;
  var minBookingDate = min ? new Date(min) : undefined;

  var _useDatepicker = useDatepicker({
    endDate: null,
    firstDayOfWeek: firstDayOfTheWeek,
    focusedInput: START_DATE,
    maxBookingDate: maxBookingDate,
    minBookingDate: minBookingDate,
    onDatesChange: handleSelectDate,
    startDate: selectedDate
  }),
      activeMonths = _useDatepicker.activeMonths,
      firstDayOfWeek = _useDatepicker.firstDayOfWeek,
      goToNextMonthsByOneMonth = _useDatepicker.goToNextMonthsByOneMonth,
      goToPreviousMonthsByOneMonth = _useDatepicker.goToPreviousMonthsByOneMonth,
      goToDate = _useDatepicker.goToDate,
      datepickerBaseState = _objectWithoutPropertiesLoose(_useDatepicker, _excluded2);

  var datepickerState = useMemo(function () {
    return _extends({}, datepickerBaseState, {
      currentDate: selectedDate
    });
  }, [datepickerBaseState, selectedDate]);
  var handleInputChange = useCallback(function (e) {
    setValue(e.target.value);
    var incomingDate = parseDate(e.target.value, 'dd/MM/yyyy', new Date());
    closeCalendar();
    onChange == null ? void 0 : onChange(e);

    if (!disabled && isValidDate(incomingDate)) {
      setSelectedDate(incomingDate);
      goToDate(incomingDate);
    }
  }, [disabled, goToDate, onChange]);
  useOnClickOutside(wrapperRef, closeCalendar);
  useEffect(function () {
    if (typeof value === 'string') {
      var date = parseDate(value, 'dd/MM/yyyy', new Date());

      if (isValidDate(date)) {
        setSelectedDate(date);
      }
    }
  }, [value]);
  return React.createElement(InputDateWrapper, {
    ref: wrapperRef
  }, React.createElement(TextField, Object.assign({
    autoComplete: autoComplete,
    disabled: disabled,
    renderEndAdornment: renderDateIcon({
      disabled: disabled,
      onIconClick: toggleCalendar,
      calendarButtonAriaLabel: calendarButtonAriaLabel
    }),
    onChange: handleInputChange,
    onFocus: handleInputFocus,
    ref: ref,
    type: "text",
    value: value
  }, props)), calendarShown && React.createElement(Calendar, {
    activeMonths: activeMonths,
    datepickerState: datepickerState,
    dayLabelFormat: dayLabelFormat,
    firstDayOfTheWeek: firstDayOfWeek,
    goToNextMonth: goToNextMonthsByOneMonth,
    goToPreviousMonth: goToPreviousMonthsByOneMonth,
    monthLabelFormat: monthLabelFormat,
    nextMonthAriaLabel: nextMonthAriaLabel,
    previousMonthAriaLabel: previousMonthAriaLabel,
    weekdayLabelFormat: weekdayLabelFormat
  }));
});
DateInput.displayName = 'DateInput';

var _templateObject$q;
var OverlayRoot = /*#__PURE__*/styled.div(function (_ref) {
  var hidden = _ref.hidden,
      overlay = _ref.theme.component.overlay;
  return css(_templateObject$q || (_templateObject$q = _taggedTemplateLiteralLoose(["\n    -webkit-tap-highlight-color: transparent;\n    align-items: center;\n    background-color: ", ";\n    bottom: 0;\n    display: ", ";\n    left: 0;\n    height: 100%;\n    justify-content: center;\n    opacity: ", ";\n    pointer-events: auto;\n    position: fixed;\n    right: 0;\n    top: 0;\n    width: 100%;\n    z-index: ", ";\n  "])), overlay.root.backgroundColor, hidden ? 'none' : 'flex', overlay.root.opacity, overlay.root.zIndex);
});

var Overlay = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$ariaHidden = props['aria-hidden'],
      ariaHidden = _props$ariaHidden === void 0 ? true : _props$ariaHidden,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$hidden = props.hidden,
      hidden = _props$hidden === void 0 ? true : _props$hidden;
  return React.createElement(OverlayRoot, Object.assign({
    "aria-hidden": ariaHidden,
    as: component,
    hidden: hidden
  }, props, {
    ref: ref
  }));
});
Overlay.displayName = 'Overlay';

var _templateObject$r;
var ModalOverlay = /*#__PURE__*/styled(Overlay)(_templateObject$r || (_templateObject$r = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  z-index: -1;\n\n  @media print {\n    position: absolute !important;\n  }\n"])));

var _excluded$v = ["children", "component", "onClose", "onKeyDown", "onOverlayClick", "open"];
var defaultProps$4 = {
  open: false
};
var Modal = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      Component = _props$component === void 0 ? 'div' : _props$component,
      onClose = props.onClose,
      onKeyDownProp = props.onKeyDown,
      onOverlayClick = props.onOverlayClick,
      _props$open = props.open,
      open = _props$open === void 0 ? defaultProps$4.open : _props$open,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$v);

  var onKeyDown = useCallback(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);

    if (event.key !== 'Escape') {
      return;
    }

    event.stopPropagation();
    onClose == null ? void 0 : onClose(event, 'escapeKeyDown');
  }, [onClose, onKeyDownProp]);
  var onOverlayClickInterceptor = useCallback(function (event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    onOverlayClick == null ? void 0 : onOverlayClick(event);
    onClose == null ? void 0 : onClose(event, 'overlayClick');
  }, [onClose, onOverlayClick]);
  var rootRef = useElementRef(ref);
  return React.createElement(Component, Object.assign({
    onKeyDown: onKeyDown,
    open: open,
    role: 'presentation'
  }, otherProps, {
    ref: rootRef
  }), React.createElement(ModalOverlay, {
    onClick: onOverlayClickInterceptor,
    hidden: !open
  }), children);
});
Modal.displayName = 'Modal';

var Context = /*#__PURE__*/createContext(null);
Context.displayName = 'ModalContext';
var ModalContext = function ModalContext(props) {
  var children = props.children,
      open = props.open;
  var context = useMemo(function () {
    return {
      open: open
    };
  }, [open]);
  return React.createElement(Context.Provider, {
    value: context
  }, children);
};
var useModalContext = function useModalContext() {
  return useContext(Context);
};

var useModal = function useModal(_ref) {
  var onClose = _ref.onClose,
      onMouseDownProp = _ref.onMouseDown,
      onOverlayClickProp = _ref.onOverlayClick,
      openProp = _ref.open;
  var overlayClick = useRef();
  var context = useModalContext();
  var open = useMemo(function () {
    if (typeof openProp === 'boolean') {
      return openProp;
    }

    if (typeof openProp === 'string') {
      return openProp === 'true';
    }

    if (context !== null) {
      return context.open;
    }

    return false;
  }, [context, openProp]);
  var onMouseDown = useCallback(function (event) {
    overlayClick.current = event.target === event.currentTarget;
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
  }, [onMouseDownProp]);
  var onOverlayClick = useCallback(function (event) {
    if (!overlayClick.current) {
      return;
    }

    overlayClick.current = null;
    onOverlayClickProp == null ? void 0 : onOverlayClickProp(event);
    onClose == null ? void 0 : onClose(event, 'overlayClick');
  }, [onClose, onOverlayClickProp]);
  return {
    onMouseDown: onMouseDown,
    onOverlayClick: onOverlayClick,
    open: open
  };
};

var _templateObject$s;
var GridContainerRoot = /*#__PURE__*/styled(Container)(function (_ref) {
  var templateColumns = _ref.templateColumns,
      templateRows = _ref.templateRows,
      _ref$theme = _ref.theme,
      breakpoint = _ref$theme.breakpoint,
      gridContainer = _ref$theme.component.gridContainer,
      variant = _ref.variant;
  return css(_templateObject$s || (_templateObject$s = _taggedTemplateLiteralLoose(["\n    box-sizing: border-box;\n    display: grid;\n    justify-content: start;\n    list-style-type: none;\n    padding-top: 0;\n\n    @media (min-width: ", ") {\n      grid-column-gap: ", ";\n      grid-template-columns: repeat(", ", 1fr);\n      grid-template-rows: ", ";\n    }\n\n    @media (min-width: ", ") {\n      grid-column-gap: ", ";\n    }\n\n    @media (min-width: ", ") {\n      grid-column-gap: ", ";\n    }\n\n    @media (min-width: ", ") {\n      grid-column-gap: ", ";\n    }\n  "])), breakpoint.breakpointSmMinWidth, variant === 'loose' ? gridContainer.looseSm.columnGap : gridContainer.tightSm.columnGap, templateColumns, templateRows === 0 ? 'none' : "repeat(" + templateRows + ", 1fr", breakpoint.breakpointMdMinWidth, variant === 'loose' ? gridContainer.looseMd.columnGap : gridContainer.tightMd.columnGap, breakpoint.breakpointLgMinWidth, variant === 'loose' ? gridContainer.looseLg.columnGap : gridContainer.tightLg.columnGap, breakpoint.breakpointXlMinWidth, variant === 'loose' ? gridContainer.looseXl.columnGap : gridContainer.tightXl.columnGap);
});

var _excluded$w = ["component", "templateColumns", "templateRows", "variant"];
var defaultProps$5 = {
  templateColumns: 12,
  templateRows: 0
};
var GridContainer = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$templateColumn = props.templateColumns,
      templateColumnsProp = _props$templateColumn === void 0 ? defaultProps$5.templateColumns : _props$templateColumn,
      templateRowsProp = props.templateRows,
      variant = props.variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$w);

  var templateColumns = useMemo(function () {
    if (typeof templateColumnsProp === 'undefined' || templateColumnsProp === 'undefined') {
      return defaultProps$5.templateColumns;
    }

    return templateColumnsProp;
  }, [templateColumnsProp]);
  var templateRows = useMemo(function () {
    if (typeof templateRowsProp === 'undefined' || templateRowsProp === 'undefined') {
      return defaultProps$5.templateRows;
    }

    return templateRowsProp;
  }, [templateRowsProp]);
  return React.createElement(GridContainerRoot, Object.assign({
    component: component,
    templateColumns: templateColumns,
    templateRows: templateRows,
    variant: variant
  }, otherProps, {
    ref: ref
  }));
});
GridContainer.displayName = 'GridContainer';

var _templateObject$t, _templateObject2$d;
var DialogContainer = /*#__PURE__*/styled(GridContainer)(_templateObject$t || (_templateObject$t = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  bottom: 0;\n  display: ", ";\n  height: ", ";\n  left: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  width: ", ";\n  z-index: ", ";\n\n  @media print {\n    position: absolute !important;\n  }\n"])), function (_ref) {
  var open = _ref.open;
  return open ? 'flex' : 'none';
}, function (_ref2) {
  var open = _ref2.open;
  return open ? '100%' : 0;
}, function (_ref3) {
  var open = _ref3.open;
  return open ? '100%' : 0;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.dialog.container.zIndex;
});
var DialogSurface = /*#__PURE__*/styled(Surface)(_templateObject2$d || (_templateObject2$d = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n  display: ", ";\n  flex-direction: column;\n  font-family: ", ";\n  grid-column: 1 / span 12;\n  height: fit-content;\n  left: 0;\n  justify-content: space-between;\n  margin: auto;\n  min-height: ", ";\n  min-width: ", ";\n  outline-color: ", ";\n  padding-bottom: ", ";\n  padding-left: ", ";\n  padding-right: ", ";\n  padding-top: ", ";\n  pointer-events: ", ";\n  right: 0;\n  row-gap: ", ";\n  width: fit-content;\n  z-index: ", ";\n\n  @media (min-width: ", ") {\n    grid-column: 3 / span 8;\n  }\n\n  &:not([open]) {\n    display: none;\n  }\n\n  &:focus {\n    outline: ", " none transparent;\n    // Despite being redundant, it ensures that the outline is not displayed in Firefox.\n    outline-style: none;\n  }\n\n  &:focus-visible {\n    outline: ", " none transparent;\n    // Despite being redundant, it ensures that the outline is not displayed in Firefox.\n    outline-style: none;\n  }\n"])), function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.dialog.surface.color;
}, function (_ref6) {
  var open = _ref6.open;
  return open ? 'flex' : 'none';
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.dialog.surface.fontFamily;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.dialog.surface.minHeight;
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.dialog.surface.minWidth;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.dialog.surface.outlineColor;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.component.dialog.surface.paddingBottom;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.dialog.surface.paddingLeft;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.dialog.surface.paddingRight;
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.component.dialog.surface.paddingTop;
}, function (_ref15) {
  var open = _ref15.open;
  return open ? 'auto' : 'none';
}, function (_ref16) {
  var theme = _ref16.theme;
  return theme.component.dialog.surface.rowGap;
}, function (_ref17) {
  var theme = _ref17.theme;
  return theme.component.dialog.surface.zIndex;
}, function (_ref18) {
  var theme = _ref18.theme;
  return theme.breakpoint.breakpointMdMinWidth;
}, function (_ref19) {
  var theme = _ref19.theme;
  return theme.component.dialog.focused.outlineWidth;
}, function (_ref20) {
  var theme = _ref20.theme;
  return theme.component.dialog.focusVisible.outlineWidth;
});

var _excluded$x = ["aria-describedby", "aria-labelledby", "children", "className", "component", "onClose", "onKeyDown", "onMouseDown", "onOverlayClick", "open", "role", "tabIndex"];
var Dialog = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaDescribedBy = props['aria-describedby'],
      ariaLabelledBy = props['aria-labelledby'],
      children = props.children,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'dialog' : _props$component,
      onClose = props.onClose,
      onKeyDown = props.onKeyDown,
      onMouseDownProp = props.onMouseDown,
      onOverlayClickProp = props.onOverlayClick,
      openProp = props.open,
      _props$role = props.role,
      role = _props$role === void 0 ? 'dialog' : _props$role,
      _props$tabIndex = props.tabIndex,
      tabIndex = _props$tabIndex === void 0 ? -1 : _props$tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$x);
  /**
   * @see useModal
   */


  var _useModal = useModal({
    onMouseDown: onMouseDownProp,
    onOverlayClick: onOverlayClickProp,
    open: openProp
  }),
      onMouseDown = _useModal.onMouseDown,
      onOverlayClick = _useModal.onOverlayClick,
      open = _useModal.open;

  var dialogRef = useElementRef(ref);
  useEffect(function () {
    if (open) {
      var _dialogRef$current;

      (_dialogRef$current = dialogRef.current) == null ? void 0 : _dialogRef$current.focus();
    }
  }, [dialogRef, open]);
  return React.createElement(Modal, Object.assign({
    component: DialogContainer,
    onClose: onClose,
    onKeyDown: onKeyDown,
    onMouseDown: onMouseDown,
    onOverlayClick: onOverlayClick,
    open: open
  }, otherProps), React.createElement(DialogSurface, {
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    className: className,
    component: component,
    open: open,
    role: role,
    shadow: 'lowest',
    shapeStyle: 'semi-square',
    tabIndex: tabIndex,
    ref: dialogRef
  }, children));
});
Dialog.displayName = 'Dialog';

var _templateObject$u, _templateObject2$e, _templateObject3$9;
var resetStyles = /*#__PURE__*/css(function (_ref) {
  var dialogActions = _ref.theme.component.dialogActions;
  return css(_templateObject$u || (_templateObject$u = _taggedTemplateLiteralLoose(["\n    list-style-type: none;\n    margin-block-end: ", ";\n    margin-block-start: ", ";\n    margin-inline-end: ", ";\n    margin-inline-start: ", ";\n    padding-inline-start: ", ";\n  "])), dialogActions.root.marginBlockEnd, dialogActions.root.marginBlockStart, dialogActions.root.marginInlineEnd, dialogActions.root.marginInlineStart, dialogActions.root.paddingInlineStart);
});
var flexStyles = /*#__PURE__*/css(function (_ref2) {
  var dialogActions = _ref2.theme.component.dialogActions;
  return css(_templateObject2$e || (_templateObject2$e = _taggedTemplateLiteralLoose(["\n    align-items: end;\n    column-gap: ", ";\n    display: flex;\n    justify-content: flex-end;\n  "])), dialogActions.root.columnGap);
});
var DialogActionsRoot = /*#__PURE__*/styled.menu(function (_ref3) {
  var dialogActions = _ref3.theme.component.dialogActions;
  return css(_templateObject3$9 || (_templateObject3$9 = _taggedTemplateLiteralLoose(["\n    border-top-color: ", ";\n    border-top-style: solid;\n    border-top-width: ", ";\n    margin-top: ", ";\n    padding-top: ", ";\n    width: 100%;\n\n    ", "\n    ", "\n  "])), dialogActions.root.borderTopColor, dialogActions.root.borderTopWidth, dialogActions.root.marginTop, dialogActions.root.paddingTop, flexStyles, resetStyles);
});

var _excluded$y = ["children", "className", "component", "type"];
var DialogActions = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'menu' : _props$component,
      _props$type = props.type,
      type = _props$type === void 0 ? 'toolbar' : _props$type,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$y);

  return React.createElement(DialogActionsRoot, Object.assign({
    as: component,
    className: className,
    type: type
  }, otherProps, {
    ref: ref
  }), children);
});
DialogActions.displayName = 'DialogActions';

var _templateObject$v;
var DialogContentRoot = /*#__PURE__*/styled.div(_templateObject$v || (_templateObject$v = /*#__PURE__*/_taggedTemplateLiteralLoose([""])));

var _excluded$z = ["className", "component"];
var DialogContent = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$z);

  return React.createElement(DialogContentRoot, Object.assign({
    as: component,
    className: className
  }, otherProps, {
    ref: ref
  }));
});
DialogContent.displayName = 'DialogContent';

var _excluded$A = ["component", "variant", "weight"];
var DialogContentText = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'p' : _props$component,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'body1' : _props$variant,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'regular' : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$A);

  return React.createElement(Text, Object.assign({
    component: component,
    variant: variant,
    weight: weight
  }, otherProps, {
    ref: ref
  }));
});
DialogContentText.displayName = 'DialogContentText';

var _excluded$B = ["component", "variant", "weight"];
var Heading = /*#__PURE__*/forwardRef(function (props, ref) {
  var componentProp = props.component,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'heading1' : _props$variant,
      weight = props.weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$B);

  var component = useMemo(function () {
    if (variant === 'inherit' && !componentProp) {
      return 'h1';
    }

    return componentProp;
  }, [componentProp, variant]);
  return React.createElement(Typography, Object.assign({
    component: component,
    ref: ref,
    variant: variant,
    weight: weight
  }, otherProps));
});
Heading.displayName = 'Heading';

var _templateObject$w, _templateObject2$f, _templateObject3$a, _templateObject4$7;
var HeaderRoot = /*#__PURE__*/styled.header(function (_ref) {
  var header = _ref.theme.component.header;
  return css(_templateObject$w || (_templateObject$w = _taggedTemplateLiteralLoose(["\n    background-color: transparent;\n    min-height: ", ";\n    min-width: ", ";\n    width: 100%;\n  "])), header.root.minHeight, header.root.minWidth);
});
var HeaderContainer = /*#__PURE__*/styled.div(function (_ref2) {
  var disableGutters = _ref2.disableGutters,
      startAdornment = _ref2.startAdornment,
      header = _ref2.theme.component.header;
  var marginLeft = header.gutters.marginLeft;

  if (startAdornment) {
    marginLeft = header.guttersWithStartAdornment.marginLeft;
  }

  if (disableGutters) {
    marginLeft = header.container.marginLeft;
  }

  return css(_templateObject2$f || (_templateObject2$f = _taggedTemplateLiteralLoose(["\n      align-items: center;\n      column-gap: ", ";\n      display: flex;\n      flex: 0 0 auto;\n      flex-direction: row;\n      justify-content: space-between;\n      margin-left: ", ";\n      margin-right: ", ";\n    "])), header.container.columnGap, marginLeft, disableGutters ? header.container.marginRight : header.gutters.marginRight);
});
var HeaderContent = /*#__PURE__*/styled.div(function (_ref3) {
  var header = _ref3.theme.component.header;
  return css(_templateObject3$a || (_templateObject3$a = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    display: flex;\n    flex: 1;\n    margin-bottom: ", ";\n    margin-top: ", ";\n    min-height: ", ";\n    min-width: ", ";\n    width: max-content;\n  "])), header.content.marginBottom, header.content.marginTop, header.content.minHeight, header.content.minWidth);
});
var HeaderAdornment = /*#__PURE__*/styled.div(function (_ref4) {
  var header = _ref4.theme.component.header;
  return css(_templateObject4$7 || (_templateObject4$7 = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    column-gap: ", ";\n    display: flex;\n    margin-bottom: ", ";\n    margin-top: ", ";\n    min-height: ", ";\n    min-width: ", ";\n  "])), header.adornment.columnGap, header.adornment.marginBottom, header.adornment.marginTop, header.adornment.minHeight, header.adornment.minWidth);
});

var _excluded$C = ["className", "children", "component", "disableGutters", "endAdornment", "startAdornment"];
var Header = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'header' : _props$component,
      _props$disableGutters = props.disableGutters,
      disableGutters = _props$disableGutters === void 0 ? false : _props$disableGutters,
      endAdornment = props.endAdornment,
      startAdornment = props.startAdornment,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$C);

  return React.createElement(HeaderRoot, Object.assign({
    as: component,
    className: className
  }, otherProps, {
    ref: ref
  }), React.createElement(HeaderContainer, {
    disableGutters: disableGutters,
    startAdornment: startAdornment
  }, startAdornment && React.createElement(HeaderAdornment, null, startAdornment), React.createElement(HeaderContent, null, children), endAdornment && React.createElement(HeaderAdornment, null, endAdornment)));
});
Header.displayName = 'Header';

var _templateObject$x;
var DialogHeaderRoot = /*#__PURE__*/styled(Header)(function (_ref) {
  var dialogHeader = _ref.theme.component.dialogHeader;
  return css(_templateObject$x || (_templateObject$x = _taggedTemplateLiteralLoose(["\n    column-gap: ", ";\n    min-height: ", ";\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n  "])), dialogHeader.root.columnGap, dialogHeader.root.minHeight, dialogHeader.root.paddingBottom, dialogHeader.root.paddingLeft, dialogHeader.root.paddingRight, dialogHeader.root.paddingTop);
});

var _excluded$D = ["component", "disableGutters", "endAdornment", "variant", "weight"];
var DialogHeader = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'h2' : _props$component,
      _props$disableGutters = props.disableGutters,
      disableGutters = _props$disableGutters === void 0 ? true : _props$disableGutters,
      endAdornment = props.endAdornment,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'heading4' : _props$variant,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'bold' : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$D);

  return React.createElement(DialogHeaderRoot, {
    disableGutters: disableGutters,
    endAdornment: endAdornment,
    ref: ref
  }, React.createElement(Heading, Object.assign({
    component: component,
    variant: variant,
    weight: weight
  }, otherProps)));
});
DialogHeader.displayName = 'DialogHeader';

var _templateObject$y, _templateObject2$g, _templateObject3$b, _templateObject4$8;
var drawerStyles = /*#__PURE__*/css(_templateObject$y || (_templateObject$y = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: fixed;\n"])));
var DrawerGridContainer = /*#__PURE__*/styled(GridContainer)(function (_ref) {
  var open = _ref.open,
      drawer = _ref.theme.component.drawer;
  return css(_templateObject2$g || (_templateObject2$g = _taggedTemplateLiteralLoose(["\n    align-content: stretch;\n    bottom: 0;\n    display: ", ";\n    grid-template-columns: repeat(12, 1fr);\n    grid-template-rows: repeat(12, 1fr);\n    left: 0;\n    height: 100%;\n    padding-left: ", ";\n    padding-right: ", ";\n    right: 0;\n    top: 0;\n    width: 100%;\n    z-index: ", ";\n\n    ", "\n  "])), open ? 'grid' : 'none', drawer.container.paddingLeft, drawer.container.paddingRight, drawer.container.zIndex, drawerStyles);
});
var DrawerMaxContainer = /*#__PURE__*/styled.div(function (_ref2) {
  var anchor = _ref2.anchor,
      maxDimensions = _ref2.maxDimensions,
      breakpoint = _ref2.theme.breakpoint;
  var isHorizontalAnchor = anchor === 'left' || anchor === 'right';
  var isStartAnchor = anchor === 'left' || anchor === 'top';
  var gridProperty = isHorizontalAnchor ? 'grid-column' : 'grid-row';
  var mediaQuery = isHorizontalAnchor ? 'min-width' : 'min-height';
  return css(_templateObject3$b || (_templateObject3$b = _taggedTemplateLiteralLoose(["\n    --grid-full-line: 1 / span 12;\n    align-self: ", ";\n    grid-column: var(--grid-full-line);\n    grid-row: var(--grid-full-line);\n    justify-self: ", ";\n    position: relative;\n    z-index: ", ";\n\n    @media (min-width: ", ") {\n      ", ": ", "\n    }\n\n    @media (", ": ", ") {\n      ", ": ", ";\n    }\n\n    @media (", ": ", ") {\n      ", ": ", ";\n    }\n\n    @media (", ": ", ") {\n      ", ": ", ";\n    }\n  "])), function () {
    if (anchor === 'top' && !maxDimensions) {
      return 'start';
    }

    if (anchor === 'bottom' && !maxDimensions) {
      return 'end';
    }

    return 'stretch';
  }, function () {
    if (anchor === 'left' && !maxDimensions) {
      return 'start';
    }

    if (anchor === 'right' && !maxDimensions) {
      return 'end';
    }

    return 'stretch';
  }, function (_ref3) {
    var theme = _ref3.theme;
    return theme.component.dialog.container.zIndex;
  }, breakpoint.breakpointSmMinWidth, gridProperty, function () {
    if (maxDimensions) {
      return 'var(--grid-full-line)';
    }

    return isStartAnchor ? '1 / span 11' : '2 / span 11';
  }, mediaQuery, breakpoint.breakpointMdMinWidth, gridProperty, isStartAnchor ? '1 / span 8' : '5 / span 8', mediaQuery, breakpoint.breakpointLgMinWidth, gridProperty, isStartAnchor ? '1 / span 6' : '7 / span 6', mediaQuery, breakpoint.breakpointXlMinWidth, gridProperty, isStartAnchor ? '1 / span 4' : '9 / span 4');
});
var DrawerSurface = /*#__PURE__*/styled(Surface)(function (_ref4) {
  var anchor = _ref4.anchor,
      maxDimensions = _ref4.maxDimensions,
      open = _ref4.open,
      drawer = _ref4.theme.component.drawer;
  var isHorizontalAnchor = anchor === 'left' || anchor === 'right';
  return css(_templateObject4$8 || (_templateObject4$8 = _taggedTemplateLiteralLoose(["\n      -webkit-overflow-scrolling: touch;\n      border-bottom-left-radius: ", ";\n      border-bottom-right-radius: ", ";\n      border-top-left-radius: ", ";\n      border-top-right-radius: ", ";\n      box-shadow: ", ";\n      flex: 1 0 auto;\n      flex-direction: column;\n      height: ", ";\n      max-height: 100%;\n      max-width: 100%;\n      outline: 0 none transparent;\n      // Despite being redundant, it ensures that the outline is not displayed in Firefox.\n      outline-style: none;\n      overflow-y: auto;\n      pointer-events: ", ";\n      position: static;\n      width: ", ";\n      z-index: ", ";\n    "])), drawer[anchor + "Anchor"].borderBottomLeftRadius, drawer[anchor + "Anchor"].borderBottomRightRadius, drawer[anchor + "Anchor"].borderTopLeftRadius, drawer[anchor + "Anchor"].borderTopRightRadius, drawer[anchor + "Anchor"].boxShadow, isHorizontalAnchor || maxDimensions ? '100%' : 'auto', open ? 'auto' : 'none', !isHorizontalAnchor || maxDimensions ? '100%' : 'auto', function (_ref5) {
    var theme = _ref5.theme;
    return theme.component.dialog.surface.zIndex;
  });
});

var _excluded$E = ["anchor", "children", "className", "component", "maxDimensions", "onClose", "onKeyDown", "onMouseDown", "onOverlayClick", "open", "tabIndex"];
var defaultProps$6 = {
  anchor: 'left'
};
var Drawer = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$anchor = props.anchor,
      anchorProp = _props$anchor === void 0 ? defaultProps$6.anchor : _props$anchor,
      children = props.children,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'aside' : _props$component,
      _props$maxDimensions = props.maxDimensions,
      maxDimensions = _props$maxDimensions === void 0 ? false : _props$maxDimensions,
      onClose = props.onClose,
      onKeyDown = props.onKeyDown,
      onMouseDownProp = props.onMouseDown,
      onOverlayClickProp = props.onOverlayClick,
      openProp = props.open,
      _props$tabIndex = props.tabIndex,
      tabIndex = _props$tabIndex === void 0 ? -1 : _props$tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$E);

  var anchor = useMemo(function () {
    if (anchorProp === 'undefined') {
      return defaultProps$6.anchor;
    }

    return anchorProp;
  }, [anchorProp]);
  /**
   * @see useModal
   */

  var _useModal = useModal({
    onMouseDown: onMouseDownProp,
    onOverlayClick: onOverlayClickProp,
    open: openProp
  }),
      onMouseDown = _useModal.onMouseDown,
      onOverlayClick = _useModal.onOverlayClick,
      open = _useModal.open;

  var drawerSurfaceRef = useElementRef(ref);
  useEffect(function () {
    if (open) {
      var _drawerSurfaceRef$cur;

      (_drawerSurfaceRef$cur = drawerSurfaceRef.current) == null ? void 0 : _drawerSurfaceRef$cur.focus();
    }
  }, [drawerSurfaceRef, open]);
  return React.createElement(Modal, Object.assign({
    component: DrawerGridContainer,
    onClose: onClose,
    onKeyDown: onKeyDown,
    onMouseDown: onMouseDown,
    onOverlayClick: onOverlayClick,
    open: open
  }, otherProps), React.createElement(DrawerMaxContainer, {
    anchor: anchor,
    maxDimensions: maxDimensions
  }, React.createElement(DrawerSurface, {
    anchor: anchor,
    className: className,
    component: component,
    hidden: !open,
    maxDimensions: maxDimensions,
    ref: drawerSurfaceRef,
    tabIndex: tabIndex,
    open: open
  }, children)));
});
Drawer.displayName = 'Drawer';

var _templateObject$z, _templateObject2$h, _templateObject3$c, _templateObject4$9;
var htmlStyles = /*#__PURE__*/css(_templateObject$z || (_templateObject$z = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  -moz-osx-font-smoothing: grayscale;\n  -moz-text-size-adjust: 100%;\n  -ms-text-size-adjust: 100%;\n  -webkit-font-smoothing: antialiased;\n  -webkit-text-size-adjust: 100%;\n  box-sizing: border-box;\n  color-scheme: ", ";\n  display: block;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.colorScheme;
});
var reducedMotionStyles = /*#__PURE__*/css(_templateObject2$h || (_templateObject2$h = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  animation-duration: ", " !important;\n  animation-iteration-count: 1 !important;\n  transition-duration: ", " !important;\n"])), function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.globalStyle.reducedMotion.animationDuration;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.globalStyle.reducedMotion.transitionDuration;
});
var bodyStyles = /*#__PURE__*/css(_templateObject3$c || (_templateObject3$c = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  background-color: ", ";\n  color: ", ";\n  font-family: ", ";\n  font-size: 1rem;\n  font-weight: ", ";\n\n  @media print {\n    background-color: ", ";\n  }\n"])), function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.globalStyle.body.backgroundColor;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.globalStyle.body.color;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.globalStyle.body.fontFamily;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.globalStyle.body.fontWeight;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.globalStyle.bodyPrint.backgroundColor;
});
var GlobalStyleRoot = /*#__PURE__*/createGlobalStyle(_templateObject4$9 || (_templateObject4$9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  html {\n    font-size: 1rem;\n\n    b {\n      font-weight: ", ";\n    }\n\n    strong {\n      font-weight: ", ";\n    }\n\n    ", "\n  }\n\n  body {\n    display: block;\n    margin-bottom: ", ";\n    margin-left: ", ";\n    margin-right: ", ";\n    margin-top: ", ";\n    min-height: 100vh;\n\n    ", "\n\n    &::backdrop {\n      background-color: ", ";\n    }\n  }\n\n  abbr[title] {\n    border-bottom: none;\n    text-decoration: underline;\n    text-decoration: underline dotted;\n  }\n\n  code,\n  kbd,\n  samp {\n    font-family: monospace, monospace;\n    font-size: 1em;\n  }\n\n  details {\n    display: block;\n  }\n\n  hr {\n    box-sizing: content-box;\n    height: 0;\n    overflow: visible;\n  }\n\n  img {\n    border-style: none;\n  }\n\n  legend {\n    box-sizing: border-box;\n    color: inherit;\n    display: table;\n    max-width: 100%;\n    padding: 0;\n    white-space: normal;\n  }\n\n  main {\n    display: block;\n  }\n\n  pre {\n    font-family: monospace, monospace;\n    font-size: 1em;\n  }\n\n  select {\n    text-transform: none;\n  }\n\n  small {\n    font-size: 80%;\n  }\n\n  sub,\n  sup {\n    font-size: 75%;\n    line-height: 0;\n    position: relative;\n    vertical-align: baseline;\n  }\n\n  sub {\n    bottom: -0.25em;\n  }\n\n  summary {\n    display: list-item;\n  }\n\n  sup {\n    top: -0.5em;\n  }\n\n  template {\n    display: none;\n  }\n\n  textarea {\n    overflow: auto;\n  }\n\n  [hidden] {\n    display: none;\n  }\n\n  [type=\"checkbox\"],\n  [type=\"radio\"] {\n    box-sizing: border-box;\n    padding: 0;\n  }\n\n  ::-webkit-file-upload-button {\n    -webkit-appearance: button;\n    font: inherit;\n  }\n\n  @media (prefers-reduced-motion: reduce) {\n    *, body {\n      scroll-behavior: auto;\n\n      ", "\n    }\n  }\n"])), function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.globalStyle.bold.fontWeight;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.globalStyle.strong.fontWeight;
}, htmlStyles, function (_ref11) {
  var theme = _ref11.theme;
  return theme.component.globalStyle.body.marginBottom;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.globalStyle.body.marginLeft;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.globalStyle.body.marginRight;
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.component.globalStyle.body.marginTop;
}, bodyStyles, function (_ref15) {
  var theme = _ref15.theme;
  return theme.component.globalStyle.bodyBackdrop.backgroundColor;
}, reducedMotionStyles);

var GlobalStyle = function GlobalStyle(_ref) {
  var children = _ref.children;
  return React.createElement(Fragment, null, React.createElement(GlobalStyleRoot, null), children);
};

var _templateObject$A, _templateObject2$i, _templateObject3$d, _templateObject4$a, _templateObject5$5, _templateObject6$4;
var PageHeaderRoot = /*#__PURE__*/styled.header(function () {
  return css(_templateObject$A || (_templateObject$A = _taggedTemplateLiteralLoose(["\n    align-items: flex-start;\n    box-sizing: border-box;\n    display: flex;\n    flex-direction: column;\n    justify-content: space-between;\n    width: 100%;\n  "])));
});
var PageHeaderHeading = /*#__PURE__*/styled(Heading)(function (_ref) {
  var pageHeader = _ref.theme.component.pageHeader;
  return css(_templateObject2$i || (_templateObject2$i = _taggedTemplateLiteralLoose(["\n    margin-bottom: ", ";\n    margin-top: ", ";\n  "])), pageHeader.heading.marginBottom, pageHeader.heading.marginTop);
});
var PageHeaderText = /*#__PURE__*/styled(Text)(function (_ref2) {
  var pageHeader = _ref2.theme.component.pageHeader;
  return css(_templateObject3$d || (_templateObject3$d = _taggedTemplateLiteralLoose(["\n    margin-bottom: ", ";\n    margin-top: ", ";\n  "])), pageHeader.text.marginBottom, pageHeader.text.marginTop);
});
var StSuffixTextContainer = /*#__PURE__*/styled.div(_templateObject4$a || (_templateObject4$a = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: flex-end;\n  justify-content: space-between;\n  width: 100%;\n"])));
var StSuffixContainer = /*#__PURE__*/styled.div(_templateObject5$5 || (_templateObject5$5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref3) {
  var pageHeader = _ref3.theme.component.pageHeader;
  return css(_templateObject6$4 || (_templateObject6$4 = _taggedTemplateLiteralLoose(["\n    margin-bottom: ", ";\n    margin-top: ", ";\n  "])), pageHeader.text.marginBottom, pageHeader.text.marginTop);
});

var _excluded$F = ["children", "suffixText", "className", "component", "endAdornment", "heading", "startAdornment"];
var PageHeader = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$suffixText = props.suffixText,
      suffixText = _props$suffixText === void 0 ? null : _props$suffixText,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'header' : _props$component,
      endAdornment = props.endAdornment,
      heading = props.heading,
      startAdornment = props.startAdornment,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$F);

  var Text = function Text(data) {
    return React.createElement(PageHeaderText, {
      component: "p",
      variant: "body1",
      weight: "regular"
    }, data);
  };

  return React.createElement(PageHeaderRoot, Object.assign({
    as: component,
    className: className,
    ref: ref
  }, otherProps), React.createElement(Header, {
    component: 'div',
    disableGutters: true,
    endAdornment: endAdornment,
    startAdornment: startAdornment
  }), React.createElement(PageHeaderHeading, {
    component: "h1",
    variant: "heading3",
    weight: "bold"
  }, heading), suffixText ? React.createElement(StSuffixTextContainer, {
    "data-testid": "suffix-text-container"
  }, Text(children), React.createElement(StSuffixContainer, null, suffixText)) : Text(children));
});
PageHeader.displayName = 'PageHeader';

var _templateObject$B, _templateObject2$j;
var listRootStyles = /*#__PURE__*/css(_templateObject$B || (_templateObject$B = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  list-style: none;\n  margin-block-end: ", ";\n  margin-block-start: ", ";\n  margin-bottom: ", ";\n  margin-left: ", ";\n  margin-inline-end: ", ";\n  margin-inline-start: ", ";\n  margin-right: ", ";\n  margin-top: ", ";\n  padding-bottom: ", ";\n  padding-inline-start: ", ";\n  padding-left: ", ";\n  padding-right: ", ";\n  padding-top: ", ";\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.list.root.marginBlockEnd;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.list.root.marginBlockStart;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.list.root.marginBottom;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.list.root.marginLeft;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.list.root.marginInlineEnd;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.list.root.marginInlineStart;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.list.root.marginRight;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.list.root.marginTop;
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.list.root.paddingBottom;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.list.root.paddingInlineStart;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.component.list.root.paddingLeft;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.list.root.paddingRight;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.list.root.paddingTop;
});
var ListRoot = /*#__PURE__*/styled.ul(_templateObject2$j || (_templateObject2$j = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n\n  ", "\n"])), listRootStyles);

var _excluded$G = ["children", "component"];
var List = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'ul' : _props$component,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$G);

  return React.createElement(ListRoot, Object.assign({
    as: component,
    ref: ref
  }, otherProps), children);
});
List.displayName = 'List';

var _templateObject$C, _templateObject2$k;
var listItemStyles = /*#__PURE__*/css(_templateObject$C || (_templateObject$C = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  box-sizing: border-box;\n  display: flex;\n  justify-content: space-between;\n  position: relative;\n  text-align: left;\n  text-decoration: none;\n  width: 100%;\n\n  &:last-child {\n    border-bottom-style: none;\n    border-bottom-width: ", ";\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.listItem.last.borderBottomWidth;
});
var ListItemRoot = /*#__PURE__*/styled.li(function (_ref2) {
  var compact = _ref2.compact,
      listItem = _ref2.theme.component.listItem;
  return css(_templateObject2$k || (_templateObject2$k = _taggedTemplateLiteralLoose(["\n    border-bottom-color: ", ";\n    border-bottom-style: solid;\n    border-bottom-width: ", ";\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n\n    ", "\n  "])), listItem.root.borderBottomColor, listItem.root.borderBottomWidth, compact ? listItem.compact.paddingBottom : listItem.expanded.paddingBottom, listItem.root.paddingLeft, listItem.root.paddingRight, compact ? listItem.compact.paddingTop : listItem.expanded.paddingTop, listItemStyles);
});

var _excluded$H = ["children", "compact", "component", "role"];
var ListItem = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      compact = props.compact,
      _props$component = props.component,
      component = _props$component === void 0 ? 'li' : _props$component,
      _props$role = props.role,
      role = _props$role === void 0 ? 'listitem' : _props$role,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$H);

  return React.createElement(ListItemRoot, Object.assign({
    compact: compact,
    component: component,
    role: role
  }, otherProps, {
    ref: ref
  }), children);
});
ListItem.displayName = 'ListItem';

var _templateObject$D;
var ListItemContentRoot = /*#__PURE__*/styled.dl(_templateObject$D || (_templateObject$D = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  box-sizing: border-box;\n  display: block;\n  margin-block-end: 0;\n  margin-block-start: 0;\n  margin-inline-end: 0;\n  margin-inline-start: 0;\n"])));

var _excluded$I = ["children", "component", "label"];
var ListItemContent = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'dl' : _props$component,
      label = props.label,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$I);

  return React.createElement(ListItemContentRoot, Object.assign({
    as: component
  }, otherProps, {
    ref: ref
  }), label && React.createElement(Text, {
    component: 'dt',
    variant: 'body2'
  }, label), children && React.createElement(Text, {
    component: 'dd',
    variant: 'body1'
  }, children));
});
ListItemContent.displayName = 'ListItemContent';

var _excluded$J = ["aria-label", "children", "onClick", "size"];
var MenuButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaLabel = props['aria-label'],
      _props$children = props.children,
      children = _props$children === void 0 ? React.createElement(Icons.MenuOutline, {
    size: 'md'
  }) : _props$children,
      onClick = props.onClick,
      _props$size = props.size,
      size = _props$size === void 0 ? 'sm' : _props$size,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$J);

  return React.createElement(IconButton, Object.assign({
    "aria-label": ariaLabel,
    onClick: onClick,
    size: size
  }, otherProps, {
    ref: ref
  }), children);
});
MenuButton.displayName = 'MenuButton';

var _templateObject$E;
var MenuItemIconRoot = /*#__PURE__*/styled.div(_templateObject$E || (_templateObject$E = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-flex;\n  flex-shrink: 0;\n  height: ", ";\n  margin-right: ", ";\n  min-height: ", ";\n  min-width: ", ";\n  width: ", ";\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.menuItemIcon.root.height;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.menuItemIcon.root.marginRight;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.menuItemIcon.root.minHeight;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.menuItemIcon.root.minWidth;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.menuItemIcon.root.width;
});

var _excluded$K = ["component"];
var MenuItemIcon = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$K);

  return React.createElement(MenuItemIconRoot, Object.assign({
    as: component
  }, otherProps, {
    ref: ref
  }));
});
MenuItemIcon.displayName = 'MenuItemIcon';

var _templateObject$F;
var MenuItemTextRoot = /*#__PURE__*/styled(Text)(_templateObject$F || (_templateObject$F = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: inherit;\n  display: block;\n  flex: 1 1 auto;\n  margin-bottom: ", ";\n  margin-left: ", ";\n  margin-right: ", ";\n  margin-top: ", ";\n  min-width: ", ";\n  will-change: color, font-weight;\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-delay: ", ";\n    transition-duration: ", ";\n    transition-property: color, font-weight;\n    transition-timing-function: ", ";\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.menuItemText.root.marginBottom;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.menuItemText.root.marginLeft;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.menuItemText.root.marginRight;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.menuItemText.root.marginTop;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.menuItemText.root.minWidth;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.menuItemText.root.transitionDelay;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.menuItemText.root.transitionDuration;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.menuItemText.root.transitionTimingFunction;
});

var _excluded$L = ["component", "variant", "weight"];
var MenuItemText = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'body2' : _props$variant,
      _props$weight = props.weight,
      weight = _props$weight === void 0 ? 'inherit' : _props$weight,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$L);

  return React.createElement(MenuItemTextRoot, Object.assign({
    component: component,
    variant: variant,
    weight: weight
  }, otherProps, {
    ref: ref
  }));
});
MenuItemText.displayName = 'MenuItemText';

var _templateObject$G, _templateObject2$l, _templateObject3$e;
var menuItemStyles = /*#__PURE__*/css(_templateObject$G || (_templateObject$G = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  background-clip: padding-box;\n  box-sizing: border-box;\n  flex-flow: row wrap;\n  justify-content: flex-start;\n  margin-bottom: ", ";\n  margin-top: ", ";\n  min-height: ", ";\n  white-space: nowrap;\n  will-change: background-color, color, font-weight, opacity;\n\n  @media (prefers-reduced-motion: no-preference) {\n    &,\n    &:before {\n      transition-delay: ", ";\n      transition-duration: ", ";\n      transition-timing-function: ", ";\n    }\n\n    transition-property: background-color, color, font-weight, opacity;\n\n    &:before {\n      transition-property: opacity;\n    }\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.menuItem.root.marginBottom;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.menuItem.root.marginTop;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.menuItem.root.minHeight;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.menuItem.root.transitionDelay;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.menuItem.root.transitionDuration;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.menuItem.root.transitionTimingFunction;
});
var focusedMenuItemStyles = /*#__PURE__*/css(_templateObject2$l || (_templateObject2$l = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  background-color: ", ";\n  color: ", ";\n  opacity: ", ";\n"])), function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.menuItem.focused.backgroundColor;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.menuItem.focused.color;
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.menuItem.focused.opacity;
});
var MenuItemRoot = /*#__PURE__*/styled(ButtonBase)(_templateObject3$e || (_templateObject3$e = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  background-color: ", ";\n  color: ", ";\n  cursor: pointer;\n  font-weight: ", ";\n  padding-bottom: ", ";\n  padding-left: ", ";\n  padding-right: ", ";\n  padding-top: ", ";\n\n  ", "\n  ", "\n\n  &:before {\n    --component-menu-item-before-height: ", ";\n    background-color: ", ";\n    border-bottom-left-radius: ", ";\n    border-bottom-right-radius: ", ";\n    border-top-left-radius: ", ";\n    border-top-right-radius: ", ";\n    content: '';\n    height: var(--component-menu-item-before-height);\n    opacity: ", ";\n    position: absolute;\n    top: calc(50% - (var(--component-menu-item-before-height) / 2));\n    width: ", ";\n    will-change: opacity;\n  }\n\n  &:focus-visible {\n    ", "\n  }\n\n  &:focus-visible:hover {\n    background-color: ", ";\n  }\n\n  &:hover {\n    background-color: ", ";\n    text-decoration: none;\n\n    @media (hover: none) {\n      background-color: ", ";\n    }\n  }\n"])), function (_ref10) {
  var selected = _ref10.selected,
      theme = _ref10.theme;
  return selected ? theme.component.menuItem.selected.backgroundColor : theme.component.menuItem.root.backgroundColor;
}, function (_ref11) {
  var selected = _ref11.selected,
      theme = _ref11.theme;
  return selected ? theme.component.menuItem.selected.color : theme.component.menuItem.root.color;
}, function (_ref12) {
  var selected = _ref12.selected,
      theme = _ref12.theme;
  return selected ? theme.component.menuItem.selected.fontWeight : theme.component.menuItem.root.fontWeight;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.menuItem.root.paddingBottom;
}, function (_ref14) {
  var disableGutters = _ref14.disableGutters,
      theme = _ref14.theme;
  return disableGutters ? theme.component.menuItem.root.paddingLeft : theme.component.menuItem.gutters.paddingLeft;
}, function (_ref15) {
  var disableGutters = _ref15.disableGutters,
      theme = _ref15.theme;
  return disableGutters ? theme.component.menuItem.root.paddingRight : theme.component.menuItem.gutters.paddingRight;
}, function (_ref16) {
  var theme = _ref16.theme;
  return theme.component.menuItem.root.paddingTop;
}, listItemStyles, menuItemStyles, function (_ref17) {
  var theme = _ref17.theme;
  return theme.component.menuItem.before.height;
}, function (_ref18) {
  var theme = _ref18.theme;
  return theme.component.menuItem.before.backgroundColor;
}, function (_ref19) {
  var theme = _ref19.theme;
  return theme.component.menuItem.root.borderBottomLeftRadius;
}, function (_ref20) {
  var theme = _ref20.theme;
  return theme.component.menuItem.root.borderBottomRightRadius;
}, function (_ref21) {
  var theme = _ref21.theme;
  return theme.component.menuItem.root.borderTopLeftRadius;
}, function (_ref22) {
  var theme = _ref22.theme;
  return theme.component.menuItem.root.borderTopRightRadius;
}, function (_ref23) {
  var selected = _ref23.selected,
      theme = _ref23.theme;
  return selected ? theme.component.menuItem.selectedBefore.opacity : theme.component.menuItem.before.opacity;
}, function (_ref24) {
  var theme = _ref24.theme;
  return theme.component.menuItem.before.width;
}, focusedMenuItemStyles, function (_ref25) {
  var theme = _ref25.theme;
  return theme.component.menuItem.focused.backgroundColor;
}, function (_ref26) {
  var selected = _ref26.selected,
      theme = _ref26.theme;
  return selected ? theme.component.menuItem.selectedHover.backgroundColor : theme.component.menuItem.hover.backgroundColor;
}, function (_ref27) {
  var selected = _ref27.selected,
      theme = _ref27.theme;
  return selected ? theme.component.menuItem.selectedHoverNone.backgroundColor : theme.component.menuItem.root.backgroundColor;
});

var _excluded$M = ["children", "component", "disableGutters", "renderIcon", "role", "selected", "tabIndex"];
var MenuItem = function MenuItem(props) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'li' : _props$component,
      _props$disableGutters = props.disableGutters,
      disableGutters = _props$disableGutters === void 0 ? false : _props$disableGutters,
      renderIcon = props.renderIcon,
      _props$role = props.role,
      role = _props$role === void 0 ? 'menuitem' : _props$role,
      _props$selected = props.selected,
      selected = _props$selected === void 0 ? false : _props$selected,
      tabIndexProp = props.tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$M);

  var tabIndex = useMemo(function () {
    if (typeof tabIndexProp === 'undefined') {
      return -1;
    }

    return tabIndexProp;
  }, [tabIndexProp]);
  return React.createElement(MenuItemRoot, Object.assign({
    "aria-current": selected,
    component: component,
    disableGutters: disableGutters,
    role: role,
    selected: selected,
    tabIndex: tabIndex
  }, otherProps), renderIcon && React.createElement(MenuItemIcon, null, renderIcon == null ? void 0 : renderIcon()), React.createElement(MenuItemText, null, children));
};

var _templateObject$H, _templateObject2$m;
var menuListRootStyles = /*#__PURE__*/css(_templateObject$H || (_templateObject$H = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-radius: ", ";\n  border-top-width: ", ";\n  box-shadow: ", ";\n\n  ", "\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.menuList.root.borderRadius;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.menuList.root.borderTopWidth;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.menuList.root.boxShadow;
}, surfaceRootStyles);
var MenuListRoot = /*#__PURE__*/styled(List)(_templateObject2$m || (_templateObject2$m = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), menuListRootStyles);

var _excluded$N = ["children", "component", "role", "tabIndex"];
var MenuList = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'ul' : _props$component,
      _props$role = props.role,
      role = _props$role === void 0 ? 'menu' : _props$role,
      _props$tabIndex = props.tabIndex,
      tabIndex = _props$tabIndex === void 0 ? -1 : _props$tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$N);

  var items = Children.map(children, function (child, index) {
    if (typeof child === 'undefined' || child === null) {
      throw new Error("The child at index " + index + " in `children` is undefined or null.");
    }

    if (typeof child === 'boolean' || typeof child === 'number' || typeof child === 'string') {
      throw new Error("The child at index " + index + " in `children` is not a valid MenuItem element.");
    }

    if (!('props' in child)) {
      throw new Error("The child at index " + index + " in `children` is not a valid MenuItem element.");
    }

    var newChildProps = {
      key: index,
      onKeyPress: child.props.onKeyPress === 'undefined' ? child.props.onClick : function (event) {
        if (event.code === 'Enter') {
          child.props.onClick(event);
        }
      },
      role: 'menuitem',
      tabIndex: child.props.tabIndex === undefined ? 0 : child.props.tabIndex
    };
    return React.cloneElement(child, newChildProps);
  });
  return React.createElement(MenuListRoot, Object.assign({
    component: component,
    role: role,
    tabIndex: tabIndex
  }, otherProps, {
    ref: ref
  }), items);
});
MenuList.displayName = 'MenuList';

var _templateObject$I, _templateObject2$n, _templateObject3$f;
/**
 * @todo add z-index
 */

var fixedStyles = /*#__PURE__*/css(_templateObject$I || (_templateObject$I = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  left: auto;\n  position: fixed;\n  right: 0;\n  top: 0;\n\n  @media print {\n    position: absolute;\n  }\n"])));
/**
 * @todo add support for color tokens, for background/foreground
 */

var NavBarRoot = /*#__PURE__*/styled(Surface)(function (_ref) {
  var position = _ref.position,
      prominent = _ref.prominent,
      surfaceColor = _ref.surfaceColor,
      navBar = _ref.theme.component.navBar;
  return css(_templateObject2$n || (_templateObject2$n = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    border-bottom-color: ", ";\n    border-bottom-style: solid;\n    border-bottom-width: ", ";\n    box-sizing: border-box;\n    display: flex;\n    flex-direction: column;\n    flex-shrink: 0;\n    font-family: ", ";\n    justify-content: center;\n    margin: ", ";\n    min-height: ", ";\n    width: 100%;\n    z-index: ", ";\n\n    ", "\n  "])), surfaceColor === 'transparent' ? surfaceColor : navBar.root.borderBottomColor, navBar.root.borderBottomWidth, navBar.root.fontFamily, navBar.root.margin, prominent ? navBar.prominent.minHeight : navBar.root.minHeight, position === 'fixed' ? navBar.positionFixed.zIndex : navBar.root.zIndex, position === 'fixed' && fixedStyles);
});
var NavBarContainer = /*#__PURE__*/styled(Container)(function (_ref2) {
  var maxWidth = _ref2.maxWidth,
      prominent = _ref2.prominent,
      navBar = _ref2.theme.component.navBar;
  return css(_templateObject3$f || (_templateObject3$f = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    justify-content: space-between;\n    min-height: ", ";\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n    width: 100%;\n  "])), navBar.container.minHeight, prominent ? navBar.prominentContainer.paddingBottom : navBar.container.paddingBottom, maxWidth === 'sidebar-layout' && navBar.maxWidthSidebarLayoutContainer.paddingLeft, maxWidth === 'sidebar-layout' && navBar.maxWidthSidebarLayoutContainer.paddingRight, prominent ? navBar.prominentContainer.paddingTop : navBar.container.paddingTop);
});

var navBarPositions = ['fixed', 'static'];

var _excluded$O = ["children", "className", "component", "maxWidth", "position", "prominent", "role", "shapeStyle", "shadow"];
var defaultProps$7 = {
  position: 'static',
  prominent: false
};
var NavBar = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'header' : _props$component,
      _props$maxWidth = props.maxWidth,
      maxWidth = _props$maxWidth === void 0 ? 'xl' : _props$maxWidth,
      _props$position = props.position,
      positionProp = _props$position === void 0 ? defaultProps$7.position : _props$position,
      _props$prominent = props.prominent,
      prominent = _props$prominent === void 0 ? defaultProps$7.prominent : _props$prominent,
      _props$role = props.role,
      role = _props$role === void 0 ? 'banner' : _props$role,
      _props$shapeStyle = props.shapeStyle,
      shapeStyle = _props$shapeStyle === void 0 ? 'none' : _props$shapeStyle,
      _props$shadow = props.shadow,
      shadow = _props$shadow === void 0 ? false : _props$shadow,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$O);

  var position = useMemo(function () {
    if (positionProp === 'undefined') {
      console.warn("NavBar: received illegal value for 'position' prop.\n        These are the valid values: " + navBarPositions.join(', '));
      return defaultProps$7.position;
    }

    return positionProp;
  }, [positionProp]);
  return React.createElement(NavBarRoot, Object.assign({
    className: className,
    component: component,
    position: position,
    prominent: prominent,
    role: role,
    shadow: shadow,
    shapeStyle: shapeStyle
  }, otherProps, {
    ref: ref
  }), React.createElement(NavBarContainer, {
    flexDirection: 'row',
    maxWidth: maxWidth,
    prominent: prominent,
    variant: 'loose'
  }, children));
});
NavBar.displayName = 'NavBar';

var _excluded$P = ["disabled", "showPassword", "toggleButtonAriaLabel"];
var EyeClosedOutline = Icons.EyeClosedOutline,
    EyeOpenOutline = Icons.EyeOpenOutline;

var renderPasswordIcon = function renderPasswordIcon(_ref) {
  var disabled = _ref.disabled,
      onIconClick = _ref.onIconClick,
      showPassword = _ref.showPassword,
      toggleButtonAriaLabel = _ref.toggleButtonAriaLabel;

  var renderIconMarkup = function renderIconMarkup(_ref2) {
    var error = _ref2.error;
    var iconColor = error ? 'colorRoleOnCriticalSecondary' : 'colorRoleOnBackground';
    return React.createElement(InputAdornment, {
      position: 'end'
    }, React.createElement(IconButton, {
      "aria-label": toggleButtonAriaLabel,
      "data-testid": "toggle-show-password",
      disabled: disabled,
      onClick: onIconClick,
      size: "sm",
      type: "button",
      variant: "tertiary"
    }, showPassword ? React.createElement(EyeClosedOutline, {
      iconColor: iconColor
    }) : React.createElement(EyeOpenOutline, {
      iconColor: iconColor
    })));
  };

  return renderIconMarkup;
};

var PasswordInput = /*#__PURE__*/forwardRef(function (props, ref) {
  var disabled = props.disabled,
      _props$showPassword = props.showPassword,
      showPasswordProp = _props$showPassword === void 0 ? false : _props$showPassword,
      toggleButtonAriaLabel = props.toggleButtonAriaLabel,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$P);

  var _useState = useState(showPasswordProp),
      showPassword = _useState[0],
      setShowPassword = _useState[1];

  var inputType = useMemo(function () {
    if (!disabled && showPassword) {
      return 'text';
    }

    return 'password';
  }, [disabled, showPassword]);
  var toggleShowPassword = useCallback(function () {
    setShowPassword(!showPassword);
  }, [showPassword]);
  return React.createElement(TextField, Object.assign({
    renderEndAdornment: renderPasswordIcon({
      disabled: disabled,
      onIconClick: toggleShowPassword,
      showPassword: showPassword,
      toggleButtonAriaLabel: toggleButtonAriaLabel
    }),
    disabled: disabled,
    type: inputType
  }, otherProps, {
    ref: ref
  }));
});
PasswordInput.displayName = 'PasswordInput';

var _templateObject$J, _templateObject2$o;
var PaginationItemRoot = /*#__PURE__*/styled(IconButton)(function (_ref) {
  var disabled = _ref.disabled,
      paginationItemType = _ref.paginationItemType,
      selected = _ref.selected,
      pagination = _ref.theme.component.pagination;
  return css(_templateObject$J || (_templateObject$J = _taggedTemplateLiteralLoose(["\n    color: ", ";\n    font-weight: ", ";\n  "])), disabled ? pagination.disabledItem.color : pagination.item.color, paginationItemType === 'page' && selected ? 'bold' : 'normal');
});
var PaginationItemEllipsis = /*#__PURE__*/styled.div(function (_ref2) {
  var iconButton = _ref2.theme.component.iconButton;
  return css(_templateObject2$o || (_templateObject2$o = _taggedTemplateLiteralLoose(["\n    ", "\n    ", "\n\n    min-width: ", ";\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n  "])), buttonBaseRootStyles, iconButtonRootStyles, iconButton.sm.minWidth, iconButton.sm.paddingBottom, iconButton.sm.paddingLeft, iconButton.sm.paddingRight, iconButton.sm.paddingTop);
});

var usePaginationItem = function usePaginationItem(_ref) {
  var ariaCurrentProp = _ref['aria-current'],
      childrenProp = _ref.children,
      componentProp = _ref.component,
      dataTestidProp = _ref['data-testid'],
      itemPropProp = _ref.itemProp,
      paginationItemType = _ref.paginationItemType,
      pageNumber = _ref.pageNumber,
      relProp = _ref.rel,
      selected = _ref.selected;
  var children = useMemo(function () {
    if (paginationItemType === 'start-ellipsis' || paginationItemType === 'end-ellipsis') {
      return '···';
    }

    if (paginationItemType === 'previous') {
      return React.createElement(Icons.ChevronLeftOutline, null);
    }

    if (paginationItemType === 'next') {
      return React.createElement(Icons.ChevronRightOutline, null);
    }

    if (paginationItemType === 'page') {
      return pageNumber;
    }

    return childrenProp;
  }, [childrenProp, pageNumber, paginationItemType]);
  var ariaCurrent = useMemo(function () {
    if (ariaCurrentProp) {
      return ariaCurrentProp;
    }

    if (selected) {
      return 'page';
    }

    return undefined;
  }, [ariaCurrentProp, selected]);
  var component = useMemo(function () {
    if (componentProp) {
      return componentProp;
    }

    if (paginationItemType === 'start-ellipsis' || paginationItemType === 'end-ellipsis') {
      return 'span';
    }

    return undefined;
  }, [componentProp, paginationItemType]);
  var dataTestId = useMemo(function () {
    if (dataTestidProp) {
      return dataTestidProp;
    }

    if (paginationItemType === 'first') {
      return "first-page-button";
    }

    if (paginationItemType === 'previous') {
      return "previous-page-button";
    }

    if (paginationItemType === 'page') {
      return "page-" + pageNumber + "-button";
    }

    if (paginationItemType === 'next') {
      return "next-page-button";
    }

    if (paginationItemType === 'last') {
      return "last-page-button";
    }

    return;
  }, [dataTestidProp, pageNumber, paginationItemType]);
  var itemProp = useMemo(function () {
    if (itemPropProp) {
      return itemPropProp;
    }

    if (paginationItemType === 'previous' || paginationItemType === 'next') {
      return 'relatedLink/pagination';
    }

    return undefined;
  }, [itemPropProp, paginationItemType]);
  var rel = useMemo(function () {
    if (relProp) {
      return relProp;
    }

    if (paginationItemType === 'previous') {
      return 'prev';
    }

    if (paginationItemType === 'next') {
      return 'next';
    }

    return undefined;
  }, [paginationItemType, relProp]);
  return {
    'aria-current': ariaCurrent,
    children: children,
    component: component,
    'data-testid': dataTestId,
    itemProp: itemProp,
    rel: rel
  };
};

var _excluded$Q = ["allowDisabledStyling", "aria-current", "className", "children", "component", "data-testid", "itemProp", "disabled", "loading", "onClick", "pageNumber", "paginationItemType", "rel", "selected", "size", "variant"];
var PaginationItem = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$allowDisabledS = props.allowDisabledStyling,
      allowDisabledStyling = _props$allowDisabledS === void 0 ? false : _props$allowDisabledS,
      ariaCurrentProp = props['aria-current'],
      className = props.className,
      _props$children = props.children,
      childrenProp = _props$children === void 0 ? null : _props$children,
      componentProp = props.component,
      dataTestIdProp = props['data-testid'],
      itemPropProp = props.itemProp,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$loading = props.loading,
      loading = _props$loading === void 0 ? false : _props$loading,
      onClick = props.onClick,
      pageNumber = props.pageNumber,
      _props$paginationItem = props.paginationItemType,
      paginationItemType = _props$paginationItem === void 0 ? 'page' : _props$paginationItem,
      relProp = props.rel,
      selected = props.selected,
      _props$size = props.size,
      size = _props$size === void 0 ? 'sm' : _props$size,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'tertiary' : _props$variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$Q);

  var _usePaginationItem = usePaginationItem({
    'aria-current': ariaCurrentProp,
    children: childrenProp,
    component: componentProp,
    'data-testid': dataTestIdProp,
    itemProp: itemPropProp,
    pageNumber: pageNumber,
    paginationItemType: paginationItemType,
    rel: relProp
  }),
      ariaCurrent = _usePaginationItem['aria-current'],
      children = _usePaginationItem.children,
      component = _usePaginationItem.component,
      dataTestId = _usePaginationItem['data-testid'],
      itemProp = _usePaginationItem.itemProp,
      rel = _usePaginationItem.rel;

  if (paginationItemType === 'start-ellipsis' || paginationItemType === 'end-ellipsis') {
    return React.createElement(PaginationItemEllipsis, null, React.createElement(ButtonContent, null, children));
  }

  return React.createElement(PaginationItemRoot, Object.assign({
    allowDisabledStyling: allowDisabledStyling,
    "aria-current": ariaCurrent,
    className: className,
    component: component,
    "data-testid": dataTestId,
    disabled: disabled,
    itemProp: itemProp,
    loading: loading,
    onClick: onClick,
    paginationItemType: paginationItemType,
    rel: rel,
    selected: selected,
    size: size,
    variant: variant
  }, otherProps, {
    ref: ref
  }), children);
});
PaginationItem.displayName = 'PaginationItem';

var _templateObject$K, _templateObject2$p;
var PaginationRoot = /*#__PURE__*/styled.nav(function (_ref) {
  var pagination = _ref.theme.component.pagination;
  return css(_templateObject$K || (_templateObject$K = _taggedTemplateLiteralLoose(["\n    font-family: ", ";\n    min-height: ", ";\n    min-width: ", ";\n  "])), pagination.root.fontFamily, pagination.root.minHeight, pagination.root.minWidth);
});
var PaginationList = /*#__PURE__*/styled.ul(function (_ref2) {
  var pagination = _ref2.theme.component.pagination;
  return css(_templateObject2$p || (_templateObject2$p = _taggedTemplateLiteralLoose(["\n    align-items: center;\n    column-gap: ", ";\n    display: flex;\n    flex-wrap: wrap;\n    list-style: none;\n    margin: ", ";\n    padding: ", ";\n  "])), pagination.list.columnGap, pagination.list.margin, pagination.list.padding);
});

var defaultProps$8 = {
  boundaryCount: 1,
  defaultPage: 1,
  disabled: false,
  hideNextButton: false,
  hidePrevButton: false,
  showFirstButton: false,
  showLastButton: false,
  siblingCount: 1,
  totalPages: 1
};

var _excluded$R = ["boundaryCount", "componentName", "currentPage", "defaultPage", "disabled", "hideNextButton", "hidePrevButton", "onChange", "showFirstButton", "showLastButton", "siblingCount", "totalPages"];
var usePagination = function usePagination(props) {
  if (props === void 0) {
    props = {};
  }

  var _props = props,
      _props$boundaryCount = _props.boundaryCount,
      boundaryCount = _props$boundaryCount === void 0 ? defaultProps$8.boundaryCount : _props$boundaryCount,
      _props$componentName = _props.componentName,
      componentName = _props$componentName === void 0 ? 'usePagination' : _props$componentName,
      pageProp = _props.currentPage,
      _props$defaultPage = _props.defaultPage,
      defaultPage = _props$defaultPage === void 0 ? defaultProps$8.defaultPage : _props$defaultPage,
      _props$disabled = _props.disabled,
      disabled = _props$disabled === void 0 ? defaultProps$8.disabled : _props$disabled,
      _props$hideNextButton = _props.hideNextButton,
      hideNextButton = _props$hideNextButton === void 0 ? defaultProps$8.hideNextButton : _props$hideNextButton,
      _props$hidePrevButton = _props.hidePrevButton,
      hidePrevButton = _props$hidePrevButton === void 0 ? defaultProps$8.hidePrevButton : _props$hidePrevButton,
      handleChange = _props.onChange,
      _props$showFirstButto = _props.showFirstButton,
      showFirstButton = _props$showFirstButto === void 0 ? defaultProps$8.showFirstButton : _props$showFirstButto,
      _props$showLastButton = _props.showLastButton,
      showLastButton = _props$showLastButton === void 0 ? defaultProps$8.showLastButton : _props$showLastButton,
      _props$siblingCount = _props.siblingCount,
      siblingCount = _props$siblingCount === void 0 ? defaultProps$8.siblingCount : _props$siblingCount,
      _props$totalPages = _props.totalPages,
      totalPages = _props$totalPages === void 0 ? defaultProps$8.totalPages : _props$totalPages,
      other = _objectWithoutPropertiesLoose(_props, _excluded$R);

  var _useControlled = useControlled({
    controlledValue: pageProp,
    "default": defaultPage,
    name: componentName,
    state: 'page'
  }),
      pageValue = _useControlled.value,
      setPageState = _useControlled.setValue;

  var page = pageValue;

  var handleClick = function handleClick(event, value) {
    if (!pageProp) {
      setPageState(value);
    }

    if (handleChange) {
      handleChange(event, value);
    }
  };

  var range = function range(start, end) {
    var length = end - start + 1;
    return Array.from({
      length: length
    }, function (_, i) {
      return start + i;
    });
  };

  var startPages = range(1, Math.min(boundaryCount, totalPages));
  var endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);
  var siblingsStart = Math.max(Math.min( // Natural start
  page - siblingCount, // Lower boundary when page is high
  totalPages - boundaryCount - siblingCount * 2 - 1), // Greater than startPages
  boundaryCount + 2);
  var siblingsEnd = Math.min(Math.max( // Natural end
  page + siblingCount, // Upper boundary when page is low
  boundaryCount + siblingCount * 2 + 2), // Less than endPages
  endPages.length > 0 ? endPages[0] - 2 : totalPages - 1); // Basic list of items to render
  // e.g. itemList = ['first', 'previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next', 'last']

  var itemList = [].concat(showFirstButton ? ['first'] : [], hidePrevButton ? [] : ['previous'], startPages, siblingsStart > boundaryCount + 2 ? ['start-ellipsis'] : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : [], range(siblingsStart, siblingsEnd), siblingsEnd < totalPages - boundaryCount - 1 ? ['end-ellipsis'] : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : [], endPages, hideNextButton ? [] : ['next'], showLastButton ? ['last'] : []); // Map the button type to its page number

  var buttonPage = function buttonPage(type) {
    switch (type) {
      case 'first':
        return 1;

      case 'previous':
        return page - 1;

      case 'next':
        return page + 1;

      case 'last':
        return totalPages;

      default:
        return 0;
    }
  }; // Convert the basic item list to PaginationItem props objects


  var items = itemList.map(function (item) {
    return typeof item === 'number' ? {
      onClick: function onClick(event) {
        handleClick(event, item);
      },
      paginationItemType: 'page',
      pageNumber: item,
      selected: item === page,
      disabled: disabled,
      'aria-current': item === page ? 'true' : undefined
    } : {
      onClick: function onClick(event) {
        handleClick(event, buttonPage(item));
      },
      paginationItemType: item,
      pageNumber: buttonPage(item),
      selected: false,
      disabled: disabled || item.indexOf('ellipsis') === -1 && (item === 'next' || item === 'last' ? page >= totalPages : page <= 1)
    };
  });
  return _extends({
    items: items
  }, other);
};

var _excluded$S = ["aria-label", "boundaryCount", "className", "component", "currentPage", "defaultPage", "disabled", "getItemAriaLabel", "hideNextButton", "hidePrevButton", "onChange", "renderItem", "siblingCount", "totalPages"];

var defaultGetAriaLabel = function defaultGetAriaLabel(_ref) {
  var pageNumber = _ref.pageNumber,
      paginationItemType = _ref.paginationItemType,
      selected = _ref.selected;

  if (paginationItemType === 'page') {
    var prefix = selected ? 'Current' : 'Go to';
    return prefix + " page " + pageNumber;
  }

  return "Go to " + paginationItemType + " page";
};

var Pagination = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$ariaLabel = props['aria-label'],
      ariaLabel = _props$ariaLabel === void 0 ? 'pagination navigation' : _props$ariaLabel,
      _props$boundaryCount = props.boundaryCount,
      boundaryCount = _props$boundaryCount === void 0 ? defaultProps$8.boundaryCount : _props$boundaryCount,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'nav' : _props$component,
      currentPage = props.currentPage,
      _props$defaultPage = props.defaultPage,
      defaultPage = _props$defaultPage === void 0 ? defaultProps$8.defaultPage : _props$defaultPage,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? defaultProps$8.disabled : _props$disabled,
      _props$getItemAriaLab = props.getItemAriaLabel,
      getItemAriaLabel = _props$getItemAriaLab === void 0 ? defaultGetAriaLabel : _props$getItemAriaLab,
      _props$hideNextButton = props.hideNextButton,
      hideNextButton = _props$hideNextButton === void 0 ? defaultProps$8.hideNextButton : _props$hideNextButton,
      _props$hidePrevButton = props.hidePrevButton,
      hidePrevButton = _props$hidePrevButton === void 0 ? defaultProps$8.hidePrevButton : _props$hidePrevButton,
      onChange = props.onChange,
      _props$renderItem = props.renderItem,
      renderItem = _props$renderItem === void 0 ? function (paginationItemProps) {
    return React.createElement(PaginationItem, Object.assign({}, paginationItemProps));
  } : _props$renderItem,
      _props$siblingCount = props.siblingCount,
      siblingCount = _props$siblingCount === void 0 ? defaultProps$8.siblingCount : _props$siblingCount,
      _props$totalPages = props.totalPages,
      totalPages = _props$totalPages === void 0 ? defaultProps$8.totalPages : _props$totalPages,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$S);

  var _usePagination = usePagination({
    boundaryCount: boundaryCount,
    currentPage: currentPage,
    componentName: 'Pagination',
    defaultPage: defaultPage,
    disabled: disabled,
    hidePrevButton: hidePrevButton,
    hideNextButton: hideNextButton,
    onChange: onChange,
    siblingCount: siblingCount,
    totalPages: totalPages
  }),
      items = _usePagination.items;

  return React.createElement(PaginationRoot, Object.assign({
    "aria-label": ariaLabel,
    as: component,
    className: className,
    itemScope: true,
    itemType: 'https://schema.org/SiteNavigationElement/Pagination',
    role: 'navigation'
  }, otherProps, {
    ref: ref
  }), React.createElement(PaginationList, null, items.map(function (paginationItemProps, index) {
    var itemAriaHidden = paginationItemProps.paginationItemType === 'start-ellipsis' || paginationItemProps.paginationItemType === 'end-ellipsis';
    var itemAriaLabel = getItemAriaLabel(paginationItemProps);
    var itemAriaLive = paginationItemProps.selected ? 'polite' : 'off';
    return React.createElement("li", {
      "aria-hidden": itemAriaHidden,
      key: index
    }, renderItem(_extends({}, paginationItemProps, {
      'aria-label': itemAriaLabel,
      'aria-live': itemAriaLive
    })));
  })));
});
Pagination.displayName = 'Pagination';

var _templateObject$L, _templateObject2$q, _templateObject3$g, _templateObject4$b, _templateObject5$6;
/**
 * This animation keyframes will be applied to the progress value of an indeterminate progress bar.
 */

var indeterminateKeyframes = /*#__PURE__*/keyframes(_templateObject$L || (_templateObject$L = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  from {\n    transform: translateX(-99%);\n  }\n  to {\n    transform: translateX(199%);\n  }\n"])));
/**
 * Here we define the styles applied to the progress bar itself.
 *
 * They are defined separately to allow reuse in more than one CSS selector, including browser-specific selectors.
 */

var progressBarRootStyles = /*#__PURE__*/css(function (_ref) {
  var color = _ref.color,
      progressBar = _ref.theme.component.progressBar;
  return css(_templateObject2$q || (_templateObject2$q = _taggedTemplateLiteralLoose(["\n    // The accent-color property is used to apply a background color to the inner bar in Chrome, Edge and Firefox.\n    accent-color: ", ";\n    // Reset the appearance of the progress bar to prevent browser-specific implementations from native styling.\n    appearance: none;\n    // Since Chrome may apply colors that contrast with the progress bar color, we explicitly set the background color.\n    background-color: ", ";\n    // We define a border-radius here, but we will also define it in the value bar.\n    border-radius: ", ";\n    // Prevent the progress bar from displaying a border in browsers like Firefox\n    border-width: ", ";\n    // We set a height for the progress bar. No internal elements will exceed this height.\n    height: ", ";\n    // We set this overflow to ensure that the border-radius and value bar are displayed correctly.\n    overflow: clip;\n    // This relative positioning allows absolute positioning to be used on internal bar elements.\n    position: relative;\n    // We set a vertical alignment to ensure the progress bar is positioned properly when next to other components.\n    vertical-align: baseline;\n    // We set a width of 100% to override the native width of the progress bar.\n    width: 100%;\n    // We set a z-index to ensure the progress bar will be positioned above surfaces.\n    z-index: ", ";\n\n    // If the person's device allows animations, apply transition effect\n    @media (prefers-reduced-motion: no-preference) {\n      // Apply transition effects to the accent color\n      transition-property: accent-color;\n    }\n  "])), progressBar[color].backgroundColor, progressBar.root.backgroundColor, progressBar.root.borderRadius, progressBar.root.borderWidth, progressBar.progressBar.height, progressBar.root.zIndex);
});
/**
 * Styles that will be applied to the progress bar representing the amount of progress (progress value).
 */

var progressBarValueStyles = /*#__PURE__*/css(function (_ref2) {
  var color = _ref2.color,
      progressBar = _ref2.theme.component.progressBar;
  return css(_templateObject3$g || (_templateObject3$g = _taggedTemplateLiteralLoose(["\n    // Set background color which will mainly apply when the progress bar is determinate.\n    background-color: ", ";\n    // Define the border-radius of the progress value\n    border-radius: ", ";\n    // Ensures that the progress value does not exceed the lower limit of the progress bar.\n    bottom: 0;\n    // Ensure that the progress value occupies the entire height of the progress bar.\n    height: 100%;\n    // Give more flexibility to position and resize the progress value bar.\n    position: absolute;\n    // Ensure that the progress value does not exceed the upper limit of the progress bar.\n    top: 0;\n    // Set a reference point for progress value transformations\n    transform-origin: left;\n    // Prepares the browser for changes in some properties, optimizing processing and avoiding animation delays.\n    will-change: background-color, transform, width;\n    // Ensure that the progress value is always above the progress bar.\n    z-index: ", ";\n\n    @media (prefers-reduced-motion: no-preference) {\n      // Ensure that the animation bar width change will be smooth.\n      animation-timing-function: ", ";\n      // Prevents delays in progress value transitions.\n      transition-delay: ", ";\n      // Sets a duration for the transition from a valid token\n      transition-duration: ", ";\n      // Allows background, width and positioning changes to be smoother in some WebKit-based browsers.\n      transition-property: background-color, transform, width;\n      // Ensures transitions happen in a more natural way.\n      transition-timing-function: ", ";\n    }\n  "])), progressBar[color].backgroundColor, progressBar.progressBar.borderRadius, function (_ref3) {
    var theme = _ref3.theme;
    return theme.component.progressBar.progressValue.zIndex;
  }, progressBar.progressValue.transitionTimingFunction, progressBar.progressValue.transitionDelay, progressBar.progressValue.transitionDuration, progressBar.progressValue.transitionTimingFunction);
});
var indeterminateProgressBarValueStyles = /*#__PURE__*/css(function (_ref4) {
  var speed = _ref4.speed;
  return css(_templateObject4$b || (_templateObject4$b = _taggedTemplateLiteralLoose(["\n    // If no animation can be applied, the undetermined progress value will assume the width of 50% (half)\n    width: 50%;\n\n    @media (prefers-reduced-motion: no-preference) {\n      // Sets a speed for the progress bar animation. Without it, the animation is not played.\n      animation-duration: ", "s;\n      // Apply animation effect to the indeterminate progress value if the person's device allows animations.\n      animation-name: ", ";\n      // Ensure that the animation of an indeterminate progress bar will play infinitely.\n      animation-iteration-count: infinite;\n    }\n  "])), speed, indeterminateKeyframes);
});
/**
 * The progress bar root component is the starting point for styling the component.
 */

var ProgressBarRoot = /*#__PURE__*/styled.progress(_templateObject5$6 || (_templateObject5$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n\n  // The following styles will apply to the progress bar in Chrome, Edge, Safari and other WebKit based browsers.\n  &::-webkit-progress-bar {\n    ", "\n  }\n\n  // Apply styled to a pseudo selector that will simulate the progress value in WebKit-based browsers, like Chrome.\n  &::before {\n    ", "\n  }\n\n  // The following styles will be applied to progress value in Chrome, Edge, Safari and another WebKit-based browsers.\n  &::-webkit-progress-value {\n    ", "\n  }\n\n  // The following styles will be applied to the bar representing the amount of progress (progress value) in Firefox.\n  &::-moz-progress-bar {\n    ", "\n  }\n\n  // The following styles activate progress value animation on indeterminate progress bars for Webkit-based browsers.\n  &:indeterminate::before {\n    // Ensures the pseudo-element enters the DOM when the progress bar is indeterminate.\n    content: '';\n\n    // The following styles are applied to the indeterminate progress value for WebKit-based browser, like Chrome.\n    ", "\n  }\n\n  // The following styles are applied to the progress value when the progress bar is indeterminate in Firefox.\n  &:indeterminate::-moz-progress-bar {\n    ", "\n  }\n"])), progressBarRootStyles, progressBarRootStyles, progressBarValueStyles, progressBarValueStyles, progressBarValueStyles, indeterminateProgressBarValueStyles, indeterminateProgressBarValueStyles);

var defaultColorProp = 'informative';
var defaultVariantProp = 'indeterminate';
var useProgressBarProps = function useProgressBarProps(_ref) {
  var _ref$color = _ref.color,
      colorProp = _ref$color === void 0 ? defaultColorProp : _ref$color,
      _ref$variant = _ref.variant,
      variantProp = _ref$variant === void 0 ? defaultVariantProp : _ref$variant;
  var color = useMemo(function () {
    if (colorProp === 'undefined') {
      return defaultColorProp;
    }

    var validColors = ['attention', 'critical', 'informative', 'success'];

    if (!validColors.includes(colorProp)) {
      throw new Error("Invalid color prop: " + colorProp + ". Valid values: " + validColors.join(', '));
    }

    return colorProp;
  }, [colorProp]);
  var variant = useMemo(function () {
    if (variantProp === 'undefined') {
      return defaultVariantProp;
    }

    var validVariants = ['determinate', 'indeterminate'];

    if (!validVariants.includes(variantProp)) {
      throw new Error("Invalid variant prop: " + variantProp + ". Valid values: " + validVariants.join(', '));
    }

    return variantProp;
  }, [variantProp]);
  return {
    color: color,
    variant: variant
  };
};

var _excluded$T = ["aria-valuenow", "color", "className", "component", "max", "speed", "value", "variant"];
var ProgressBar = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaValueNowProp = props['aria-valuenow'],
      colorProp = props.color,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'progress' : _props$component,
      max = props.max,
      _props$speed = props.speed,
      speed = _props$speed === void 0 ? 2.1 : _props$speed,
      valueProp = props.value,
      variantProp = props.variant,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$T);

  var _useProgressBarProps = useProgressBarProps({
    color: colorProp,
    variant: variantProp
  }),
      color = _useProgressBarProps.color,
      variant = _useProgressBarProps.variant;

  var value = useMemo(function () {
    if (variant === 'indeterminate') {
      return;
    }

    return valueProp || 0;
  }, [variant, valueProp]);
  var ariaValueNow = useMemo(function () {
    if (variant === 'indeterminate') {
      return;
    }

    return ariaValueNowProp || value;
  }, [ariaValueNowProp, value, variant]);
  return React.createElement(ProgressBarRoot, Object.assign({
    "aria-valuenow": ariaValueNow,
    as: component,
    className: className,
    color: color,
    max: max,
    role: 'progressbar',
    speed: speed,
    value: value,
    variant: variant
  }, otherProps, {
    ref: ref
  }));
});
ProgressBar.displayName = 'ProgressBar';

var _excluded$U = ["className", "clearButtonAriaLabel", "disabled", "hideClearButton", "hideSearchButton", "label", "onClear", "onSearch", "readOnly", "searchButtonAriaLabel", "type", "value"];

var _templateObject$M;
var SearchOutline = Icons.SearchOutline;
var SearchInputAdornment = /*#__PURE__*/styled(InputAdornment)(_templateObject$M || (_templateObject$M = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  pointer-events: none;\n"])));
var SearchInput = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$clearButtonAri = props.clearButtonAriaLabel,
      clearButtonAriaLabel = _props$clearButtonAri === void 0 ? 'Clear' : _props$clearButtonAri,
      disabled = props.disabled,
      _props$hideClearButto = props.hideClearButton,
      hideClearButton = _props$hideClearButto === void 0 ? true : _props$hideClearButto,
      _props$hideSearchButt = props.hideSearchButton,
      hideSearchButton = _props$hideSearchButt === void 0 ? true : _props$hideSearchButt,
      label = props.label,
      onClear = props.onClear,
      onSearch = props.onSearch,
      readOnly = props.readOnly,
      _props$searchButtonAr = props.searchButtonAriaLabel,
      searchButtonAriaLabel = _props$searchButtonAr === void 0 ? 'Search' : _props$searchButtonAr,
      _props$type = props.type,
      type = _props$type === void 0 ? 'search' : _props$type,
      value = props.value,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$U);

  var renderSearchIcon = function renderSearchIcon(_ref) {
    var error = _ref.error;
    var iconColor = error ? 'colorRoleOnCriticalSecondary' : 'colorRoleOnBackground';
    return React.createElement(SearchInputAdornment, {
      position: 'start'
    }, React.createElement(SearchOutline, {
      iconColor: iconColor
    }));
  };

  var hasClearIcon = !disabled && !hideClearButton && !readOnly;

  var renderEndAdornment = function renderEndAdornment() {
    return React.createElement(InputAdornment, {
      position: 'end'
    }, hasClearIcon && React.createElement(IconButton, {
      "aria-label": clearButtonAriaLabel,
      onClick: onClear,
      size: 'sm'
    }, React.createElement(Icons.CircleCrossOutline, {
      size: 'md'
    })), !hideSearchButton && React.createElement(IconButton, {
      "aria-label": searchButtonAriaLabel,
      onClick: onSearch,
      size: 'sm'
    }, React.createElement(Icons.ArrowRightOutline, {
      size: 'md'
    })));
  };

  return React.createElement(TextField, Object.assign({
    className: className,
    disabled: disabled,
    label: label,
    readOnly: readOnly,
    renderEndAdornment: renderEndAdornment,
    renderStartAdornment: renderSearchIcon,
    type: type,
    value: value
  }, otherProps, {
    ref: ref
  }));
});
SearchInput.displayName = 'SearchInput';

var _templateObject$N, _templateObject2$r;
var SelectRoot = /*#__PURE__*/styled.div(_templateObject$N || (_templateObject$N = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", ";\n"])), function (_ref) {
  var classNamePrefix = _ref.classNamePrefix,
      errorClassName = _ref.errorClassName,
      theme = _ref.theme;
  return css(_templateObject2$r || (_templateObject2$r = _taggedTemplateLiteralLoose(["\n    min-height: ", ";\n\n    .", "__control {\n      background-color: ", ";\n      border-color: ", ";\n      border-radius: ", ";\n      border-style: solid;\n      border-width: ", ";\n      box-shadow: ", ";\n      color: ", ";\n\n      .", "__input-container {\n        margin-bottom: ", ";\n        margin-left: ", ";\n        margin-right: ", ";\n        margin-top: ", ";\n      }\n\n      .", "__indicator {\n        color: ", ";\n        padding-bottom: ", ";\n        padding-left: ", ";\n        padding-right: ", ";\n        padding-top: ", ";\n      }\n\n      &.", "__control--is-disabled {\n        background-color: ", ";\n\n        .", "__indicator {\n          color: ", ";\n        }\n      }\n\n      &:hover {\n        border-color: ", ";\n      }\n\n      &.", "__control--is-focused {\n        border-color: ", ";\n        outline: ", " solid\n          ", " !important;\n        outline-offset: ", ";\n        // Despite being redundant, it ensures that the outline is displayed correctly in Firefox.\n        outline-style: solid;\n      }\n\n      .", "__placeholder {\n        color: ", ";\n        font-weight: ", ";\n        margin-bottom: ", ";\n      }\n\n      .", "__value-container {\n        padding-bottom: ", ";\n        padding-left: ", ";\n        padding-right: ", ";\n        padding-top: ", ";\n      }\n\n      .", "__single-value {\n        color: ", ";\n      }\n\n      .", "__indicator-separator {\n        display: none;\n      }\n\n      &.", "__control--menu-is-open {\n        .", "__indicator {\n          svg {\n            transform: rotate(180deg);\n          }\n        }\n      }\n    } // end control\n\n    .", "__menu, .", "__menu-list {\n      ", "\n      ", "\n    }\n\n    .", "__menu {\n      border-bottom-style: solid;\n      border-bottom-width: ", ";\n      border-color: ", ";\n      border-left-style: solid;\n      border-left-width: ", ";\n      border-right-style: solid;\n      border-right-width: ", ";\n      border-top-style: none;\n      border-top-width: ", ";\n      z-index: ", ";\n\n      .", "__menu-list {\n        .", "__option {\n          background-color: ", ";\n          color: ", ";\n          cursor: pointer;\n          padding-bottom: ", ";\n          padding-left: ", ";\n          padding-right: ", ";\n          padding-top: ", ";\n\n          ", "\n\n          &.", "__option--is-focused {\n            ", "\n          }\n\n          &.", "__option--is-selected {\n            background-color: ", ";\n            color: ", ";\n            opacity: ", ";\n          }\n        }\n      }\n    }\n\n    .", " {\n      .", "__control {\n        background-color: ", ";\n        border-color: ", ";\n        border-width: ", ";\n        color: ", ";\n\n        .", "__indicator {\n          color: ", ";\n        }\n\n        .", "__placeholder {\n          color: ", ";\n        }\n      }\n    }\n  "])), theme.component.select.root.minHeight, classNamePrefix, theme.component.select.control.backgroundColor, theme.component.select.control.borderColor, theme.component.select.control.borderRadius, theme.component.select.control.borderWidth, theme.component.select.control.boxShadow, theme.component.select.control.color, classNamePrefix, theme.component.select.inputContainer.marginBottom, theme.component.select.inputContainer.marginLeft, theme.component.select.inputContainer.marginRight, theme.component.select.inputContainer.marginTop, classNamePrefix, theme.component.select.indicator.color, theme.component.select.indicator.paddingBottom, theme.component.select.indicator.paddingLeft, theme.component.select.indicator.paddingRight, theme.component.select.indicator.paddingTop, classNamePrefix, theme.component.select.disabledControl.backgroundColor, classNamePrefix, theme.component.select.disabledIndicator.color, theme.component.select.controlHover.borderColor, classNamePrefix, theme.component.select.controlFocused.borderColor, theme.component.select.controlFocused.outlineWidth, theme.component.select.controlFocused.outlineColor, theme.component.select.controlFocused.outlineOffset, classNamePrefix, theme.component.select.placeholder.color, theme.component.select.placeholder.fontWeight, theme.component.select.placeholder.marginBottom, classNamePrefix, theme.component.select.valueContainer.paddingBottom, theme.component.select.valueContainer.paddingLeft, theme.component.select.valueContainer.paddingRight, theme.component.select.valueContainer.paddingTop, classNamePrefix, theme.component.select.singleValue.color, classNamePrefix, classNamePrefix, classNamePrefix, classNamePrefix, classNamePrefix, listRootStyles, menuListRootStyles, classNamePrefix, theme.component.select.menu.borderBottomWidth, theme.component.select.menu.borderColor, theme.component.select.menu.borderLeftWidth, theme.component.select.menu.borderRightWidth, theme.component.select.menu.borderTopWidth, theme.component.select.menu.zIndex, classNamePrefix, classNamePrefix, theme.component.select.menuOption.backgroundColor, theme.component.select.menuOption.color, theme.component.select.menuOption.paddingBottom, theme.component.select.menuOption.paddingLeft, theme.component.select.menuOption.paddingRight, theme.component.select.menuOption.paddingTop, menuItemStyles, classNamePrefix, focusedMenuItemStyles, classNamePrefix, theme.component.select.menuOptionSelected.backgroundColor, theme.component.select.menuOptionSelected.color, theme.component.select.menuOptionSelected.opacity, errorClassName, classNamePrefix, theme.component.select.controlError.backgroundColor, theme.component.select.controlError.borderColor, theme.component.select.controlError.borderWidth, theme.component.select.controlError.color, classNamePrefix, theme.component.select.indicatorError.color, classNamePrefix, theme.component.select.placeholderError.color);
});

var _excluded$V = ["aria-labelledby", "className", "classNamePrefix", "components", "disabled", "error", "errorClassName", "hint", "multiple", "name", "id", "inputId", "isSearchable", "label", "placeholder", "required"];

var DropdownIndicator = function DropdownIndicator(_ref) {
  var props = _extends({}, _ref);

  return React.createElement(components.DropdownIndicator, Object.assign({}, props), React.createElement(Icons.ChevronDownOutline, null));
};

var Select = function Select(props) {
  var ariaLabeledBy = props['aria-labelledby'],
      classNameProp = props.className,
      _props$classNamePrefi = props.classNamePrefix,
      classNamePrefix = _props$classNamePrefi === void 0 ? 'select' : _props$classNamePrefi,
      _props$components = props.components,
      components = _props$components === void 0 ? {
    DropdownIndicator: DropdownIndicator
  } : _props$components,
      disabled = props.disabled,
      error = props.error,
      _props$errorClassName = props.errorClassName,
      errorClassName = _props$errorClassName === void 0 ? 'selectHasError' : _props$errorClassName,
      hint = props.hint,
      multiple = props.multiple,
      name = props.name,
      id = props.id,
      inputIdProp = props.inputId,
      _props$isSearchable = props.isSearchable,
      isSearchable = _props$isSearchable === void 0 ? true : _props$isSearchable,
      label = props.label,
      _props$placeholder = props.placeholder,
      placeholder = _props$placeholder === void 0 ? 'Select…' : _props$placeholder,
      _props$required = props.required,
      required = _props$required === void 0 ? false : _props$required,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$V);

  var className = useMemo(function () {
    var classNames = [classNameProp];

    if (error) {
      classNames.push(errorClassName);
    }

    return classNames.join(' ');
  }, [classNameProp, errorClassName, error]);
  var inputId = useMemo(function () {
    if (inputIdProp) {
      return inputIdProp;
    }

    if (id) {
      return id + "-input";
    }

    return undefined;
  }, [id, inputIdProp]);
  var inputLabelId = useInputLabelId({
    id: id,
    label: label
  });
  return React.createElement(FieldWrapper, {
    disabled: disabled,
    error: error,
    hint: hint,
    label: label,
    required: required
  }, React.createElement(SelectRoot, {
    classNamePrefix: classNamePrefix,
    disabled: disabled,
    errorClassName: errorClassName
  }, React.createElement(SelectBase, Object.assign({
    "aria-labelledby": ariaLabeledBy || inputLabelId,
    className: className,
    classNamePrefix: classNamePrefix,
    components: components,
    id: id,
    inputId: inputId,
    isDisabled: disabled,
    isMulti: multiple,
    isSearchable: isSearchable,
    name: name,
    placeholder: placeholder
  }, otherProps))));
};
Select.displayName = 'Select';

var _templateObject$O;
var ShapeRoot = /*#__PURE__*/styled.div(_templateObject$O || (_templateObject$O = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  --height: ", ";\n  background-color: ", ";\n  border-radius: ", ";\n  height: var(--height);\n  overflow: hidden;\n  width: ", ";\n"])), function (_ref) {
  var height = _ref.height,
      theme = _ref.theme;

  if (typeof height === 'undefined') {
    return 'auto';
  }

  if (typeof height === 'number') {
    return height + "px";
  }

  if (theme.size[height]) {
    return theme.size[height];
  }

  return height;
}, function (_ref2) {
  var shapeColor = _ref2.shapeColor,
      theme = _ref2.theme;

  if (typeof shapeColor === 'undefined') {
    return 'transparent';
  }

  if (theme.color[shapeColor]) {
    return theme.color[shapeColor];
  }

  return shapeColor;
}, function (_ref3) {
  var shapeStyle = _ref3.shapeStyle,
      theme = _ref3.theme;
  return theme.component.shape[shapeStyle != null ? shapeStyle : 'rectangle'].borderRadius;
}, function (_ref4) {
  var height = _ref4.height,
      width = _ref4.width,
      theme = _ref4.theme;

  if (typeof width === 'undefined') {
    return "var(--height)";
  }

  if (typeof height === 'number') {
    return width + "px";
  }

  if (theme.size[width]) {
    return theme.size[width];
  }

  return width;
});

var shapeStyles = ['circle', 'square', 'ellipse', 'pill', 'rectangle'];

var _excluded$W = ["children", "className", "component", "height", "shapeStyle", "variant", "width"];
var defaultProps$9 = {
  shapeStyle: 'rectangle'
};
var Shape = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      height = props.height,
      _props$shapeStyle = props.shapeStyle,
      shapeStyleProp = _props$shapeStyle === void 0 ? defaultProps$9.shapeStyle : _props$shapeStyle,
      variantProp = props.variant,
      widthProp = props.width,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$W);

  var variant = useMemo(function () {
    if (typeof variantProp === 'undefined' || !shapeStyles.includes(variantProp)) {
      return undefined;
    }

    console.warn('The `variant` prop is deprecated. Please use `shapeStyle` instead.');
    return variantProp;
  }, [variantProp]);
  var shapeStyle = useMemo(function () {
    if (variant) {
      return variant;
    }

    if (typeof shapeStyleProp === 'undefined') {
      console.warn('Shape component requires a valid `shapeStyle` property');
      return defaultProps$9.shapeStyle;
    }

    if (!shapeStyles.includes(shapeStyleProp)) {
      console.warn("Shape: " + shapeStyleProp + " is not a valid shape style. Valid styles are: " + shapeStyles.join(', '));
      return defaultProps$9.shapeStyle;
    }

    return shapeStyleProp;
  }, [shapeStyleProp, variant]);
  var width = useMemo(function () {
    if (typeof shapeStyle === 'undefined') {
      return widthProp;
    }

    if (['circle', 'square'].includes(shapeStyle)) {
      return height;
    }

    return widthProp;
  }, [height, shapeStyle, widthProp]);
  return React.createElement(ShapeRoot, Object.assign({
    as: component,
    className: className,
    height: height,
    shapeStyle: shapeStyle,
    width: width
  }, otherProps, {
    ref: ref
  }), children);
});
Shape.displayName = 'Shape';

var useMediaQuery = function useMediaQuery(query) {
  var supportMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';
  var matchMedia = supportMatchMedia ? window.matchMedia : null;
  var defaultMatches = false;

  var _useState = useState(function () {
    if (supportMatchMedia) {
      return false;
    }

    if (matchMedia) {
      return matchMedia(query).matches;
    }

    return defaultMatches;
  }),
      matches = _useState[0],
      setMatches = _useState[1];

  useEffect(function () {
    if (!supportMatchMedia) {
      return;
    }

    var mediaQueryList = window.matchMedia(query);

    var updateMatch = function updateMatch() {
      setMatches(mediaQueryList.matches);
    };

    updateMatch();
    window.addEventListener('resize', updateMatch);
    return function () {
      window.removeEventListener('resize', updateMatch);
    };
  }, [query, matchMedia, matches, supportMatchMedia]);
  useDebugValue({
    query: query,
    matches: matches
  });
  return matches;
};

var _templateObject$P;
var SidebarLayoutRoot = /*#__PURE__*/styled.div(function (_ref) {
  var hideSidebarOnMobile = _ref.hideSidebarOnMobile,
      _ref$theme = _ref.theme,
      breakpoint = _ref$theme.breakpoint,
      sidebarLayout = _ref$theme.component.sidebarLayout;
  return css(_templateObject$P || (_templateObject$P = _taggedTemplateLiteralLoose(["\n    margin: 0 auto;\n    position: relative;\n    width: 100%;\n\n    & > :first-child {\n      border-right-color: ", ";\n      border-right-style: solid;\n      border-right-width: ", ";\n      display: ", ";\n    }\n\n    @media (min-width: 352px) {\n      & > :first-child {\n        max-width: 352px;\n        min-width: 352px;\n      }\n    }\n\n    @media (min-width: ", ") {\n      &,\n      & > :first-child {\n        display: grid;\n      }\n    }\n\n    @media (min-width: ", ") and (max-width: 1491px) {\n      grid-template-columns: 352px calc(100% - 352px);\n      min-width: ", ";\n      max-width: 1491px;\n    }\n\n    @media (min-width: 1492px) and (max-width: 1717px) {\n      grid-template-columns: 352px calc(100% - 352px);\n      min-width: 1492px;\n      max-width: 1717px;\n    }\n\n    @media (min-width: 1718px) {\n      grid-template-columns: 352px 1366px;\n      min-width: 1718px;\n      max-width: 1718px;\n    }\n\n    & > :last-child {\n      max-width: 100%;\n      min-width: 100%;\n    }\n  "])), sidebarLayout.sidebar.borderRightColor, sidebarLayout.sidebar.borderRightWidth, hideSidebarOnMobile ? 'none' : 'grid', breakpoint.breakpointLgMinWidth, breakpoint.breakpointLgMinWidth, breakpoint.breakpointLgMinWidth);
});

var _excluded$X = ["children", "component", "hideSidebarOnMobile", "sidebarContent"];
var SidebarLayout = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$hideSidebarOnM = props.hideSidebarOnMobile,
      hideSidebarOnMobile = _props$hideSidebarOnM === void 0 ? false : _props$hideSidebarOnM,
      sidebarContent = props.sidebarContent,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$X);

  var matches = {
    md: useMediaQuery('(min-width: 1140px) and (max-width: 1491px)'),
    lg: useMediaQuery('(min-width: 1492px) and (max-width: 1717px)'),
    xl: useMediaQuery('(min-width: 1718px)')
  };
  var sidebarContainerMaxWidth = useMemo(function () {
    return (matches.md || matches.lg || matches.xl) && 'sm';
  }, [matches.md, matches.lg, matches.xl]);
  var mainContainerMaxWidth = useMemo(function () {
    if (matches.md) {
      return 'md';
    }

    if (matches.lg) {
      return 'lg';
    }

    if (matches.xl) {
      return 'xl';
    }

    return false;
  }, [matches.md, matches.lg, matches.xl]);
  return React.createElement(SidebarLayoutRoot, Object.assign({
    as: component,
    hideSidebarOnMobile: hideSidebarOnMobile
  }, otherProps, {
    ref: ref
  }), React.createElement(GridContainer, {
    component: 'aside',
    maxWidth: sidebarContainerMaxWidth,
    variant: 'tight'
  }, sidebarContent), React.createElement(GridContainer, {
    component: 'main',
    maxWidth: mainContainerMaxWidth,
    role: 'main',
    variant: 'tight'
  }, children));
});
SidebarLayout.displayName = 'SidebarLayout';

var _templateObject$Q, _templateObject2$s, _templateObject3$h;
var Pin = /*#__PURE__*/styled.div(_templateObject$Q || (_templateObject$Q = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  background-color: #3e3e3e;\n  border-radius: 34px;\n  cursor: ", ";\n  transition: 0.4s;\n  pointer-events: ", ";\n\n  &::before {\n    position: absolute;\n    bottom: 4px;\n    left: 4px;\n    width: 16px;\n    height: 16px;\n    background-color: #fff;\n    border-radius: 50%;\n    transition: 0.4s;\n    content: '';\n  }\n"])), function (_ref) {
  var disabled = _ref.disabled;
  return disabled ? 'not-allowed' : 'pointer';
}, function (_ref2) {
  var disabled = _ref2.disabled;
  return disabled ? 'none' : 'auto';
});
var Input = /*#__PURE__*/styled.input(_templateObject2$s || (_templateObject2$s = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  width: 0;\n  height: 0;\n  opacity: 0;\n"])));
var Toggle = /*#__PURE__*/styled.label(_templateObject3$h || (_templateObject3$h = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n  display: inline-block;\n  width: 40px;\n  height: 24px;\n\n  ", ":checked + ", " {\n    background-color: #49a782;\n  }\n\n  ", ":focus + ", " {\n    box-shadow: 0 0 1px #49a782;\n  }\n\n  ", ":disabled + ", " {\n    background-color: #a6a6a6;\n  }\n\n  ", ":checked + ", ":before {\n    transform: translateX(16px);\n  }\n"])), Input, Pin, Input, Pin, Input, Pin, Input, Pin);

var _excluded$Y = ["disabled", "error", "hint", "id", "label", "required"];
var SwitchInput = /*#__PURE__*/forwardRef(function (props, ref) {
  var disabled = props.disabled,
      error = props.error,
      hint = props.hint,
      id = props.id,
      label = props.label,
      _props$required = props.required,
      required = _props$required === void 0 ? false : _props$required,
      inputProps = _objectWithoutPropertiesLoose(props, _excluded$Y);

  return React.createElement(FieldWrapper, {
    id: id,
    disabled: disabled,
    error: error,
    hint: hint,
    label: label,
    required: required
  }, React.createElement(Toggle, null, React.createElement(Input, Object.assign({
    disabled: disabled,
    id: id,
    ref: ref,
    required: required,
    type: "checkbox"
  }, inputProps)), React.createElement(Pin, {
    disabled: disabled
  })));
});
SwitchInput.displayName = 'SwitchInput';

var Context$1 = /*#__PURE__*/createContext(null);
Context$1.displayName = 'TabContext';
var TabContext = function TabContext(props) {
  var children = props.children,
      value = props.value;
  var context = useMemo(function () {
    return {
      value: value
    };
  }, [value]);
  return React.createElement(Context$1.Provider, {
    value: context
  }, children);
};
var useTabContext = function useTabContext() {
  return useContext(Context$1);
};

var _templateObject$R, _templateObject2$t, _templateObject3$i, _templateObject4$c, _templateObject5$7, _templateObject6$5, _templateObject7$3, _templateObject8$3, _templateObject9$3, _templateObject10$3;
var notSelectedStyles = /*#__PURE__*/css(_templateObject$R || (_templateObject$R = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref) {
  var theme = _ref.theme;
  return css(_templateObject2$t || (_templateObject2$t = _taggedTemplateLiteralLoose(["\n    border-bottom-color: ", ";\n    color: ", ";\n    font-weight: ", ";\n\n    &:before {\n      background-color: ", ";\n    }\n\n    &:hover {\n      color: ", ";\n\n      &:before {\n        background-color: ", ";\n      }\n\n      &:after {\n        background-color: ", ";\n        opacity: ", ";\n      }\n    }\n\n    &:active,\n    &:active:hover {\n      color: ", ";\n    }\n\n    &:focus-visible {\n      color: ", ";\n    }\n  "])), theme.component.tab.root.borderBottomColor, theme.component.tab.root.color, theme.component.tab.root.fontWeight, theme.component.tab.rootBefore.backgroundColor, theme.component.tab.hover.color, theme.component.tab.hoverBefore.backgroundColor, theme.component.tab.hoverAfter.backgroundColor, theme.component.tab.hoverAfter.opacity, theme.component.tab.pressed.color, theme.component.tab.focusVisible.color);
});
var selectedStyles = /*#__PURE__*/css(_templateObject3$i || (_templateObject3$i = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref2) {
  var theme = _ref2.theme;
  return css(_templateObject4$c || (_templateObject4$c = _taggedTemplateLiteralLoose(["\n    border-bottom-color: ", ";\n    color: ", ";\n    font-weight: ", ";\n\n    &:before {\n      background-color: ", ";\n    }\n\n    &:hover {\n      color: ", ";\n    }\n\n    &:hover:before {\n      background-color: ", ";\n    }\n\n    &:hover:after {\n      background-color: ", ";\n      opacity: ", ";\n    }\n\n    &:active,\n    &:active:hover {\n      color: ", ";\n    }\n\n    &:active:before {\n      background-color: ", ";\n    }\n\n    &:active:after {\n      background-color: ", ";\n      opacity: ", ";\n    }\n\n    &:focus-visible {\n      color: ", ";\n    }\n  "])), theme.component.tab.selected.borderBottomColor, theme.component.tab.selected.color, theme.component.tab.selected.fontWeight, theme.component.tab.selectedBefore.backgroundColor, theme.component.tab.selectedHover.color, theme.component.tab.selectedHoverBefore.backgroundColor, theme.component.tab.selectedHoverAfter.backgroundColor, theme.component.tab.selectedHoverAfter.opacity, theme.component.tab.selectedPressed.color, theme.component.tab.selectedPressedBefore.backgroundColor, theme.component.tab.selectedPressedAfter.backgroundColor, theme.component.tab.selectedPressedAfter.opacity, theme.component.tab.selectedFocusVisible.color);
});
var TabRoot = /*#__PURE__*/styled(ButtonBase)(_templateObject5$7 || (_templateObject5$7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-bottom-style: solid;\n  cursor: ", ";\n  display: flex;\n  overflow: hidden;\n  position: relative;\n  z-index: ", ";\n\n  & [role='status'] {\n    max-width: ", ";\n  }\n\n  &:active,\n  &:active:hover,\n  &:focus-visible,\n  &:hover {\n    will-change: border-bottom-color, color;\n    &:before,\n    &:after {\n      will-change: background-color, opacity;\n    }\n  }\n\n  ", "\n"])), function (_ref3) {
  var disabled = _ref3.disabled;
  return disabled ? 'not-allowed' : 'pointer';
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.tab.root.zIndex;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.tab.tabStatus.maxWidth;
}, function (_ref6) {
  var tab = _ref6.theme.component.tab;
  return css(_templateObject6$5 || (_templateObject6$5 = _taggedTemplateLiteralLoose(["\n    border-bottom-width: ", ";\n    border-top-left-radius: ", ";\n    border-top-right-radius: ", ";\n    height: ", ";\n    min-width: ", ";\n\n    &:before,\n    &:after {\n      border-radius: ", ";\n      content: '';\n      height: ", ";\n      will-change: background-color;\n    }\n\n    &:before {\n      left: ", ";\n      z-index: ", ";\n    }\n\n    &:after {\n      right: ", ";\n      z-index: ", ";\n    }\n\n    @media (prefers-reduced-motion: no-preference) {\n      &,\n      &:before,\n      &:after {\n        transition-delay: ", ";\n        transition-duration: ", ";\n        transition-property: background-color, color, opacity, outline;\n        transition-timing-function: ", ";\n      }\n    }\n\n    &:focus-visible {\n      border-bottom-color: ", ";\n      border-bottom-left-radius: ", ";\n      border-bottom-right-radius: ", ";\n      outline: ", " solid ", ";\n      outline-offset: ", ";\n      // Despite being redundant, it ensures that the outline is displayed correctly in Firefox.\n      outline-style: solid;\n      will-change: border-bottom-color, border-bottom-left-radius, border-bottom-right-radius, color, outline;\n    }\n\n    &:focus-visible:before {\n      background-color: ", ";\n      will-change: background-color;\n    }\n\n    &:focus-visible:after {\n      background-color: ", ";\n      opacity: ", ";\n      will-change: background-color, opacity;\n    }\n\n    &:active:before,\n    &:active:hover:before {\n      background-color: ", ";\n    }\n\n    &:active:after,\n    &:active:hover:after {\n      background-color: ", ";\n      opacity: ", ";\n    }\n\n    &:disabled {\n      color: ", ";\n    }\n\n    ", "\n  "])), tab.root.borderBottomWidth, tab.root.borderTopLeftRadius, tab.root.borderTopRightRadius, tab.root.height, tab.root.minWidth, tab.content.borderRadius, tab.content.height, tab.rootBefore.paddingLeft, tab.rootBefore.zIndex, tab.rootAfter.paddingRight, tab.rootAfter.zIndex, function (_ref7) {
    var theme = _ref7.theme;
    return theme.component.tab.root.transitionDelay;
  }, function (_ref8) {
    var theme = _ref8.theme;
    return theme.component.tab.root.transitionDuration;
  }, function (_ref9) {
    var theme = _ref9.theme;
    return theme.component.tab.root.transitionTimingFunction;
  }, tab.focusVisible.borderBottomColor, tab.focusVisible.borderBottomLeftRadius, tab.focusVisible.borderBottomRightRadius, tab.focusVisible.outlineWidth, tab.focusVisible.outlineColor, tab.focusVisible.outlineOffset, tab.focusVisibleBefore.backgroundColor, tab.focusVisibleAfter.backgroundColor, tab.focusVisibleAfter.opacity, tab.pressedBefore.backgroundColor, tab.pressedAfter.backgroundColor, tab.pressedAfter.opacity, tab.disabled.color, function (_ref10) {
    var selected = _ref10.selected;
    return selected ? selectedStyles : notSelectedStyles;
  });
});
var TabContentWrapper = /*#__PURE__*/styled.div(_templateObject7$3 || (_templateObject7$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  align-items: center;\n  box-sizing: border-box;\n  display: flex;\n  flex-direction: row;\n  flex-shrink: 0;\n  overflow: hidden;\n  position: relative;\n  text-align: center;\n  max-width: 100%;\n  white-space: normal;\n\n  ", "\n"])), function (_ref11) {
  var theme = _ref11.theme;
  return css(_templateObject8$3 || (_templateObject8$3 = _taggedTemplateLiteralLoose(["\n    height: ", ";\n    margin-bottom: calc(", " - ", ");\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n    z-index: ", ";\n\n    @media (min-width: ", ") {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), theme.component.tab.content.height, theme.component.tab.content.marginBottom, theme.component.tab.root.borderBottomWidth, theme.component.tab.content.paddingBottom, theme.component.tab.compactContent.paddingLeft, theme.component.tab.compactContent.paddingRight, theme.component.tab.content.paddingTop, theme.component.tab.content.zIndex, theme.breakpoint.breakpointLgMinWidth, theme.component.tab.standardContent.paddingLeft, theme.component.tab.standardContent.paddingRight);
});
var TabStartAdornment = /*#__PURE__*/styled.div(_templateObject9$3 || (_templateObject9$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-block;\n  margin-left: ", ";\n  margin-right: ", ";\n"])), function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.tab.adornment.marginLeft;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.tab.adornment.marginRight;
});
var TabEndAdornment = /*#__PURE__*/styled.div(_templateObject10$3 || (_templateObject10$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-block;\n  margin-left: ", ";\n  margin-right: ", ";\n"])), function (_ref14) {
  var theme = _ref14.theme;
  return theme.component.tab.endAdornment.marginLeft;
}, function (_ref15) {
  var theme = _ref15.theme;
  return theme.component.tab.endAdornment.marginRight;
});

var _excluded$Z = ["aria-selected", "children", "disabled", "onChange", "onClick", "onFocus", "renderEndAdornment", "renderStartAdornment", "role", "selected", "selectionFollowsFocus", "tabIndex", "value"];
var Tab = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaSelected = props['aria-selected'],
      children = props.children,
      disabled = props.disabled,
      onChange = props.onChange,
      onClick = props.onClick,
      onFocus = props.onFocus,
      renderEndAdornment = props.renderEndAdornment,
      renderStartAdornment = props.renderStartAdornment,
      _props$role = props.role,
      role = _props$role === void 0 ? 'tab' : _props$role,
      selectedProp = props.selected,
      selectionFollowsFocus = props.selectionFollowsFocus,
      tabIndexProp = props.tabIndex,
      value = props.value,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$Z);

  var context = useTabContext();
  var selected = useMemo(function () {
    if (typeof selectedProp !== 'undefined') {
      return selectedProp;
    }

    if (context !== null) {
      return context.value === value;
    }

    return false;
  }, [context, selectedProp, value]);
  var tabIndex = useMemo(function () {
    if (tabIndexProp) {
      return tabIndexProp;
    }

    if (!disabled && selected) {
      return 0;
    }

    return -1;
  }, [disabled, selected, tabIndexProp]);
  var handleClick = useCallback(function (event) {
    if (onChange) {
      onChange(event, value);
    }

    if (onClick) {
      onClick(event);
    }
  }, [onChange, onClick, value]);
  var handleFocus = useCallback(function (event) {
    if (selectionFollowsFocus && !selected && onChange) {
      onChange(event, value);
    }

    if (onFocus) {
      onFocus(event);
    }
  }, [onChange, onFocus, selected, selectionFollowsFocus, value]);
  return React.createElement(TabRoot, Object.assign({
    "aria-selected": ariaSelected || selected,
    disabled: disabled,
    onClick: handleClick,
    onFocus: handleFocus,
    role: role,
    selected: selected,
    tabIndex: tabIndex
  }, otherProps, {
    ref: ref
  }), React.createElement(TabContentWrapper, null, renderStartAdornment && React.createElement(TabStartAdornment, null, renderStartAdornment({
    disabled: disabled,
    selected: selected
  })), children && React.createElement(Text, {
    align: 'center',
    component: 'div',
    textColor: 'inherit',
    variant: 'body2',
    weight: 'inherit'
  }, children), renderEndAdornment && React.createElement(TabEndAdornment, null, renderEndAdornment({
    disabled: disabled,
    selected: selected
  }))));
});
Tab.displayName = 'Tab';

var _templateObject$S;
var TabBarRoot = /*#__PURE__*/styled.div(_templateObject$S || (_templateObject$S = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  -webkit-overflow-scrolling: touch;\n  display: flex;\n  flex-direction: row;\n  padding-bottom: ", ";\n  padding-top: ", ";\n  width: 100%;\n\n  &:after {\n    border-bottom-color: ", ";\n    border-bottom-style: solid;\n    border-bottom-width: ", ";\n    content: '';\n    flex-grow: 1;\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.tabBar.root.paddingBottom;
}, function (_ref2) {
  var gutterTop = _ref2.gutterTop,
      theme = _ref2.theme;
  return gutterTop ? theme.component.tabBar.gutterTop.paddingTop : theme.component.tabBar.root.paddingTop;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.tabBar.after.borderBottomColor;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.tabBar.after.borderBottomWidth;
});

var _excluded$_ = ["aria-label", "aria-labelledby", "aria-orientation", "component", "role"];
var TabBar = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaLabel = props['aria-label'],
      ariaLabelledBy = props['aria-labelledby'],
      _props$ariaOrientati = props['aria-orientation'],
      ariaOrientation = _props$ariaOrientati === void 0 ? 'horizontal' : _props$ariaOrientati,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$role = props.role,
      role = _props$role === void 0 ? 'tablist' : _props$role,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$_);

  return React.createElement(TabBarRoot, Object.assign({
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-orientation": ariaOrientation,
    as: component,
    role: role
  }, otherProps, {
    ref: ref
  }));
});
TabBar.displayName = 'TabBar';

var _templateObject$T;
var TabPanelRoot = /*#__PURE__*/styled.div(_templateObject$T || (_templateObject$T = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: ", ";\n  flex: 1;\n  flex-direction: column;\n  padding-top: 0;\n  width: 100%;\n"])), function (_ref) {
  var hidden = _ref.hidden;
  return hidden ? 'none' : 'flex';
});

var _excluded$$ = ["aria-labelledby", "component", "hidden", "id", "role", "tabIndex", "value"];
var defaultProps$a = {
  hidden: true
};
var TabPanel = /*#__PURE__*/forwardRef(function (props, ref) {
  var ariaLabelledBy = props['aria-labelledby'],
      component = props.component,
      hiddenProp = props.hidden,
      id = props.id,
      _props$role = props.role,
      role = _props$role === void 0 ? 'tabpanel' : _props$role,
      _props$tabIndex = props.tabIndex,
      tabIndex = _props$tabIndex === void 0 ? 0 : _props$tabIndex,
      value = props.value,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$$);

  var context = useTabContext();
  var hidden = useMemo(function () {
    if (typeof hiddenProp === 'boolean') {
      return hiddenProp;
    }

    if (context !== null) {
      return value !== context.value;
    }

    return defaultProps$a.hidden;
  }, [context, hiddenProp, value]);
  return React.createElement(TabPanelRoot, Object.assign({
    "aria-labelledby": ariaLabelledBy,
    as: component,
    hidden: hidden,
    id: id,
    ref: ref,
    role: role,
    tabIndex: tabIndex
  }, otherProps));
});
TabPanel.displayName = 'TabPanel';

var _templateObject$U, _templateObject2$u, _templateObject3$j, _templateObject4$d;
var checkboxPaddingStyles = /*#__PURE__*/css(function (_ref) {
  var tableCell = _ref.theme.component.tableCell;
  return css(_templateObject$U || (_templateObject$U = _taggedTemplateLiteralLoose(["\n    max-height: ", ";\n    min-height: ", ";\n    min-width: ", ";\n    width: ", ";\n  "])), tableCell.checkboxPadding.maxHeight, tableCell.checkboxPadding.minHeight, tableCell.checkboxPadding.minWidth, tableCell.checkboxPadding.width);
});
var densePaddingStyles = /*#__PURE__*/css(function (_ref2) {
  var tableCell = _ref2.theme.component.tableCell;
  return css(_templateObject2$u || (_templateObject2$u = _taggedTemplateLiteralLoose(["\n    height: ", ";\n    min-height: ", ";\n  "])), tableCell.densePadding.height, tableCell.densePadding.minHeight);
});
var stickyStyles = /*#__PURE__*/css(function (_ref3) {
  var sticky = _ref3.sticky,
      _ref3$theme$component = _ref3.theme.component,
      tableCell = _ref3$theme$component.tableCell,
      tableRow = _ref3$theme$component.tableRow,
      variant = _ref3.variant;
  return css(_templateObject3$j || (_templateObject3$j = _taggedTemplateLiteralLoose(["\n    background-color: ", ";\n    border-right: ", "; // TODO: adopt color token\n    border-left: ", "; // TODO: adopt color token\n    left: ", ";\n    right: ", ";\n    position: sticky;\n    z-index: 2; // TODO: adopt z-index token\n\n    tr[aria-selected='true'] & {\n      background-color: ", ";\n    }\n\n    tr:hover & {\n      background-color: ", ";\n    }\n  "])), variant === 'head' ? tableCell.head.backgroundColor : tableCell.root.backgroundColor, sticky === 'left' ? "1px solid #E6E6E6" : '0', sticky === 'right' ? "1px solid #E6E6E6" : '0', sticky === 'left' ? '0' : 'auto', sticky === 'right' ? '0' : 'auto', tableRow.selected.backgroundColor, tableRow.hover.backgroundColor);
});
var TableCellRoot = /*#__PURE__*/styled.td(function (_ref4) {
  var padding = _ref4.padding,
      sticky = _ref4.sticky,
      _ref4$theme = _ref4.theme,
      breakpoint = _ref4$theme.breakpoint,
      tableCell = _ref4$theme.component.tableCell,
      variant = _ref4.variant;
  return css(_templateObject4$d || (_templateObject4$d = _taggedTemplateLiteralLoose(["\n    border-bottom: 1px solid #e6e6e6; // TODO: adopt color token\n    display: table-cell;\n    margin: 0;\n    overflow-y: visible;\n    padding-bottom: ", ";\n    padding-left: ", ";\n    padding-right: ", ";\n    padding-top: ", ";\n    text-align: left;\n    vertical-align: inherit;\n    white-space: nowrap;\n    box-sizing: border-box;\n    height: ", ";\n    min-height: ", ";\n\n    &:first-child {\n      border-top-left-radius: ", ";\n    }\n\n    &:last-child {\n      border-top-right-radius: ", ";\n    }\n\n    @media (min-width: ", ") {\n      ", "\n    }\n\n    ", "\n    ", "\n  "])), padding === 'checkbox' ? 0 : tableCell.root.paddingBottom, tableCell.root.paddingLeft, padding === 'checkbox' ? 0 : tableCell.root.paddingRight, padding === 'checkbox' ? 0 : tableCell.root.paddingTop, tableCell.root.height, tableCell.root.minHeight, variant === 'head' ? tableCell.head.borderRadius : '', variant === 'head' ? tableCell.head.borderRadius : '', breakpoint.breakpointMdMinWidth, sticky ? stickyStyles : '', padding === 'checkbox' ? checkboxPaddingStyles : '', padding === 'dense' && variant !== 'head' ? densePaddingStyles : '');
});

var _excluded$10 = ["className", "component", "padding", "scope"];
var TableCell = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'td' : _props$component,
      _props$padding = props.padding,
      padding = _props$padding === void 0 ? 'standard' : _props$padding,
      scope = props.scope,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$10);

  return React.createElement(TableCellRoot, Object.assign({
    as: component,
    className: className,
    padding: padding,
    scope: scope
  }, otherProps, {
    ref: ref
  }));
});
TableCell.displayName = 'TableCell';

var _templateObject$V;
var TableHead = /*#__PURE__*/styled.thead(_templateObject$V || (_templateObject$V = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n  display: table-header-group;\n  font-family: ", ";\n  font-weight: ", ";\n\n  & tr {\n    background-color: ", ";\n  }\n\n  &:after {\n    background-color: transparent;\n    color: transparent;\n    content: '';\n    display: block;\n    text-indent: -99999px;\n    width: 100%;\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.tableHead.root.color;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.tableHead.root.fontFamily;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.tableHead.root.fontWeight;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.tableHead.root.backgroundColor;
});

var _templateObject$W;
var TableRowRoot = /*#__PURE__*/styled.tr(function (_ref) {
  var selected = _ref.selected,
      tableRow = _ref.theme.component.tableRow;
  return css(_templateObject$W || (_templateObject$W = _taggedTemplateLiteralLoose(["\n    background-color: ", "; // TODO: adopt selected backgroundColor token\n    color: inherit;\n    display: table-row;\n    outline: ", " none transparent;\n    // Despite being redundant, it ensures that the outline is not displayed in Firefox.\n    outline-style: none;\n    vertical-align: middle;\n\n    &:hover {\n      background-color: ", ";\n    }\n  "])), selected ? '#EFF3FA' : tableRow.root.backgroundColor, tableRow.root.outlineWidth, tableRow.hover.backgroundColor);
});

var _excluded$11 = ["children", "component", "className", "onClick", "selected", "tabIndex"];
var TableRow = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
      component = props.component,
      className = props.className,
      onClick = props.onClick,
      selected = props.selected,
      tabIndexProp = props.tabIndex,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$11);

  var tabIndex = useMemo(function () {
    if (tabIndexProp) {
      return tabIndexProp;
    }

    if (onClick) {
      return -1;
    }

    return;
  }, [onClick, tabIndexProp]);
  return React.createElement(TableRowRoot, Object.assign({
    "aria-selected": selected,
    as: component,
    className: className,
    onClick: onClick,
    selected: selected,
    tabIndex: tabIndex
  }, otherProps, {
    ref: ref
  }), children);
});
TableRow.displayName = 'TableRow';

var _templateObject$X;
var Body = /*#__PURE__*/styled.tbody(_templateObject$X || (_templateObject$X = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: table-row-group;\n"])));

var _templateObject$Y;
var Table = /*#__PURE__*/styled.table(_templateObject$Y || (_templateObject$Y = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-collapse: separate;\n  border-spacing: 0 0;\n  color: ", ";\n  display: table;\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  min-width: 100%;\n  position: relative;\n  text-align: left;\n  width: 100%;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.table.root.color;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.table.root.fontFamily;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.table.root.fontSize;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.table.root.lineHeight;
});

var useCheckboxes = function useCheckboxes() {
  var _useState = useState([]),
      checked = _useState[0],
      setChecked = _useState[1];

  var handleToggleCheckbox = useCallback(function (index) {
    return function (event) {
      setChecked(function (prevState) {
        var newState = [].concat(prevState);
        newState[index] = event.target.checked;
        return newState;
      });
      event.stopPropagation();
    };
  }, []);
  var handleRowClick = useCallback(function (index) {
    return function () {
      setChecked(function (prevState) {
        var newState = [].concat(prevState);

        if (typeof newState[index] === 'undefined' || newState[index] === null) {
          newState[index] = true;
        } else {
          newState[index] = !newState[index];
        }

        return newState;
      });
    };
  }, []);
  var allSelected = useMemo(function () {
    if (checked.length === 0) {
      return false;
    }

    return checked.every(function (item) {
      return item === true;
    });
  }, [checked]);
  var indeterminate = useMemo(function () {
    return checked.some(function (item) {
      return item;
    }) && !allSelected;
  }, [allSelected, checked]);
  var handleSelectAll = useCallback(function () {
    setChecked(function (prevState) {
      return prevState.map(function () {
        return !indeterminate && !allSelected;
      });
    });
  }, [allSelected, indeterminate]);
  return {
    allSelected: allSelected,
    checked: checked,
    handleRowClick: handleRowClick,
    handleSelectAll: handleSelectAll,
    handleToggleCheckbox: handleToggleCheckbox,
    indeterminate: indeterminate
  };
};

var Table$1 = function Table$1(props) {
  return React.createElement(Table, Object.assign({}, props));
};

Table$1.Body = Body;
Table$1.Cell = TableCell;
Table$1.Row = TableRow;
Table$1.Head = TableHead;

var _templateObject$Z;
var TableContainerRoot = /*#__PURE__*/styled(Surface)(_templateObject$Z || (_templateObject$Z = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border: 0;\n  overflow-x: auto;\n  overflow-y: scroll;\n  width: 100%;\n"])));

var _excluded$12 = ["className", "component", "outlined"];
var TableContainer = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$outlined = props.outlined,
      outlined = _props$outlined === void 0 ? true : _props$outlined,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded$12);

  return React.createElement(TableContainerRoot, Object.assign({
    component: component,
    className: className,
    outlined: outlined
  }, otherProps, {
    ref: ref
  }));
});
TableContainer.displayName = 'TableContainer';

var _templateObject$_, _templateObject2$v, _templateObject3$k;
var fade = function fade(from, to) {
  return keyframes(_templateObject$_ || (_templateObject$_ = _taggedTemplateLiteralLoose(["\n  from {\n  opacity: ", ";\n  }\n\n  to {\n  opacity: ", ";\n  }\n"])), from, to);
};
var getTooltipAnimation = function getTooltipAnimation(isActive) {
  return css(_templateObject2$v || (_templateObject2$v = _taggedTemplateLiteralLoose(["\n    animation: ", ";\n  "])), function (_ref) {
    var theme = _ref.theme;
    var keyframes = isActive ? fade(theme.component.tooltip.hidden.opacity, theme.component.tooltip.shown.opacity) : fade(theme.component.tooltip.shown.opacity, theme.component.tooltip.hidden.opacity);
    return css(_templateObject3$k || (_templateObject3$k = _taggedTemplateLiteralLoose(["\n        ", " ", " ", "\n      "])), keyframes, theme.component.tooltip.root.animationDuration, theme.component.tooltip.root.animationTimingFunction);
  });
};

var _templateObject$$, _templateObject2$w, _templateObject3$l, _templateObject4$e, _templateObject5$8, _templateObject6$6, _templateObject7$4, _templateObject8$4, _templateObject9$4, _templateObject10$4, _templateObject11$2, _templateObject12$2, _templateObject13$2, _templateObject14, _templateObject15, _templateObject16;
var containerTop = /*#__PURE__*/css(_templateObject$$ || (_templateObject$$ = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  bottom: calc(100% + ", ");\n  left: 50%;\n  transform: translateX(-50%);\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.component.tooltip.containerTop.bottom;
});
var containerTopLeft = /*#__PURE__*/css(_templateObject2$w || (_templateObject2$w = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  bottom: calc(100% + ", ");\n  right: calc(100% - ", ");\n"])), function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.tooltip.containerTopLeft.bottom;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.tooltip.containerTopLeft.right;
});
var containerTopRight = /*#__PURE__*/css(_templateObject3$l || (_templateObject3$l = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  bottom: calc(100% + ", ");\n  left: calc(100% - ", ");\n"])), function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.tooltip.containerTopRight.bottom;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.component.tooltip.containerTopRight.left;
});
var containerBottom = /*#__PURE__*/css(_templateObject4$e || (_templateObject4$e = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  top: calc(100% + ", ");\n  left: 50%;\n  transform: translateX(-50%);\n"])), function (_ref6) {
  var theme = _ref6.theme;
  return theme.component.tooltip.containerBottom.top;
});
var containerBottomLeft = /*#__PURE__*/css(_templateObject5$8 || (_templateObject5$8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  top: calc(100% + ", ");\n  right: calc(100% - ", ");\n"])), function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.tooltip.containerBottomLeft.top;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.tooltip.containerBottomLeft.right;
});
var containerBottomRight = /*#__PURE__*/css(_templateObject6$6 || (_templateObject6$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  top: calc(100% + ", ");\n  left: calc(100% - ", ");\n"])), function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.tooltip.containerBottomRight.top;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.tooltip.containerBottomRight.left;
});
var containerLeft = /*#__PURE__*/css(_templateObject7$4 || (_templateObject7$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  top: 50%;\n  right: calc(100% + ", ");\n  transform: translateY(-50%);\n"])), function (_ref11) {
  var theme = _ref11.theme;
  return theme.component.tooltip.containerLeft.right;
});
var containerRight = /*#__PURE__*/css(_templateObject8$4 || (_templateObject8$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  top: 50%;\n  left: calc(100% + ", ");\n  transform: translateY(-50%);\n"])), function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.tooltip.containerRight.left;
});
var containerPositions = {
  top: containerTop,
  'top-left': containerTopLeft,
  'top-right': containerTopRight,
  bottom: containerBottom,
  'bottom-left': containerBottomLeft,
  'bottom-right': containerBottomRight,
  left: containerLeft,
  right: containerRight
};
var arrowTop = /*#__PURE__*/css(_templateObject9$4 || (_templateObject9$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  left: calc(50% - ", ");\n  top: 100%;\n  transform: translateY(-30%) rotate(90deg);\n"])), function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.tooltip.arrowTop.left;
});
var arrowTopLeft = /*#__PURE__*/css(_templateObject10$4 || (_templateObject10$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  right: ", ";\n  top: 100%;\n  transform: translateY(-30%) rotate(90deg);\n"])), function (_ref14) {
  var theme = _ref14.theme;
  return theme.component.tooltip.arrowTopLeft.right;
});
var arrowTopRight = /*#__PURE__*/css(_templateObject11$2 || (_templateObject11$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  left: ", ";\n  top: 100%;\n  transform: translateY(-30%) rotate(90deg);\n"])), function (_ref15) {
  var theme = _ref15.theme;
  return theme.component.tooltip.arrowTopRight.left;
});
var arrowBottom = /*#__PURE__*/css(_templateObject12$2 || (_templateObject12$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  left: calc(50% - ", ");\n  bottom: 100%;\n  transform: translateY(30%) rotate(-90deg);\n"])), function (_ref16) {
  var theme = _ref16.theme;
  return theme.component.tooltip.arrowBottom.left;
});
var arrowBottomLeft = /*#__PURE__*/css(_templateObject13$2 || (_templateObject13$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  right: ", ";\n  bottom: 100%;\n  transform: translateY(30%) rotate(-90deg);\n"])), function (_ref17) {
  var theme = _ref17.theme;
  return theme.component.tooltip.arrowBottomLeft.right;
});
var arrowBottomRight = /*#__PURE__*/css(_templateObject14 || (_templateObject14 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  left: ", ";\n  bottom: 100%;\n  transform: translateY(30%) rotate(-90deg);\n"])), function (_ref18) {
  var theme = _ref18.theme;
  return theme.component.tooltip.arrowBottomRight.left;
});
var arrowLeft = /*#__PURE__*/css(_templateObject15 || (_templateObject15 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  left: 100%;\n  top: 50%;\n  transform: translateY(-50%);\n"])));
var arrowRight = /*#__PURE__*/css(_templateObject16 || (_templateObject16 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  right: 100%;\n  top: 50%;\n  transform: translateY(-50%) rotate(180deg);\n"])));
var arrowPositions = {
  top: arrowTop,
  'top-left': arrowTopLeft,
  'top-right': arrowTopRight,
  bottom: arrowBottom,
  'bottom-left': arrowBottomLeft,
  'bottom-right': arrowBottomRight,
  left: arrowLeft,
  right: arrowRight
};
var getContainerPositionStyles = function getContainerPositionStyles(position) {
  if (position === void 0) {
    position = 'top';
  }

  return containerPositions[position];
};
var getArrowPositionStyle = function getArrowPositionStyle(position) {
  if (position === void 0) {
    position = 'top';
  }

  return arrowPositions[position];
};

var TooltipArrow = function TooltipArrow(props) {
  return React.createElement("svg", Object.assign({
    width: "8",
    height: "20",
    viewBox: "0 0 8 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), React.createElement("path", {
    d: "M6.87662 12.0203L2.7121 15.1226C0.97557 16.4161 -7.99655e-08 18.1706 0 20L-8.74228e-07 -4.76837e-07C-7.94262e-07 1.8294 0.975567 3.58387 2.71209 4.87745L6.87661 7.97969C8.37446 9.09548 8.37446 10.9045 6.87662 12.0203Z"
  }));
};

var _templateObject$10, _templateObject2$x;
var variants = ['attention', 'critical', 'informative', 'success', 'default'];

var tooltipVariantIsValid = function tooltipVariantIsValid(variant) {
  return variants.includes(variant);
};

var getTooltipVariant = function getTooltipVariant(variant) {
  var variantToken = tooltipVariantIsValid(variant) ? variant : 'informative';
  return css(_templateObject$10 || (_templateObject$10 = _taggedTemplateLiteralLoose(["\n    background-color: ", ";\n    color: ", ";\n  "])), function (_ref) {
    var theme = _ref.theme;
    return theme.component.tooltip[variantToken].backgroundColor;
  }, function (_ref2) {
    var theme = _ref2.theme;
    return theme.component.tooltip[variantToken].color;
  });
};
var getArrowVariant = function getArrowVariant(variant) {
  var variantToken = tooltipVariantIsValid(variant) ? variant : 'informative';
  return css(_templateObject2$x || (_templateObject2$x = _taggedTemplateLiteralLoose(["\n    fill: ", ";\n  "])), function (_ref3) {
    var theme = _ref3.theme;
    return theme.component.tooltip["arrow-" + variantToken].fill;
  });
};

var _templateObject$11, _templateObject2$y, _templateObject3$m, _templateObject4$f, _templateObject5$9;
var TooltipContainer = /*#__PURE__*/styled.div(_templateObject$11 || (_templateObject$11 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-flex;\n  position: relative;\n"])));
var TooltipAnchor = /*#__PURE__*/styled.span(_templateObject2$y || (_templateObject2$y = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  background: none;\n  border: 0;\n  cursor: inherit;\n  color: inherit;\n  display: inline-flex;\n"])));
var TooltipPositionContainer = /*#__PURE__*/styled.div(_templateObject3$m || (_templateObject3$m = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  pointer-events: none;\n  visibility: ", ";\n\n  z-index: ", ";\n\n  @media (prefers-reduced-motion: no-preference) {\n    transition-property: opacity;\n    transition-duration: ", ";\n    transition-timing-function: ", ";\n    will-change: opacity, visibility;\n    ", ";\n  }\n\n  ", ";\n"])), function (_ref) {
  var isActive = _ref.isActive;
  return isActive ? 'visible' : 'hidden';
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.component.tooltip.root.zIndex;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.component.tooltip.root.transitionDuration;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.component.tooltip.root.transitionTimingFunction;
}, function (_ref5) {
  var isActive = _ref5.isActive;
  return getTooltipAnimation(isActive);
}, function (_ref6) {
  var position = _ref6.position;
  return getContainerPositionStyles(position != null ? position : 'top');
});
var TooltipBalloon = /*#__PURE__*/styled.div(_templateObject4$f || (_templateObject4$f = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n  width: max-content;\n  max-width: 400px;\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  text-align: center;\n  border-radius: 0.375rem;\n  padding-top: ", ";\n  padding-right: ", ";\n  padding-bottom: ", ";\n  padding-left: ", ";\n\n  ", ";\n"])), function (_ref7) {
  var theme = _ref7.theme;
  return theme.component.tooltip.root.fontFamily;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.component.tooltip.root.fontSize;
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.component.tooltip.root.lineHeight;
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.component.tooltip.root.paddingTop;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.component.tooltip.root.paddingRight;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.component.tooltip.root.paddingBottom;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.component.tooltip.root.paddingLeft;
}, function (_ref14) {
  var variant = _ref14.variant;
  return getTooltipVariant(variant || 'informative');
});
var BalloonArrow = /*#__PURE__*/styled(TooltipArrow)(_templateObject5$9 || (_templateObject5$9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  box-sizing: border-box;\n  content: '';\n  display: block;\n  overflow: hidden;\n  position: absolute;\n  ", ";\n\n  & path {\n    ", ";\n  }\n"])), function (_ref15) {
  var position = _ref15.position;
  return getArrowPositionStyle(position || 'top');
}, function (_ref16) {
  var variant = _ref16.variant;
  return getArrowVariant(variant || 'informative');
});

var Tooltip = function Tooltip(_ref) {
  var children = _ref.children,
      content = _ref.content,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'top' : _ref$position,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'informative' : _ref$variant;

  var _useState = useState(false),
      isActive = _useState[0],
      setIsActive = _useState[1];

  var handleShowTooltip = useCallback(function () {
    setIsActive(true);
  }, []);
  var handleHideTooltip = useCallback(function () {
    setIsActive(false);
  }, []);
  return React.createElement(TooltipContainer, null, React.createElement(TooltipAnchor, {
    onBlur: handleHideTooltip,
    onFocus: handleShowTooltip,
    onMouseEnter: handleShowTooltip,
    onMouseLeave: handleHideTooltip,
    role: "tooltip",
    tabIndex: 0
  }, children), React.createElement(TooltipPositionContainer, {
    isActive: isActive,
    position: position
  }, React.createElement(TooltipBalloon, {
    "aria-hidden": !isActive,
    variant: variant
  }, content), React.createElement(BalloonArrow, {
    position: position,
    variant: variant
  })));
};

export { BackButton, Badge, BreadcrumbItem, Breadcrumbs, Button, ButtonBase, ButtonGroup, Card, CardActionArea, CardContent, Checkbox, Chip, ChipGroup, CircularProgress, CloseButton, Container, DateInput, Dialog, DialogActions, DialogContent, DialogContentText, DialogHeader, Drawer, FieldWrapper, FormLabel, GlobalStyle, GridContainer, Header, Heading, Hyperlink, IconButton, TextField as Input, InputAdornment, InputBase, List, ListItem, ListItemContent, MenuButton, MenuItem, MenuList, Modal, ModalContext, NavBar, Overlay, PageHeader, Pagination, PaginationItem, PasswordInput, ProgressBar, SearchInput, Select, Shape, SidebarLayout, Skeleton, Surface, SwitchBase, SwitchInput, Tab, TabBar, TabContext, TabPanel, Table$1 as Table, TableCell, TableContainer, TableHead, Text, TextField, Tooltip, Typography, useCheckboxes, useControlled, useElementRef, useMediaQuery, useModal, useModalContext, usePagination, useTabContext };
//# sourceMappingURL=fits-components.esm.js.map
